import {
  acknowledgeMlsWelcomes,
  fetchConversationMlsGroup,
  fetchLatestMlsKeyPackage,
  fetchMlsMessages,
  fetchPendingMlsWelcomes,
  getMlsDeviceIdentity,
  getJoinBundle,
  persistHistoryKey,
  publishMlsKeyPackage,
  reissueMlsWelcomes,
  sendMlsMessage,
} from './messengerV2Platform.js';
import {
  createGroup,
  createIdentity,
  decryptMessage,
  debugGroupRuntimeState,
  debugIdentityState,
  dropRuntimeGroupsByGroupId,
  encryptMessage,
  exportGroupKey,
  exportKeyPackage,
  getSupportedCiphersuites,
  initMls,
  joinGroup,
  listGroups as workerListGroups,
  rotateKeyPackage,
  restoreRuntimeGroup,
  shutdownMlsWorker,
} from './messengerV2Platform.js';
import {
  normalizeSuiteId,
  deleteItem,
  getItem,
  updateItem,
  withItemLock,
  base64ToBytes,
  bytesToBase64,
} from './messengerV2Platform.js';
import {
  hasMessengerV2ConcreteRuntime,
  isMessengerV2CurrentDeviceEnrollmentRequired,
  isMessengerV2ReadOnlyGroup,
} from './groupReadiness.js';
import {
  extractMessengerV2ApiErrorCode,
  shouldBlockMessengerV2JoinBundleRecovery,
} from './mlsRecoveryError.js';
import { computeMlsKeyPackageRefHex } from './mlsKeyPackageRef.js';

const DEFAULT_MLS_CIPHERSUITE = 0x004d;
const DEFAULT_KEY_PACKAGE_POOL_TARGET = 8;
const MIN_READY_KEY_PACKAGE_COUNT = 1;
const HISTORY_DERIVED_KEY_LABEL = 'history-key-v1';
const HISTORY_DERIVED_KEY_LENGTH = 32;
const CONVERSATION_SNAPSHOT_CACHE_TTL_MS = 1500;
const RUNTIME_RECOVERY_LOCK_TTL_MS = 15000;
const RUNTIME_RECOVERY_LOCK_REFRESH_MS = Math.floor(RUNTIME_RECOVERY_LOCK_TTL_MS / 3);
const RUNTIME_RECOVERY_LOCK_WAIT_MS = 3000;
const RUNTIME_RECOVERY_LOCK_POLL_MS = 250;
const RUNTIME_RECOVERY_REQUEST_STALE_MS = 30000;
const MAX_HANDSHAKE_REPLAY_PAGES = 50;
const managerCache = new Map();
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const getCryptoRef = () => {
  if (typeof window !== 'undefined' && window.crypto) {
    return window.crypto;
  }
  if (typeof crypto !== 'undefined') {
    return crypto;
  }
  return null;
};

const buildRuntimeRecoveryLockKey = ({ userId = null, groupId = null, clientId = null } = {}) =>
  [
    'messenger-v2-runtime-recovery-lock',
    toStringOrNull(userId) || 'anonymous',
    toStringOrNull(groupId) || 'unknown-group',
    toStringOrNull(clientId) || 'unknown-client',
  ].join(':');

const buildKeyPackageRecoveryLockKey = ({ userId = null, clientId = null } = {}) =>
  [
    'messenger-v2-key-package-recovery-lock',
    toStringOrNull(userId) || 'anonymous',
    toStringOrNull(clientId) || 'unknown-client',
  ].join(':');

const makeRuntimeOwnerId = (userId = null) =>
  [
    'messages-v2',
    toStringOrNull(userId) || 'anonymous',
    Date.now().toString(36),
    Math.random().toString(36).slice(2, 10),
  ].join(':');

const toStringOrNull = (value) => {
  if (value == null) return null;
  const normalized = String(value).trim();
  return normalized || null;
};

const toFiniteEpoch = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const toTimestampMillis = (value) => {
  const normalized = toStringOrNull(value);
  if (!normalized) return null;
  const timestamp = Date.parse(normalized);
  return Number.isFinite(timestamp) ? timestamp : null;
};

const normalizeReadinessState = (value) => {
  const normalized = toStringOrNull(value);
  return normalized ? normalized.toLowerCase() : null;
};

const normalizeRecoveryClaim = (value = null) => {
  if (!value || typeof value !== 'object') return null;
  const key = toStringOrNull(value.key ?? null);
  if (!key) return null;
  return {
    key,
    phase: toStringOrNull(value.phase ?? null) || 'requesting',
    groupId: toStringOrNull(value.groupId ?? value.group_id ?? null),
    conversationId: toStringOrNull(value.conversationId ?? value.conversation_id ?? null),
    clientId: toStringOrNull(value.clientId ?? value.client_id ?? null),
    keyPackageRef:
      toStringOrNull(value.keyPackageRef ?? value.key_package_ref ?? null)?.toLowerCase() || null,
    epoch: toFiniteEpoch(value.epoch ?? null),
    attemptedOperations: Array.from(
      new Set(
        (Array.isArray(value.attemptedOperations) ? value.attemptedOperations : [])
          .map((operation) => toStringOrNull(operation))
          .filter(Boolean),
      ),
    ),
    reason: toStringOrNull(value.reason ?? null),
    updatedAt: toStringOrNull(value.updatedAt ?? value.updated_at ?? null),
  };
};

const isSettledRecoveryClaim = (value = null) => {
  const claim = normalizeRecoveryClaim(value);
  return Boolean(claim && claim.phase === 'blocked');
};

const isFreshRequestingRecoveryClaim = (value = null, nowMs = Date.now()) => {
  const claim = normalizeRecoveryClaim(value);
  if (!claim || claim.phase !== 'requesting') return false;
  const updatedAtMs = Date.parse(claim.updatedAt || '');
  return Number.isFinite(updatedAtMs) && nowMs - updatedAtMs < RUNTIME_RECOVERY_REQUEST_STALE_MS;
};

const isFreshAwaitingRecoveryClaim = (value = null, nowMs = Date.now()) => {
  const claim = normalizeRecoveryClaim(value);
  if (!claim || !['awaiting_runtime', 'awaiting_snapshot', 'awaiting_ack'].includes(claim.phase)) {
    return false;
  }
  const updatedAtMs = Date.parse(claim.updatedAt || '');
  return Number.isFinite(updatedAtMs) && nowMs - updatedAtMs < RUNTIME_RECOVERY_REQUEST_STALE_MS;
};

const normalizeRuntimeCiphersuite = (
  value,
  fallback = DEFAULT_MLS_CIPHERSUITE,
  { wantPQ = true } = {},
) => {
  const normalized =
    normalizeSuiteId(value) ?? normalizeSuiteId(fallback) ?? DEFAULT_MLS_CIPHERSUITE;
  if (wantPQ && normalized !== DEFAULT_MLS_CIPHERSUITE) {
    return DEFAULT_MLS_CIPHERSUITE;
  }
  return normalized;
};

const toFiniteServerSeq = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return null;
  const normalized = Math.trunc(parsed);
  return normalized > 0 ? normalized : null;
};

const shouldRecoverFreshKeyPackage = (error = null) => {
  const code = extractMessengerV2ApiErrorCode(error);
  if (
    code === 'key_package_required' ||
    code === 'key_package_unavailable' ||
    code === 'historical_key_package_reuse'
  ) {
    return true;
  }
  const message = String(
    error?.message ?? error?.payload?.error?.message ?? error?.payload?.message ?? '',
  ).toLowerCase();
  return (
    message.includes('fresh mls key package is required') ||
    message.includes('key package unavailable') ||
    message.includes('historical key package reuse') ||
    message.includes('consumed key package')
  );
};

const isRetryableKeyPackageRecoveryState = (info = null) => {
  const reason = toStringOrNull(
    info?.recoveryBlockedReason ??
      info?.recovery_blocked_reason ??
      info?.readinessReason ??
      info?.readiness_reason ??
      info?.currentDevice?.reason ??
      info?.current_device?.reason ??
      null,
  );
  return (
    reason === 'key_package_required' ||
    reason === 'key_package_unavailable' ||
    reason === 'current_device_key_package_required'
  );
};

const shouldBlockRuntimeRecoveryWelcome = (error = null) => {
  const status = Number(error?.status ?? error?.response?.status ?? error?.payload?.status ?? null);
  if (status === 503) {
    return true;
  }

  const code = extractMessengerV2ApiErrorCode(error);
  if (code === 'reissue_failed' || code === 'reissue_welcome_unavailable') {
    return true;
  }

  const message = String(
    error?.message ?? error?.payload?.error?.message ?? error?.payload?.message ?? '',
  ).toLowerCase();
  return (
    message.includes('without mutating group membership') ||
    message.includes('service unavailable') ||
    message.includes('sidecar reissue failed') ||
    message.includes('reissue failed')
  );
};

const hasOwn = (object, key) =>
  Boolean(object && Object.prototype.hasOwnProperty.call(object, key));

const normalizeHex = (value) => {
  const normalized = toStringOrNull(value)?.toLowerCase() ?? null;
  if (!normalized || !/^[0-9a-f]+$/.test(normalized)) {
    return null;
  }
  return normalized;
};

const pickHex = (...candidates) => {
  for (const candidate of candidates) {
    const normalized = normalizeHex(candidate);
    if (normalized) {
      return normalized;
    }
  }
  return null;
};

const pickBase64 = (...candidates) => {
  for (const candidate of candidates) {
    const normalized = toStringOrNull(candidate);
    if (normalized) {
      return normalized;
    }
  }
  return null;
};

const buildStorageKey = (scope, userId, deviceId = null) =>
  [
    'messenger-v2',
    'mls',
    scope,
    encodeURIComponent(String(userId)),
    ...(deviceId ? [encodeURIComponent(String(deviceId))] : []),
  ].join(':');

const generateSeed = (length = 32) => {
  const bytes = new Uint8Array(length);
  const cryptoRef = getCryptoRef();
  if (cryptoRef?.getRandomValues) {
    cryptoRef.getRandomValues(bytes);
    return bytesToBase64(bytes);
  }
  for (let index = 0; index < bytes.length; index += 1) {
    bytes[index] = Math.floor(Math.random() * 256);
  }
  return bytesToBase64(bytes);
};

const generateClientId = () => {
  const cryptoRef = getCryptoRef();
  if (cryptoRef?.randomUUID) {
    return `mls-client-${cryptoRef.randomUUID()}`;
  }
  return `mls-client-${Math.random().toString(36).slice(2)}-${Date.now()}`;
};

const decodeUtf8Base64 = (value) => {
  const normalized = pickBase64(value);
  if (!normalized) return null;
  try {
    const bytes = base64ToBytes(normalized);
    if (!(bytes instanceof Uint8Array)) {
      return null;
    }
    if (typeof TextDecoder !== 'undefined') {
      return new TextDecoder().decode(bytes);
    }
    if (typeof Buffer !== 'undefined' && typeof Buffer.from === 'function') {
      return Buffer.from(bytes).toString('utf8');
    }
  } catch {}
  return null;
};

const normalizeServerMessageType = (record = null) => {
  const rawType = record?.msg_type ?? record?.msgType ?? record?.message_type ?? null;
  return typeof rawType === 'string' ? rawType.trim().toLowerCase() || null : null;
};

const isHandshakeServerRecord = (record = null) => {
  if (!record || typeof record !== 'object') return false;
  const messageType = normalizeServerMessageType(record);
  const contentTypeRaw = record?.content_type ?? record?.contentType ?? null;
  const contentType =
    typeof contentTypeRaw === 'string' ? contentTypeRaw.trim().toLowerCase() || null : null;
  const clientMsgId =
    typeof record?.client_msg_id === 'string' ? record.client_msg_id.trim().toLowerCase() : '';

  return (
    (messageType && messageType !== 'application') ||
    contentType === 'application/mls' ||
    clientMsgId.startsWith('commit-') ||
    clientMsgId.startsWith('proposal-')
  );
};

const isCommitServerRecord = (record = null) => {
  if (!record || typeof record !== 'object') return false;
  const messageType = normalizeServerMessageType(record);
  const clientMsgId =
    typeof record?.client_msg_id === 'string' ? record.client_msg_id.trim().toLowerCase() : '';
  return messageType === 'commit' || clientMsgId.startsWith('commit-');
};

const isRuntimeNotReadyError = (error) => {
  const code = toStringOrNull(error?.code)?.toUpperCase() || null;
  const message = String(error?.message || '').toLowerCase();
  return (
    code === 'MLS_GROUP_NEEDS_RECOVERY' ||
    code === 'E2EE_NOT_READY' ||
    message.includes('unknown group') ||
    message.includes('group context not found') ||
    message.includes('runtime not ready') ||
    message.includes('e2ee not ready')
  );
};

const isBenignReplayDecryptError = ({ error, incomingEpoch = null, localEpoch = null } = {}) => {
  if (incomingEpoch == null || localEpoch == null || localEpoch < incomingEpoch) {
    return false;
  }
  const message = String(error?.message || '').toLowerCase();
  if (
    message.includes('generation is too old') ||
    message.includes('message generation too old') ||
    message.includes('sender data decryption error')
  ) {
    return true;
  }
  return localEpoch > incomingEpoch && message.includes('epoch differs from the group');
};

const normalizeMembers = (value) => {
  if (!Array.isArray(value)) {
    return [];
  }
  return Array.from(
    new Set(
      value
        .map((entry) => {
          if (entry == null) return null;
          if (typeof entry === 'string') return toStringOrNull(entry);
          if (typeof entry === 'object') {
            return toStringOrNull(
              entry.client_id ??
                entry.clientId ??
                entry.identity_id ??
                entry.identityId ??
                entry.id ??
                null,
            );
          }
          return null;
        })
        .filter(Boolean),
    ),
  );
};

const normalizeSnapshotMembers = (snapshot = {}) =>
  normalizeMembers(
    snapshot.member_client_ids ??
      snapshot.memberClientIds ??
      snapshot.members ??
      snapshot.participants ??
      [],
  );

const computeKeyPackageRefHex = async ({ keyPackageB64, ciphersuite } = {}) => {
  const payloadBytes = base64ToBytes(keyPackageB64);
  return computeMlsKeyPackageRefHex({
    keyPackageBytes: payloadBytes,
    ciphersuite,
    cryptoRef: getCryptoRef(),
  });
};

const pickKeyPackageMetadata = (payload = {}) => {
  const source = payload && typeof payload === 'object' ? payload : {};
  const root = source?.key_package ?? source?.keyPackage ?? source;
  const keyPackageB64 = pickBase64(
    root.key_package_b64,
    root.keyPackageB64,
    root.keyPackage,
    source.key_package_b64,
    source.keyPackageB64,
  );
  return {
    clientId: toStringOrNull(
      root.client_id ?? root.clientId ?? source.client_id ?? source.clientId ?? null,
    ),
    ciphersuite: normalizeSuiteId(root.ciphersuite ?? source.ciphersuite ?? null),
    keyPackageB64,
    keyPackageRef: normalizeHex(
      root.kp_ref ??
        root.kpRef ??
        root.key_package_ref ??
        root.keyPackageRef ??
        source.kp_ref ??
        source.kpRef ??
        source.key_package_ref ??
        source.keyPackageRef ??
        null,
    ),
  };
};

const normalizePendingWelcomes = (value) => {
  if (!Array.isArray(value)) return [];
  return value
    .map((entry) => {
      if (!entry || typeof entry !== 'object') return null;
      const welcomeB64 = pickBase64(entry.welcome_b64, entry.welcomeB64, entry.welcome);
      if (!welcomeB64) return null;
      const clientId = toStringOrNull(
        entry.to_client_id ??
          entry.toClientId ??
          entry.client_id ??
          entry.clientId ??
          entry.identity_id ??
          entry.identityId ??
          null,
      );
      return {
        ...entry,
        welcome_id: toStringOrNull(entry.welcome_id ?? entry.welcomeId ?? entry.id ?? null),
        welcome_b64: welcomeB64,
        ratchet_tree_b64: pickBase64(
          entry.ratchet_tree_b64,
          entry.ratchetTreeB64,
          entry.ratchet_tree,
          entry.ratchetTree,
        ),
        epoch: toFiniteEpoch(entry.epoch ?? entry.current_epoch ?? entry.currentEpoch ?? null),
        session_generation: toFiniteEpoch(
          entry.session_generation ?? entry.sessionGeneration ?? null,
        ),
        client_id: clientId,
        to_client_id: clientId,
        used_kp_ref: normalizeHex(entry.used_kp_ref ?? entry.usedKpRef ?? null),
        usedKpRef: normalizeHex(entry.used_kp_ref ?? entry.usedKpRef ?? null),
        kp_ref: normalizeHex(
          entry.kp_ref ?? entry.kpRef ?? entry.key_package_ref ?? entry.keyPackageRef ?? null,
        ),
      };
    })
    .filter(Boolean);
};

const normalizeRecoveryPayloadWelcomes = ({
  payload = null,
  groupId = null,
  fallbackEpoch = null,
  keyPackage = null,
} = {}) => {
  if (!payload || typeof payload !== 'object') return [];
  const pendingWelcomes = normalizePendingWelcomes(
    payload.pending_welcomes ??
      payload.pendingWelcomes ??
      (payload.pending_welcome || payload.pendingWelcome
        ? [payload.pending_welcome ?? payload.pendingWelcome]
        : []),
  );
  if (pendingWelcomes.length) return pendingWelcomes;

  return normalizePendingWelcomes(
    payload.welcome_b64 || payload.welcomeB64
      ? [
          {
            welcome_id: payload.welcome_id ?? payload.welcomeId ?? null,
            group_id: groupId,
            epoch: payload.epoch ?? fallbackEpoch,
            welcome_b64: payload.welcome_b64 ?? payload.welcomeB64,
            ratchet_tree_b64: payload.ratchet_tree_b64 ?? payload.ratchetTreeB64 ?? null,
            to_client_id: keyPackage?.clientId ?? keyPackage?.identityId ?? null,
            used_kp_ref:
              payload.used_kp_ref ?? payload.usedKpRef ?? keyPackage?.keyPackageRef ?? null,
          },
        ]
      : [],
  );
};

const pickPendingWelcomeForIdentity = (entries = [], identity = null) => {
  const identityCandidates = new Set(
    [identity?.clientId, identity?.identityId]
      .map((value) => toStringOrNull(value))
      .filter(Boolean),
  );
  if (!entries.length) return null;
  if (!identityCandidates.size) {
    return entries[0];
  }
  return (
    entries.find((entry) => {
      const targetClientId = toStringOrNull(
        entry.to_client_id ?? entry.client_id ?? entry.identity_id ?? null,
      );
      return targetClientId ? identityCandidates.has(targetClientId) : false;
    }) || entries[0]
  );
};

const pickPendingWelcomesForIdentity = (entries = [], identity = null) => {
  if (!Array.isArray(entries) || !entries.length) {
    return [];
  }
  const identityCandidates = new Set(
    [identity?.clientId, identity?.identityId]
      .map((value) => toStringOrNull(value))
      .filter(Boolean),
  );
  if (!identityCandidates.size) {
    return [...entries];
  }
  const matched = entries.filter((entry) => {
    const targetClientId = toStringOrNull(
      entry?.to_client_id ?? entry?.client_id ?? entry?.identity_id ?? null,
    );
    return targetClientId ? identityCandidates.has(targetClientId) : false;
  });
  if (matched.length > 0) {
    return matched;
  }
  // A single pending welcome is still the most likely recovery target when the
  // identity/client metadata is temporarily stale after restart. Returning an
  // empty set here deadlocks ACK and keeps the thread stuck in pending.
  if (entries.length === 1) {
    return [...entries];
  }
  return [];
};

const shouldMaterializeCreatorRuntime = (entry = null) => {
  if (!entry || typeof entry !== 'object') {
    return false;
  }
  const authority = toStringOrNull(
    entry.bootstrapAuthority ?? entry.bootstrap_authority ?? entry.source ?? null,
  );
  if (authority !== 'creator') {
    return false;
  }
  const epoch = toFiniteEpoch(entry?.epoch ?? entry?.current_epoch) ?? 1;
  return epoch <= 1;
};

const hasConcreteRuntime = (entry = null) => {
  if (!entry || typeof entry !== 'object') {
    return false;
  }
  return hasMessengerV2ConcreteRuntime(entry);
};

const hasObservedRuntimeState = (entry = null) =>
  Boolean(
    toStringOrNull(entry?.runtimeHandle ?? entry?.runtime_handle ?? null) &&
      toFiniteEpoch(entry?.runtimeEpoch ?? entry?.runtime_epoch ?? null) != null,
  );

const runtimeConflictsWithAuthority = ({
  runtimeEpoch = null,
  authoritativeEpoch = null,
  runtimeCiphersuite = null,
  authoritativeCiphersuite = null,
} = {}) => {
  const observedEpoch = toFiniteEpoch(runtimeEpoch);
  const serverEpoch = toFiniteEpoch(authoritativeEpoch);
  const observedCiphersuite = normalizeSuiteId(runtimeCiphersuite);
  const serverCiphersuite = normalizeSuiteId(authoritativeCiphersuite);

  return Boolean(
    (observedEpoch != null && serverEpoch != null && observedEpoch > serverEpoch) ||
      (observedCiphersuite != null &&
        serverCiphersuite != null &&
        observedCiphersuite !== serverCiphersuite),
  );
};

const hasPendingWelcomesForIdentity = (entry = null, identity = null) => {
  if (!entry || typeof entry !== 'object') {
    return false;
  }
  return (
    pickPendingWelcomesForIdentity(
      normalizePendingWelcomes(entry?.pendingWelcomes ?? entry?.pending_welcomes ?? []),
      identity,
    ).length > 0
  );
};

const resolvePendingWelcomeTargetRef = (entry = null) =>
  normalizeHex(
    entry?.used_kp_ref ??
      entry?.usedKpRef ??
      entry?.kp_ref ??
      entry?.kpRef ??
      entry?.key_package_ref ??
      entry?.keyPackageRef ??
      null,
  );

const hasMatchingPendingWelcomeTarget = (entries = [], keyPackageRef = null) => {
  const normalizedRef = normalizeHex(keyPackageRef);
  if (!normalizedRef) {
    return Array.isArray(entries) && entries.length > 0;
  }
  return Array.isArray(entries)
    ? entries.some((entry) => resolvePendingWelcomeTargetRef(entry) === normalizedRef)
    : false;
};

const entryContainsIdentityMember = (entry = null, identity = null) => {
  if (!entry || typeof entry !== 'object') {
    return false;
  }
  const memberSet = new Set(normalizeMembers(entry?.members));
  const identityCandidates = [
    identity?.clientId,
    identity?.client_id,
    identity?.identityId,
    identity?.identity_id,
  ]
    .map((value) => toStringOrNull(value))
    .filter(Boolean);
  return identityCandidates.some((candidate) => memberSet.has(candidate));
};

const asConcreteRuntimeInfo = (
  entry = null,
  { runtimeHandle = null, runtimeEpoch = null } = {},
) => {
  if (!entry || typeof entry !== 'object') {
    return entry;
  }
  const resolvedRuntimeHandle =
    toStringOrNull(runtimeHandle ?? entry?.runtimeHandle ?? entry?.runtime_handle ?? null) || null;
  const resolvedEpoch = toFiniteEpoch(entry?.epoch ?? entry?.current_epoch ?? null) ?? 1;
  const resolvedRuntimeEpoch =
    toFiniteEpoch(runtimeEpoch ?? entry?.runtimeEpoch ?? entry?.runtime_epoch ?? null) ??
    resolvedEpoch;
  const resolvedRuntimeSessionGeneration = toFiniteEpoch(
    entry?.runtimeSessionGeneration ??
      entry?.runtime_session_generation ??
      entry?.sessionGeneration ??
      entry?.session_generation ??
      null,
  );
  return {
    ...entry,
    runtimeHandle: resolvedRuntimeHandle,
    runtime_handle: resolvedRuntimeHandle,
    lastKnownRuntimeHandle: resolvedRuntimeHandle,
    last_known_runtime_handle: resolvedRuntimeHandle,
    runtimeEpoch: resolvedRuntimeEpoch,
    runtime_epoch: resolvedRuntimeEpoch,
    runtimeSessionGeneration: resolvedRuntimeSessionGeneration,
    runtime_session_generation: resolvedRuntimeSessionGeneration,
    runtimeLive: Boolean(resolvedRuntimeHandle),
    runtime_live: Boolean(resolvedRuntimeHandle),
    epoch: resolvedEpoch,
    current_epoch: resolvedEpoch,
    pendingWelcomes: normalizePendingWelcomes(
      entry?.pendingWelcomes ?? entry?.pending_welcomes ?? [],
    ),
    pending_welcomes: normalizePendingWelcomes(
      entry?.pendingWelcomes ?? entry?.pending_welcomes ?? [],
    ),
    members: normalizeMembers(entry?.members),
    ready: Boolean(resolvedRuntimeHandle),
    runtimeState: resolvedRuntimeHandle ? 'ready' : 'welcome_required',
    needsRecovery: false,
    recoveryReason: null,
  };
};

const preferAuthoritativeRuntimeInfo = (authoritative = null, observed = null) => {
  if (!observed || typeof observed !== 'object') {
    return authoritative;
  }
  if (!authoritative || typeof authoritative !== 'object') {
    return observed;
  }
  if (hasConcreteRuntime(authoritative) && !hasConcreteRuntime(observed)) {
    const runtimeEpoch = toFiniteEpoch(
      authoritative?.runtimeEpoch ?? authoritative?.runtime_epoch ?? null,
    );
    const runtimeHandle = toStringOrNull(
      authoritative?.runtimeHandle ?? authoritative?.runtime_handle ?? null,
    );
    if (
      runtimeConflictsWithAuthority({
        runtimeEpoch,
        authoritativeEpoch: observed?.epoch ?? observed?.current_epoch ?? null,
        runtimeCiphersuite: authoritative?.ciphersuite ?? null,
        authoritativeCiphersuite: observed?.ciphersuite ?? null,
      })
    ) {
      return observed;
    }
    const concrete = asConcreteRuntimeInfo(
      {
        ...authoritative,
        ...observed,
        pendingWelcomes: normalizePendingWelcomes(
          observed?.pendingWelcomes ?? observed?.pending_welcomes ?? [],
        ),
        pending_welcomes: normalizePendingWelcomes(
          observed?.pendingWelcomes ?? observed?.pending_welcomes ?? [],
        ),
      },
      { runtimeHandle, runtimeEpoch },
    );
    const sendCapable = authoritative?.sendCapable === true || authoritative?.send_capable === true;
    const readyToSend =
      authoritative?.readyToSend === true || authoritative?.ready_to_send === true || sendCapable;
    return {
      ...concrete,
      ready: true,
      readyToSend,
      ready_to_send: readyToSend,
      sendCapable,
      send_capable: sendCapable,
      readinessState: 'ready',
      readiness_state: 'ready',
      readinessReason: null,
      readiness_reason: null,
    };
  }
  return observed;
};

class SessionManagerV2 {
  constructor({
    userId,
    identityName = null,
    preferredCiphersuite = DEFAULT_MLS_CIPHERSUITE,
    wantPQ = true,
    serverManaged = true,
  } = {}) {
    this.userId = toStringOrNull(userId);
    this.identityName =
      toStringOrNull(identityName) || (this.userId ? `user-${this.userId}` : 'user');
    this.preferredCiphersuite = normalizeSuiteId(preferredCiphersuite) ?? DEFAULT_MLS_CIPHERSUITE;
    this.wantPQ = wantPQ !== false;
    this.serverManaged = serverManaged !== false;
    this.instanceId = makeRuntimeOwnerId(this.userId);
    this.disposed = false;
    this.lifecycleGeneration = 0;

    this.legacyIdentityKey = buildStorageKey('identity', this.userId || 'anonymous');
    this.legacyGroupsKey = buildStorageKey('groups', this.userId || 'anonymous');
    this.identityKey = null;
    this.groupsKey = null;
    this.storageDeviceId = null;
    this.storageClientId = null;
    this.storageNamespacePromise = null;

    this.initPromise = null;
    this.identityPromise = null;
    this.serverDeviceIdentityPromise = null;
    this.bootstrapPromise = null;
    this.bootstrapResult = null;
    this.pendingWelcomeDrainPromise = null;
    this.activeThreadSyncPromises = new Map();
    this.groupConvergePromises = new Map();
    this.groupConvergeTails = new Map();
    this.conversationSnapshotPromises = new Map();
    this.conversationSnapshotCache = new Map();

    this.identityRecord = null;
    this.identityId = null;
    this.identityClientId = null;
    this.serverDeviceIdentityRecord = null;
    this.localRuntimeAssertions = new Map();
    this.runtimeRecoveryPromises = new Map();
    this.runtimeRestorePromises = new Map();
    this.keyPackageRefreshPromises = new Map();
    this.groupReadyWaitPromises = new Map();
    this.keyPackageReplenishPromise = null;
  }

  _assertActive(expectedGeneration = this.lifecycleGeneration) {
    if (this.disposed || expectedGeneration !== this.lifecycleGeneration) {
      const error = new Error('Messenger V2 session manager is no longer active');
      error.code = 'MLS_V2_SESSION_DISPOSED';
      throw error;
    }
  }

  dispose() {
    if (this.disposed) return;
    this.disposed = true;
    this.lifecycleGeneration += 1;
    this.activeThreadSyncPromises.clear();
    this.groupConvergePromises.clear();
    this.groupConvergeTails.clear();
    this.conversationSnapshotPromises.clear();
    this.conversationSnapshotCache.clear();
    this.localRuntimeAssertions.clear();
    this.runtimeRecoveryPromises.clear();
    this.runtimeRestorePromises.clear();
    this.keyPackageRefreshPromises.clear();
    this.groupReadyWaitPromises.clear();
    this.keyPackageReplenishPromise = null;
    this.bootstrapResult = null;
  }

  async ensureInitialized() {
    this._assertActive();
    const lifecycleGeneration = this.lifecycleGeneration;
    if (!this.initPromise) {
      this.initPromise = (async () => {
        const initResult = await initMls({
          preferredCiphersuite: this.preferredCiphersuite,
          options: { allowExperimental: true },
          storage: { backend: 'idb' },
        });
        if (this.wantPQ) {
          const supported = Array.isArray(initResult?.suites)
            ? initResult.suites
            : await Promise.resolve(getSupportedCiphersuites?.()).catch(() => []);
          const normalized = (Array.isArray(supported) ? supported : [])
            .map((suite) => normalizeSuiteId(suite))
            .filter(Boolean);
          if (!normalized.includes(DEFAULT_MLS_CIPHERSUITE)) {
            const error = new Error(
              'Post-quantum ciphersuite 0x004d is unavailable for V2 MLS runtime',
            );
            error.code = 'MLS_V2_PQ_UNAVAILABLE';
            throw error;
          }
        }
        this._assertActive(lifecycleGeneration);
        return initResult;
      })().catch((error) => {
        this.initPromise = null;
        throw error;
      });
    }
    return this.initPromise;
  }

  runActiveThreadSync({ conversationId = null, task } = {}) {
    this._assertActive();
    const lifecycleGeneration = this.lifecycleGeneration;
    const normalizedConversationId = toStringOrNull(conversationId);
    if (!normalizedConversationId) {
      throw new TypeError('conversationId is required for V2 active thread sync');
    }
    if (typeof task !== 'function') {
      throw new TypeError('task is required for V2 active thread sync');
    }

    const existing = this.activeThreadSyncPromises.get(normalizedConversationId);
    if (existing) return existing;

    const promise = Promise.resolve()
      .then(() => {
        this._assertActive(lifecycleGeneration);
        return task();
      })
      .then((result) => {
        this._assertActive(lifecycleGeneration);
        return result;
      })
      .finally(() => {
        if (this.activeThreadSyncPromises.get(normalizedConversationId) === promise) {
          this.activeThreadSyncPromises.delete(normalizedConversationId);
        }
      });
    this.activeThreadSyncPromises.set(normalizedConversationId, promise);
    return promise;
  }

  async _fetchServerDeviceIdentity({ force = false } = {}) {
    this._assertActive();
    const lifecycleGeneration = this.lifecycleGeneration;
    if (!this.serverManaged) {
      return null;
    }
    if (force === true) {
      this.serverDeviceIdentityRecord = null;
      this.serverDeviceIdentityPromise = null;
    }
    if (this.serverDeviceIdentityRecord && typeof this.serverDeviceIdentityRecord === 'object') {
      return this.serverDeviceIdentityRecord;
    }
    if (!this.serverDeviceIdentityPromise) {
      this.serverDeviceIdentityPromise = Promise.resolve(getMlsDeviceIdentity?.({ force }))
        .then((identity) => {
          this._assertActive(lifecycleGeneration);
          if (!identity || typeof identity !== 'object') {
            throw new Error('Canonical MLS device identity is unavailable');
          }
          this.serverDeviceIdentityRecord = identity;
          return this.serverDeviceIdentityRecord;
        })
        .finally(() => {
          this.serverDeviceIdentityPromise = null;
        });
    }
    return this.serverDeviceIdentityPromise;
  }

  async _ensureStorageNamespace({ serverIdentity = null, force = false } = {}) {
    this._assertActive();
    const lifecycleGeneration = this.lifecycleGeneration;
    if (force === true) {
      this.storageNamespacePromise = null;
    }
    if (this.storageNamespacePromise) {
      return this.storageNamespacePromise;
    }

    this.storageNamespacePromise = (async () => {
      const canonicalIdentity = this.serverManaged
        ? serverIdentity || (await this._fetchServerDeviceIdentity())
        : null;
      this._assertActive(lifecycleGeneration);
      const deviceId =
        toStringOrNull(canonicalIdentity?.device_id ?? canonicalIdentity?.deviceId ?? null) ||
        toStringOrNull(canonicalIdentity?.client_id ?? canonicalIdentity?.clientId ?? null) ||
        'local';
      const clientId = toStringOrNull(
        canonicalIdentity?.client_id ?? canonicalIdentity?.clientId ?? null,
      );
      const identityKey = buildStorageKey('identity', this.userId || 'anonymous', deviceId);
      const groupsKey = buildStorageKey('groups', this.userId || 'anonymous', deviceId);

      this.identityKey = identityKey;
      this.groupsKey = groupsKey;
      this.storageDeviceId = deviceId;
      this.storageClientId = clientId;

      const storedIdentity = await getItem(identityKey).catch(() => null);
      this._assertActive(lifecycleGeneration);
      const storedClientId = toStringOrNull(
        storedIdentity?.clientId ?? storedIdentity?.client_id ?? storedIdentity?.identityId ?? null,
      );
      if (clientId && storedClientId && storedClientId !== clientId) {
        this._assertActive(lifecycleGeneration);
        await Promise.all([
          deleteItem(identityKey).catch(() => null),
          deleteItem(groupsKey).catch(() => null),
        ]);
      }

      const currentIdentity = await getItem(identityKey).catch(() => null);
      this._assertActive(lifecycleGeneration);
      if (!currentIdentity) {
        const legacyIdentity = await getItem(this.legacyIdentityKey).catch(() => null);
        this._assertActive(lifecycleGeneration);
        const legacyClientId = toStringOrNull(
          legacyIdentity?.clientId ??
            legacyIdentity?.client_id ??
            legacyIdentity?.identityId ??
            legacyIdentity?.identity_id ??
            null,
        );
        if (legacyIdentity && (!clientId || legacyClientId === clientId)) {
          this._assertActive(lifecycleGeneration);
          await updateItem(identityKey, (previous) => previous || legacyIdentity);
          const legacyGroups = await getItem(this.legacyGroupsKey).catch(() => null);
          this._assertActive(lifecycleGeneration);
          if (legacyGroups && typeof legacyGroups === 'object') {
            await updateItem(groupsKey, (previous) => previous || legacyGroups);
          }
        }
      }

      // Remove the old user-only records after the one-time migration. Keeping
      // them would create a second writable source that other tabs could revive.
      this._assertActive(lifecycleGeneration);
      await Promise.all([
        deleteItem(this.legacyIdentityKey).catch(() => null),
        deleteItem(this.legacyGroupsKey).catch(() => null),
      ]);

      return { deviceId, clientId, identityKey, groupsKey };
    })().catch((error) => {
      this.storageNamespacePromise = null;
      throw error;
    });

    return this.storageNamespacePromise;
  }

  async _loadIdentityRecord({ refreshFromStorage = false } = {}) {
    const lifecycleGeneration = this.lifecycleGeneration;
    await this._ensureStorageNamespace();
    this._assertActive(lifecycleGeneration);
    if (!refreshFromStorage && this.identityRecord) {
      return this.identityRecord;
    }
    let stored = null;
    try {
      stored = (await getItem(this.identityKey)) || null;
    } catch {
      stored = null;
    }
    this._assertActive(lifecycleGeneration);
    this.identityRecord = stored && typeof stored === 'object' ? stored : null;
    this.identityId = toStringOrNull(
      this.identityRecord?.identityId ?? this.identityRecord?.identity_id ?? null,
    );
    this.identityClientId = toStringOrNull(
      this.identityRecord?.clientId ?? this.identityRecord?.client_id ?? this.identityId,
    );
    return this.identityRecord;
  }

  async _persistIdentityRecord(record, { updateProviderSnapshot = false } = {}) {
    if (!record || typeof record !== 'object') return null;
    const lifecycleGeneration = this.lifecycleGeneration;
    await this._ensureStorageNamespace();
    this._assertActive(lifecycleGeneration);
    let persisted = null;
    try {
      persisted = await updateItem(this.identityKey, (previous) => {
        this._assertActive(lifecycleGeneration);
        const current = previous && typeof previous === 'object' ? previous : {};
        const next = { ...current, ...record };
        const currentSnapshot = pickBase64(
          current.storageSnapshotB64,
          current.storage_snapshot_b64,
        );
        const proposedSnapshot = pickBase64(record.storageSnapshotB64, record.storage_snapshot_b64);
        const canonicalSnapshot =
          updateProviderSnapshot === true || !currentSnapshot
            ? proposedSnapshot || currentSnapshot
            : currentSnapshot;
        if (canonicalSnapshot) {
          next.storageSnapshotB64 = canonicalSnapshot;
          next.storage_snapshot_b64 = canonicalSnapshot;
        }
        const currentSnapshotUpdatedAt = toStringOrNull(
          current.providerSnapshotUpdatedAt ?? current.provider_snapshot_updated_at ?? null,
        );
        const proposedSnapshotUpdatedAt = toStringOrNull(
          record.providerSnapshotUpdatedAt ?? record.provider_snapshot_updated_at ?? null,
        );
        const canonicalSnapshotUpdatedAt =
          updateProviderSnapshot === true || !currentSnapshotUpdatedAt
            ? proposedSnapshotUpdatedAt || currentSnapshotUpdatedAt
            : currentSnapshotUpdatedAt;
        if (canonicalSnapshotUpdatedAt) {
          next.providerSnapshotUpdatedAt = canonicalSnapshotUpdatedAt;
          next.provider_snapshot_updated_at = canonicalSnapshotUpdatedAt;
        }
        return next;
      });
    } catch (error) {
      this._assertActive(lifecycleGeneration);
    }
    this._assertActive(lifecycleGeneration);
    const canonical = persisted && typeof persisted === 'object' ? persisted : record;
    this.identityRecord = canonical;
    this.identityId = toStringOrNull(canonical.identityId ?? canonical.identity_id ?? null);
    this.identityClientId = toStringOrNull(
      canonical.clientId ?? canonical.client_id ?? this.identityId,
    );
    return canonical;
  }

  async _persistProviderSnapshot(storageSnapshotB64) {
    const lifecycleGeneration = this.lifecycleGeneration;
    const normalizedSnapshot = pickBase64(storageSnapshotB64);
    if (!normalizedSnapshot) return this._loadIdentityRecord();

    const updatedAt = new Date().toISOString();
    const persisted = await this._persistIdentityRecord(
      {
        storageSnapshotB64: normalizedSnapshot,
        storage_snapshot_b64: normalizedSnapshot,
        providerSnapshotUpdatedAt: updatedAt,
        provider_snapshot_updated_at: updatedAt,
        updatedAt,
      },
      { updateProviderSnapshot: true },
    );
    this._assertActive(lifecycleGeneration);

    // Older builds copied the global provider store into every group record.
    // Once a canonical provider snapshot is durable, remove those divergent
    // copies. Group records retain only group metadata and runtime handles.
    await updateItem(this.groupsKey, (value) => {
      this._assertActive(lifecycleGeneration);
      const groups = value && typeof value === 'object' ? value : {};
      let changed = false;
      const migratedGroups = Object.fromEntries(
        Object.entries(groups).map(([groupId, entry]) => {
          if (!entry || typeof entry !== 'object') return [groupId, entry];
          if (!hasOwn(entry, 'storageSnapshotB64') && !hasOwn(entry, 'storage_snapshot_b64')) {
            return [groupId, entry];
          }
          const {
            storageSnapshotB64: _legacyStorageSnapshot,
            storage_snapshot_b64: _legacyStorageSnapshotSnake,
            ...groupMetadata
          } = entry;
          changed = true;
          return [groupId, groupMetadata];
        }),
      );
      return changed ? migratedGroups : groups;
    }).catch(() => null);
    return persisted;
  }

  async _loadGroups() {
    const lifecycleGeneration = this.lifecycleGeneration;
    await this._ensureStorageNamespace();
    this._assertActive(lifecycleGeneration);
    let stored = {};
    try {
      stored = (await getItem(this.groupsKey)) || {};
    } catch {
      stored = {};
    }
    this._assertActive(lifecycleGeneration);
    return stored && typeof stored === 'object' ? stored : {};
  }

  async _updateGroup(groupId, updater) {
    const normalizedGroupId = toStringOrNull(groupId);
    if (!normalizedGroupId) return null;
    const lifecycleGeneration = this.lifecycleGeneration;
    await this._ensureStorageNamespace();
    this._assertActive(lifecycleGeneration);
    let nextEntry = null;
    await updateItem(this.groupsKey, (value) => {
      this._assertActive(lifecycleGeneration);
      const groups = value && typeof value === 'object' ? value : {};
      const previous = groups[normalizedGroupId] || null;
      const nextValue = typeof updater === 'function' ? updater(previous) : updater;
      if (!nextValue || typeof nextValue !== 'object') {
        nextEntry = previous;
        return groups;
      }
      nextEntry = nextValue;
      return {
        ...groups,
        [normalizedGroupId]: nextValue,
      };
    });
    this._assertActive(lifecycleGeneration);
    return nextEntry;
  }

  async ensureIdentity({ name, ciphersuite, identityId: preferredIdentityId = null } = {}) {
    await this.ensureInitialized();
    if (!this.identityPromise) {
      this.identityPromise = (async () => {
        const preferredSuite = normalizeSuiteId(ciphersuite) ?? this.preferredCiphersuite;
        const serverIdentity = (await this._fetchServerDeviceIdentity()) || {};
        await this._ensureStorageNamespace({ serverIdentity });
        const stored = (await this._loadIdentityRecord()) || {};
        const identityId =
          toStringOrNull(serverIdentity.client_id ?? serverIdentity.clientId ?? null) ||
          toStringOrNull(preferredIdentityId) ||
          toStringOrNull(stored.identityId ?? stored.identity_id ?? null) ||
          generateClientId();
        const seed =
          pickBase64(
            serverIdentity.seed_b64,
            serverIdentity.seedB64,
            serverIdentity.seed,
            stored.seed,
          ) || generateSeed();
        const identityName =
          toStringOrNull(name) ||
          toStringOrNull(stored.identityName ?? stored.identity_name ?? null) ||
          this.identityName;
        const storedPublishedKeyPackageRef = pickHex(
          stored.keyPackageRef,
          stored.key_package_ref,
          stored.publishedKeyPackageRef,
          stored.published_key_package_ref,
        );
        const storedStorageSnapshotB64 = pickBase64(
          stored.storageSnapshotB64,
          stored.storage_snapshot_b64,
        );

        const debugIdentity =
          (await Promise.resolve(
            debugIdentityState?.({
              identityId,
              includeKeyPackage: true,
            }),
          ).catch(() => null)) || null;
        const debugIdentitySuite = normalizeSuiteId(debugIdentity?.ciphersuite ?? null);
        const debugIdentitySuiteAllowed =
          !this.wantPQ ||
          debugIdentitySuite == null ||
          debugIdentitySuite === DEFAULT_MLS_CIPHERSUITE;

        let resolvedIdentity = null;
        if (
          debugIdentity &&
          debugIdentitySuiteAllowed &&
          debugIdentity.identityFound === true &&
          (debugIdentity.identityHandleAlive === true ||
            debugIdentity.privateMaterialReady === true ||
            debugIdentity.keyPackageHandleAlive === true ||
            toStringOrNull(debugIdentity.keyPackageB64 ?? debugIdentity.key_package_b64 ?? null))
        ) {
          const exportedKeyPackage =
            pickBase64(
              debugIdentity.keyPackageB64,
              debugIdentity.key_package_b64,
              debugIdentity.keyPackage,
            ) ||
            pickBase64(
              (await Promise.resolve(exportKeyPackage?.({ identityId })).catch(() => null))
                ?.keyPackage,
            );
          resolvedIdentity = {
            identityId,
            clientId: identityId,
            ciphersuite: normalizeRuntimeCiphersuite(
              debugIdentity.ciphersuite ?? preferredSuite,
              preferredSuite,
              { wantPQ: this.wantPQ },
            ),
            keyPackage: exportedKeyPackage,
            keyPackageRef: pickHex(
              debugIdentity.keyPackageRef,
              debugIdentity.key_package_ref,
              debugIdentity.publishedKeyPackageRef,
              debugIdentity.published_key_package_ref,
              storedPublishedKeyPackageRef,
            ),
            storageSnapshotB64: pickBase64(
              debugIdentity.storageSnapshotB64,
              debugIdentity.storage_snapshot_b64,
              stored.storageSnapshotB64,
              stored.storage_snapshot_b64,
            ),
            seed,
            identityName,
          };
        } else {
          const created = await createIdentity({
            name: identityName,
            ciphersuite: preferredSuite,
            seed,
            identityId,
            storageSnapshotB64: storedStorageSnapshotB64,
            storage_snapshot_b64: storedStorageSnapshotB64,
          });
          resolvedIdentity = {
            identityId: toStringOrNull(created?.identityId ?? identityId) || identityId,
            clientId: identityId,
            ciphersuite: normalizeRuntimeCiphersuite(
              created?.ciphersuite ?? preferredSuite,
              preferredSuite,
              { wantPQ: this.wantPQ },
            ),
            keyPackage: pickBase64(
              created?.keyPackage,
              created?.keyPackageB64,
              created?.key_package_b64,
            ),
            keyPackageRef: pickHex(
              created?.keyPackageRef,
              created?.key_package_ref,
              created?.publishedKeyPackageRef,
              created?.published_key_package_ref,
              storedPublishedKeyPackageRef,
            ),
            storageSnapshotB64: pickBase64(
              created?.storageSnapshotB64,
              created?.storage_snapshot_b64,
              storedStorageSnapshotB64,
            ),
            seed,
            identityName,
          };
        }

        const localKeyPackageRef = await computeKeyPackageRefHex({
          keyPackageB64: resolvedIdentity.keyPackage,
          ciphersuite: resolvedIdentity.ciphersuite,
        });
        const resolvedPublishedKeyPackageRef =
          resolvedIdentity.keyPackageRef && localKeyPackageRef
            ? resolvedIdentity.keyPackageRef === localKeyPackageRef
              ? resolvedIdentity.keyPackageRef
              : storedPublishedKeyPackageRef
            : resolvedIdentity.keyPackageRef || null;

        await this._persistIdentityRecord({
          identityId: resolvedIdentity.identityId,
          identity_id: resolvedIdentity.identityId,
          clientId: resolvedIdentity.clientId,
          client_id: resolvedIdentity.clientId,
          ciphersuite: resolvedIdentity.ciphersuite,
          keyPackage: resolvedIdentity.keyPackage ?? null,
          key_package_b64: resolvedIdentity.keyPackage ?? null,
          keyPackageRef: resolvedPublishedKeyPackageRef,
          key_package_ref: resolvedPublishedKeyPackageRef,
          storageSnapshotB64: resolvedIdentity.storageSnapshotB64 ?? null,
          storage_snapshot_b64: resolvedIdentity.storageSnapshotB64 ?? null,
          seed: resolvedIdentity.seed,
          identityName: resolvedIdentity.identityName,
          identity_name: resolvedIdentity.identityName,
          updatedAt: new Date().toISOString(),
        });
        return {
          identityId: resolvedIdentity.identityId,
          identity_id: resolvedIdentity.identityId,
          clientId: resolvedIdentity.clientId,
          client_id: resolvedIdentity.clientId,
          ciphersuite: resolvedIdentity.ciphersuite,
          keyPackage: resolvedIdentity.keyPackage ?? null,
          key_package_b64: resolvedIdentity.keyPackage ?? null,
          keyPackageRef: resolvedPublishedKeyPackageRef,
          key_package_ref: resolvedPublishedKeyPackageRef,
          storageSnapshotB64: resolvedIdentity.storageSnapshotB64 ?? null,
          storage_snapshot_b64: resolvedIdentity.storageSnapshotB64 ?? null,
          identityName: resolvedIdentity.identityName,
          identity_name: resolvedIdentity.identityName,
        };
      })().finally(() => {
        this.identityPromise = null;
      });
    }
    return this.identityPromise;
  }

  async getBootstrapPublishDisposition({
    identityId = null,
    clientId = null,
    ciphersuite = null,
  } = {}) {
    const identity = await this.ensureIdentity({ ciphersuite });
    const resolvedIdentityId = toStringOrNull(identityId ?? identity?.identityId ?? null);
    const resolvedClientId = toStringOrNull(clientId ?? identity?.clientId ?? resolvedIdentityId);
    const resolvedSuite = normalizeSuiteId(
      ciphersuite ?? identity?.ciphersuite ?? this.preferredCiphersuite,
    );
    const exported =
      (await Promise.resolve(
        exportKeyPackage?.({
          identityId: resolvedIdentityId,
          refreshIfConsumed: false,
        }),
      ).catch(() => null)) || null;
    const localKeyPackageB64 = pickBase64(exported?.keyPackage, identity?.keyPackage);
    const localKeyPackageRef = await computeKeyPackageRefHex({
      keyPackageB64: localKeyPackageB64,
      ciphersuite: resolvedSuite,
    });
    const persistedPublishedRef = pickHex(
      identity?.keyPackageRef,
      identity?.key_package_ref,
      this.identityRecord?.keyPackageRef,
      this.identityRecord?.key_package_ref,
      this.identityRecord?.publishedKeyPackageRef,
      this.identityRecord?.published_key_package_ref,
    );
    const latest = await Promise.resolve(
      fetchLatestMlsKeyPackage?.({
        clientId: resolvedClientId,
        ciphersuite: resolvedSuite,
      }),
    ).catch(() => null);
    const latestMetadata = pickKeyPackageMetadata(latest);
    const serverAvailable = Boolean(latest) && latest?.available !== false;
    const requiresFreshPackage = latest?.available === false;
    const inventoryKnown = Boolean(
      latest &&
        ('available_count' in latest ||
          'availableCount' in latest ||
          'target_count' in latest ||
          'targetCount' in latest),
    );
    let serverRef = latestMetadata.keyPackageRef;
    if (!serverRef && latestMetadata.keyPackageB64) {
      serverRef = await computeKeyPackageRefHex({
        keyPackageB64: latestMetadata.keyPackageB64,
        ciphersuite: latestMetadata.ciphersuite ?? resolvedSuite,
      });
    }

    const localMatchesServer = Boolean(
      serverAvailable &&
        serverRef &&
        (serverRef === localKeyPackageRef || serverRef === persistedPublishedRef),
    );
    const allowPublish = !localMatchesServer;
    return {
      allowPublish,
      deferred: false,
      reason: requiresFreshPackage
        ? 'server_key_package_consumed'
        : allowPublish
          ? 'local_key_package_differs'
          : 'server_key_package_present',
      requiresFreshPackage,
      identityId: resolvedIdentityId,
      clientId: resolvedClientId,
      ciphersuite: resolvedSuite,
      targetRef: serverRef || localKeyPackageRef || null,
      keyPackageB64: localKeyPackageB64,
      inventoryKnown,
      availableCount: Number(
        latest?.available_count ?? latest?.availableCount ?? (serverAvailable ? 1 : 0),
      ),
      targetCount: Number(latest?.target_count ?? latest?.targetCount ?? (serverAvailable ? 1 : 0)),
    };
  }

  async publishKeyPackageAndSync(
    identityId,
    {
      forcePublish = false,
      allowFreshRecovery = true,
      allowDeviceIdentityRecovery = true,
      enrollmentMode = null,
    } = {},
  ) {
    const identity = await this.ensureIdentity({});
    const resolvedIdentityId = toStringOrNull(identityId ?? identity?.identityId ?? null);
    const resolvedClientId = toStringOrNull(identity?.clientId ?? resolvedIdentityId);
    const resolvedSuite = normalizeSuiteId(identity?.ciphersuite ?? this.preferredCiphersuite);
    try {
      const exported = await exportKeyPackage({
        identityId: resolvedIdentityId,
        refreshIfConsumed: forcePublish === true,
      });
      const keyPackageB64 = pickBase64(exported?.keyPackage, exported?.keyPackageB64);
      const localRef = await computeKeyPackageRefHex({
        keyPackageB64,
        ciphersuite: resolvedSuite,
      });
      await publishMlsKeyPackage({
        identityId: resolvedIdentityId,
        clientId: resolvedClientId,
        ciphersuite: resolvedSuite,
        keyPackageB64,
        force: forcePublish === true,
        enrollmentMode,
      });
      const latest = await Promise.resolve(
        fetchLatestMlsKeyPackage?.({
          clientId: resolvedClientId,
          ciphersuite: resolvedSuite,
        }),
      ).catch(() => null);
      const latestMetadata = pickKeyPackageMetadata(latest);
      let targetRef = latestMetadata.keyPackageRef;
      if (!targetRef && latestMetadata.keyPackageB64) {
        targetRef = await computeKeyPackageRefHex({
          keyPackageB64: latestMetadata.keyPackageB64,
          ciphersuite: latestMetadata.ciphersuite ?? resolvedSuite,
        });
      }
      await this._persistProviderSnapshot(
        pickBase64(exported?.storageSnapshotB64, exported?.storage_snapshot_b64),
      );
      await this._persistIdentityRecord({
        ...(this.identityRecord || {}),
        identityId: resolvedIdentityId,
        identity_id: resolvedIdentityId,
        clientId: resolvedClientId,
        client_id: resolvedClientId,
        ciphersuite: resolvedSuite,
        keyPackage: keyPackageB64,
        key_package_b64: keyPackageB64,
        keyPackageRef: targetRef || localRef || null,
        key_package_ref: targetRef || localRef || null,
        updatedAt: new Date().toISOString(),
      });
      return {
        identityId: resolvedIdentityId,
        clientId: resolvedClientId,
        ciphersuite: resolvedSuite,
        targetRef: targetRef || localRef || null,
        keyPackageB64,
        availableCount: Number(latest?.available_count ?? latest?.availableCount ?? 0),
        targetCount: Number(
          latest?.target_count ?? latest?.targetCount ?? DEFAULT_KEY_PACKAGE_POOL_TARGET,
        ),
        neededCount: Number(latest?.needed_count ?? latest?.neededCount ?? 0),
        published: true,
      };
    } catch (error) {
      if (
        allowDeviceIdentityRecovery &&
        extractMessengerV2ApiErrorCode(error) === 'client_id_mismatch'
      ) {
        await this.recoverCanonicalDeviceIdentity();
        return this.publishKeyPackageAndSync(null, {
          forcePublish: true,
          allowFreshRecovery,
          allowDeviceIdentityRecovery: false,
          enrollmentMode,
        });
      }
      if (allowFreshRecovery && shouldRecoverFreshKeyPackage(error)) {
        await Promise.resolve(
          rotateKeyPackage?.({
            identityId: resolvedIdentityId,
            ciphersuite: resolvedSuite,
          }),
        ).catch(() => null);
        return this.publishKeyPackageAndSync(resolvedIdentityId, {
          forcePublish: true,
          allowFreshRecovery: false,
          allowDeviceIdentityRecovery,
          enrollmentMode,
        });
      }
      throw error;
    }
  }

  async recoverCanonicalDeviceIdentity() {
    this.identityPromise = null;
    this.serverDeviceIdentityRecord = null;
    const canonicalIdentity = await this._fetchServerDeviceIdentity({ force: true });
    this.storageNamespacePromise = null;
    this.identityRecord = null;
    this.identityId = null;
    this.identityClientId = null;
    await this._ensureStorageNamespace({ serverIdentity: canonicalIdentity, force: true });
    return this.ensureIdentity({
      identityId: toStringOrNull(
        canonicalIdentity?.client_id ?? canonicalIdentity?.clientId ?? null,
      ),
    });
  }

  async _resolveCurrentKeyPackage({ identity = null, ciphersuite = null } = {}) {
    const resolvedIdentity =
      identity ||
      (await this.ensureIdentity({
        ciphersuite,
      }));
    const resolvedIdentityId = toStringOrNull(
      resolvedIdentity?.identityId ?? resolvedIdentity?.identity_id ?? null,
    );
    const resolvedClientId = toStringOrNull(
      resolvedIdentity?.clientId ?? resolvedIdentity?.client_id ?? resolvedIdentityId,
    );
    const resolvedSuite = normalizeSuiteId(
      ciphersuite ?? resolvedIdentity?.ciphersuite ?? this.preferredCiphersuite,
    );
    const exported =
      (await Promise.resolve(
        exportKeyPackage?.({
          identityId: resolvedIdentityId,
          refreshIfConsumed: false,
        }),
      ).catch(() => null)) || null;
    const keyPackageB64 = pickBase64(
      exported?.keyPackage,
      exported?.keyPackageB64,
      resolvedIdentity?.keyPackage,
      resolvedIdentity?.key_package_b64,
    );
    const keyPackageRef = await computeKeyPackageRefHex({
      keyPackageB64,
      ciphersuite: resolvedSuite,
    });

    await this._persistIdentityRecord({
      ...(this.identityRecord || {}),
      identityId: resolvedIdentityId,
      identity_id: resolvedIdentityId,
      clientId: resolvedClientId,
      client_id: resolvedClientId,
      ciphersuite: resolvedSuite,
      keyPackage: keyPackageB64 ?? null,
      key_package_b64: keyPackageB64 ?? null,
      keyPackageRef: keyPackageRef ?? null,
      key_package_ref: keyPackageRef ?? null,
      updatedAt: new Date().toISOString(),
    });

    return {
      identityId: resolvedIdentityId,
      clientId: resolvedClientId,
      ciphersuite: resolvedSuite,
      keyPackageB64,
      keyPackageRef,
    };
  }

  async _ensureJoinKeyPackage({ identity = null, ciphersuite = null } = {}) {
    const resolvedIdentity =
      identity ||
      (await this.ensureIdentity({
        ciphersuite,
      }));
    const resolvedSuite = normalizeSuiteId(
      ciphersuite ?? resolvedIdentity?.ciphersuite ?? this.preferredCiphersuite,
    );

    await this.ensureBootstrapReady({
      name: resolvedIdentity?.identityName ?? resolvedIdentity?.identity_name ?? this.identityName,
      ciphersuite: resolvedSuite,
    });

    // Bootstrap readiness is intentionally cached so opening Messenger is not
    // coupled to an account-wide package refill. A package is one-time use,
    // though, so every enrollment must revalidate the server inventory under
    // the device package lock instead of trusting that cached bootstrap bit.
    const disposition = await this.getBootstrapPublishDisposition({
      identityId: resolvedIdentity?.identityId ?? resolvedIdentity?.identity_id ?? null,
      clientId:
        resolvedIdentity?.clientId ??
        resolvedIdentity?.client_id ??
        resolvedIdentity?.identityId ??
        null,
      ciphersuite: resolvedSuite,
    });
    await this._ensureKeyPackageInventory({
      identity: resolvedIdentity,
      disposition,
      forcePublish: disposition?.requiresFreshPackage === true,
      fillTarget: false,
      enrollmentMode: 'explicit_recovery',
    });

    return this._resolveCurrentKeyPackage({
      identity: resolvedIdentity,
      ciphersuite: resolvedSuite,
    });
  }

  async _refreshJoinKeyPackage({ identity = null, ciphersuite = null } = {}) {
    const resolvedIdentity =
      identity ||
      (await this.ensureIdentity({
        ciphersuite,
      }));
    const resolvedSuite = normalizeSuiteId(
      ciphersuite ?? resolvedIdentity?.ciphersuite ?? this.preferredCiphersuite,
    );
    const resolvedIdentityId = toStringOrNull(
      resolvedIdentity?.identityId ?? resolvedIdentity?.identity_id ?? null,
    );
    const resolvedClientId = toStringOrNull(
      resolvedIdentity?.clientId ?? resolvedIdentity?.client_id ?? resolvedIdentityId,
    );

    const refreshKey = [
      resolvedClientId || resolvedIdentityId || 'unknown-client',
      resolvedSuite ?? this.preferredCiphersuite,
    ].join(':');
    if (this.keyPackageRefreshPromises.has(refreshKey)) {
      return this.keyPackageRefreshPromises.get(refreshKey);
    }

    const promise = Promise.resolve(
      rotateKeyPackage?.({
        identityId: resolvedIdentityId,
        ciphersuite: resolvedSuite,
      }),
    )
      .catch(() => null)
      .then(() =>
        this.publishKeyPackageAndSync(resolvedIdentityId, {
          forcePublish: true,
          enrollmentMode: 'explicit_recovery',
        }),
      )
      .then(async (published) => {
        const current = await this._resolveCurrentKeyPackage({
          identity: resolvedIdentity,
          ciphersuite: resolvedSuite,
        });
        return {
          identityId: toStringOrNull(
            published?.identityId ?? current?.identityId ?? resolvedIdentityId,
          ),
          clientId: toStringOrNull(
            published?.clientId ?? current?.clientId ?? resolvedClientId ?? resolvedIdentityId,
          ),
          ciphersuite: normalizeSuiteId(
            published?.ciphersuite ??
              current?.ciphersuite ??
              resolvedSuite ??
              this.preferredCiphersuite,
          ),
          keyPackageB64: pickBase64(
            published?.keyPackageB64,
            current?.keyPackageB64,
            resolvedIdentity?.keyPackage ?? null,
          ),
          keyPackageRef: toStringOrNull(published?.targetRef ?? current?.keyPackageRef ?? null),
        };
      })
      .finally(() => {
        if (this.keyPackageRefreshPromises.get(refreshKey) === promise) {
          this.keyPackageRefreshPromises.delete(refreshKey);
        }
      });
    this.keyPackageRefreshPromises.set(refreshKey, promise);
    return promise;
  }

  async ensureBootstrapReady({ name = null, ciphersuite = null, forcePublish = false } = {}) {
    if (forcePublish !== true && this.bootstrapResult?.ready === true) {
      return this.bootstrapResult;
    }

    if (!this.bootstrapPromise) {
      this.bootstrapPromise = (async () => {
        const identity = await this.ensureIdentity({
          name,
          ciphersuite,
        });
        const disposition = await this.getBootstrapPublishDisposition({
          identityId: identity.identityId,
          clientId: identity.clientId,
          ciphersuite: normalizeSuiteId(
            ciphersuite ?? identity.ciphersuite ?? this.preferredCiphersuite,
          ),
        });

        const publishResult = await this._ensureKeyPackageInventory({
          identity,
          disposition,
          forcePublish,
        });

        const keyPackageStatus =
          disposition?.allowPublish === false
            ? toStringOrNull(disposition.reason) || 'present'
            : publishResult?.skipped === true
              ? toStringOrNull(publishResult.reason) || 'present'
              : 'published';

        const result = {
          identity,
          identityId: toStringOrNull(identity?.identityId ?? identity?.identity_id ?? null),
          clientId: toStringOrNull(identity?.clientId ?? identity?.client_id ?? null),
          ciphersuite:
            normalizeSuiteId(
              publishResult?.ciphersuite ??
                identity?.ciphersuite ??
                ciphersuite ??
                this.preferredCiphersuite,
            ) ?? this.preferredCiphersuite,
          keyPackageStatus,
          keyPackageRef: toStringOrNull(publishResult?.targetRef ?? null),
          publishResult,
          welcomeRecovery: null,
          recoveryDegraded: false,
          ready: true,
        };
        this.bootstrapResult = result;
        return result;
      })().finally(() => {
        this.bootstrapPromise = null;
      });
    }

    return this.bootstrapPromise;
  }

  async drainPendingWelcomes({ identity = null } = {}) {
    if (this.pendingWelcomeDrainPromise) {
      return this.pendingWelcomeDrainPromise;
    }

    const promise = (async () => {
      const resolvedIdentity = identity || (await this.ensureIdentity({}));
      const clientId = toStringOrNull(
        resolvedIdentity?.clientId ?? resolvedIdentity?.identityId ?? this.identityClientId,
      );
      const response = await fetchPendingMlsWelcomes({ clientId });
      const pendingWelcomes = normalizePendingWelcomes(response?.items ?? []);
      const welcomesByGroup = new Map();

      pendingWelcomes.forEach((welcome) => {
        const groupId = toStringOrNull(welcome?.group_id ?? welcome?.groupId ?? null);
        const targetClientId = toStringOrNull(
          welcome?.to_client_id ?? welcome?.client_id ?? welcome?.identity_id ?? null,
        );
        if (!groupId || (targetClientId && targetClientId !== clientId)) return;

        const groupWelcomes = welcomesByGroup.get(groupId) || [];
        groupWelcomes.push(welcome);
        welcomesByGroup.set(groupId, groupWelcomes);
      });

      const result = {
        discovered: pendingWelcomes.length,
        converged: 0,
        failed: 0,
        failedGroups: [],
      };
      for (const [groupId, welcomes] of welcomesByGroup.entries()) {
        const conversationId = toStringOrNull(
          welcomes[0]?.conversation_id ?? welcomes[0]?.conversationId ?? null,
        );
        try {
          const info = await this.ingestPendingEntries({
            groupId,
            conversationId,
            items: welcomes,
          });
          if (!hasConcreteRuntime(info) && !isMessengerV2ReadOnlyGroup(info)) {
            const error = new Error('MLS pending Welcome did not create a local runtime');
            error.code = 'MLS_PENDING_WELCOME_RUNTIME_MISSING';
            throw error;
          }
          result.converged += 1;
        } catch (error) {
          result.failed += 1;
          result.failedGroups.push({
            groupId,
            conversationId,
            code: toStringOrNull(error?.code) || 'MLS_PENDING_WELCOME_RECOVERY_FAILED',
          });
          console.warn('[MLS V2] pending Welcome convergence failed', {
            groupId,
            conversationId,
            error,
          });
        }
      }
      return result;
    })()
      .catch((error) => {
        console.warn('[MLS V2] pending Welcome drain failed', error);
        return {
          discovered: 0,
          converged: 0,
          failed: 1,
          failedGroups: [
            {
              groupId: null,
              conversationId: null,
              code: toStringOrNull(error?.code) || 'MLS_PENDING_WELCOME_DRAIN_FAILED',
            },
          ],
        };
      })
      .finally(() => {
        if (this.pendingWelcomeDrainPromise === promise) {
          this.pendingWelcomeDrainPromise = null;
        }
      });

    this.pendingWelcomeDrainPromise = promise;
    return promise;
  }

  async _ensureKeyPackageInventory({
    identity,
    disposition,
    forcePublish = false,
    fillTarget = false,
    enrollmentMode = null,
  } = {}) {
    const identityId = toStringOrNull(identity?.identityId ?? identity?.identity_id ?? null);
    const clientId = toStringOrNull(identity?.clientId ?? identity?.client_id ?? identityId);
    const ciphersuite = normalizeSuiteId(identity?.ciphersuite ?? this.preferredCiphersuite);
    const latest =
      disposition ||
      (await Promise.resolve(fetchLatestMlsKeyPackage?.({ clientId, ciphersuite })).catch(
        () => null,
      ));
    const reportedTarget = Number(latest?.target_count ?? latest?.targetCount);
    const inventoryKnown = Boolean(
      latest?.inventoryKnown === true ||
        (latest && ('available_count' in latest || 'target_count' in latest)),
    );
    const targetCount =
      inventoryKnown && Number.isFinite(reportedTarget) && reportedTarget > 0
        ? reportedTarget
        : inventoryKnown
          ? DEFAULT_KEY_PACKAGE_POOL_TARGET
          : 1;
    let availableCount = Math.max(
      0,
      Number(
        latest?.available_count ??
          latest?.availableCount ??
          (latest && latest.available !== false ? 1 : 0),
      ) || 0,
    );
    const desiredCount = fillTarget
      ? targetCount
      : Math.min(targetCount, MIN_READY_KEY_PACKAGE_COUNT);
    let lastPublish = disposition;
    let publishedCount = 0;
    let currentPackagePublished = Boolean(
      disposition?.allowPublish === false && disposition?.requiresFreshPackage !== true,
    );
    const maxPublishes = fillTarget ? Math.max(targetCount, 1) : 1;

    // Login only waits for one usable package. The full server-computed pool is
    // replenished separately in the background, while an explicit enrollment
    // always proves that this device's current package is fresh before use.
    while (
      (availableCount < desiredCount || !currentPackagePublished) &&
      publishedCount < maxPublishes
    ) {
      const shouldRotate =
        availableCount > 0 ||
        disposition?.allowPublish === false ||
        disposition?.requiresFreshPackage === true ||
        forcePublish;
      if (shouldRotate) {
        await rotateKeyPackage?.({ identityId, ciphersuite });
      }

      lastPublish = await this.publishKeyPackageAndSync(identityId, {
        forcePublish: shouldRotate || disposition?.requiresFreshPackage === true,
        allowFreshRecovery: true,
        enrollmentMode,
      });
      const reportedCount = Number(lastPublish?.availableCount);
      availableCount =
        Number.isFinite(reportedCount) && reportedCount > availableCount
          ? reportedCount
          : availableCount + 1;
      publishedCount += 1;
      currentPackagePublished = true;
    }

    return {
      ...(lastPublish || {}),
      availableCount,
      targetCount,
      neededCount: Math.max(targetCount - availableCount, 0),
      inventoryReady: availableCount >= desiredCount && currentPackagePublished,
      poolReady: availableCount >= targetCount,
    };
  }

  async _markRuntimeRecoveryBlocked(
    groupId,
    recoveryMarker,
    reason = 'reissue_welcome_unavailable',
  ) {
    const blockedAt = new Date().toISOString();
    await this._updateGroup(groupId, (previous) => {
      const currentClaim = normalizeRecoveryClaim(previous?.runtimeRecoveryClaim ?? null);
      return {
        ...(previous || {}),
        recoveryBlockedMarker: recoveryMarker,
        recovery_blocked_marker: recoveryMarker,
        recoveryBlockedReason: reason,
        recovery_blocked_reason: reason,
        recoveryBlockedAt: blockedAt,
        recovery_blocked_at: blockedAt,
        runtimeRecoveryClaim: currentClaim
          ? {
              ...currentClaim,
              phase: 'blocked',
              reason,
              updatedAt: blockedAt,
            }
          : null,
      };
    });
  }

  async _clearRuntimeRecoveryBlocked(groupId) {
    await this._updateGroup(groupId, (previous) => {
      const currentClaim = normalizeRecoveryClaim(previous?.runtimeRecoveryClaim ?? null);
      return {
        ...(previous || {}),
        recoveryBlockedMarker: null,
        recovery_blocked_marker: null,
        recoveryBlockedReason: null,
        recovery_blocked_reason: null,
        recoveryBlockedAt: null,
        recovery_blocked_at: null,
        runtimeRecoveryClaim: currentClaim?.phase === 'blocked' ? null : currentClaim,
      };
    });
  }

  _buildRuntimeRecoveryClaim({
    groupId,
    conversationId = null,
    identity = null,
    keyPackage = null,
    info = null,
  } = {}) {
    const normalizedGroupId = toStringOrNull(groupId);
    const clientId =
      toStringOrNull(keyPackage?.clientId ?? keyPackage?.identityId ?? null) ||
      toStringOrNull(identity?.clientId ?? identity?.identityId ?? null) ||
      this.identityClientId ||
      this.identityId ||
      'unknown-client';
    const keyPackageRef =
      toStringOrNull(
        keyPackage?.keyPackageRef ??
          info?.welcomeTargetRefLock ??
          info?.welcome_target_ref_lock ??
          null,
      )?.toLowerCase() || null;
    const epoch = toFiniteEpoch(info?.epoch ?? info?.current_epoch ?? null);
    // One MLS group + one device is the recovery authority. Conversation
    // discovery and epoch changes must not create parallel recovery owners.
    const key = [normalizedGroupId || 'unknown-group', clientId].join(':');

    return {
      key,
      phase: 'requesting',
      groupId: normalizedGroupId,
      conversationId: toStringOrNull(conversationId),
      clientId,
      keyPackageRef,
      epoch,
      attemptedOperations: [],
      reason: null,
      updatedAt: new Date().toISOString(),
    };
  }

  async _claimRuntimeRecovery({ claim, operation, info = null } = {}) {
    const normalizedClaim = normalizeRecoveryClaim(claim);
    const normalizedOperation = toStringOrNull(operation);
    if (!normalizedClaim?.groupId || !normalizedOperation) {
      return { claimed: false, claim: normalizedClaim };
    }

    let claimed = false;
    let storedClaim = null;
    await this._updateGroup(normalizedClaim.groupId, (previous) => {
      const current = normalizeRecoveryClaim(previous?.runtimeRecoveryClaim ?? null);
      const sameAuthority = current?.key === normalizedClaim.key;
      const alreadyAttempted = Boolean(
        sameAuthority && current.attemptedOperations.includes(normalizedOperation),
      );
      const attemptSettled =
        alreadyAttempted &&
        (isSettledRecoveryClaim(current) ||
          isFreshRequestingRecoveryClaim(current) ||
          isFreshAwaitingRecoveryClaim(current));

      if (attemptSettled) {
        storedClaim = current;
        return previous;
      }

      storedClaim = {
        ...(sameAuthority ? current : normalizedClaim),
        phase: 'requesting',
        attemptedOperations: Array.from(
          new Set([...(sameAuthority ? current.attemptedOperations : []), normalizedOperation]),
        ),
        reason: null,
        updatedAt: new Date().toISOString(),
      };
      claimed = true;
      return {
        ...(info || {}),
        ...(previous || {}),
        runtimeRecoveryClaim: storedClaim,
      };
    });

    return { claimed, claim: storedClaim };
  }

  async _settleRuntimeRecoveryClaim(groupId, phase, reason = null) {
    const normalizedGroupId = toStringOrNull(groupId);
    if (!normalizedGroupId) return null;

    return this._updateGroup(normalizedGroupId, (previous) => {
      const current = normalizeRecoveryClaim(previous?.runtimeRecoveryClaim ?? null);
      if (!current) return previous;
      return {
        ...(previous || {}),
        runtimeRecoveryClaim: {
          ...current,
          phase: toStringOrNull(phase) || 'awaiting_runtime',
          reason: toStringOrNull(reason),
          updatedAt: new Date().toISOString(),
        },
      };
    });
  }

  async _clearRuntimeRecoveryClaim(groupId) {
    const normalizedGroupId = toStringOrNull(groupId);
    if (!normalizedGroupId) return null;
    return this._updateGroup(normalizedGroupId, (previous) => ({
      ...(previous || {}),
      runtimeRecoveryClaim: null,
    }));
  }

  async prepareConversationCreateBootstrap({ ciphersuite, source = 'messages_v2_create' } = {}) {
    const identity = await this.ensureIdentity({ ciphersuite });
    const disposition = await this.getBootstrapPublishDisposition({
      identityId: identity.identityId,
      clientId: identity.clientId,
      ciphersuite: identity.ciphersuite,
    });
    let published = null;
    if (disposition.allowPublish) {
      published = await this.publishKeyPackageAndSync(identity.identityId, {
        forcePublish: false,
      });
    }
    return {
      identity,
      identityId: identity.identityId,
      clientId: identity.clientId,
      ciphersuite: identity.ciphersuite,
      source,
      targetRef: published?.targetRef ?? disposition.targetRef ?? null,
    };
  }

  clearConversationCreateBootstrapAuthority() {
    return null;
  }

  _rememberLocalRuntimeAssertion({ groupId = null, runtimeHandle = null, epoch = null } = {}) {
    const normalizedGroupId = toStringOrNull(groupId);
    const normalizedRuntimeHandle = toStringOrNull(runtimeHandle);
    const normalizedEpoch = toFiniteEpoch(epoch);
    if (!normalizedGroupId || !normalizedRuntimeHandle || normalizedEpoch == null) {
      return null;
    }
    const assertion = {
      groupId: normalizedGroupId,
      runtimeHandle: normalizedRuntimeHandle,
      epoch: normalizedEpoch,
    };
    this.localRuntimeAssertions.set(normalizedGroupId, assertion);
    return assertion;
  }

  _resolveLocalRuntimeAssertion({ groupId = null, groupHandle = null } = {}) {
    const normalizedGroupId = toStringOrNull(groupId);
    const normalizedGroupHandle = toStringOrNull(groupHandle);
    if (normalizedGroupId && this.localRuntimeAssertions.has(normalizedGroupId)) {
      return this.localRuntimeAssertions.get(normalizedGroupId) || null;
    }
    if (!normalizedGroupHandle) {
      return null;
    }
    for (const assertion of this.localRuntimeAssertions.values()) {
      if (toStringOrNull(assertion?.runtimeHandle ?? null) === normalizedGroupHandle) {
        return assertion;
      }
    }
    return null;
  }

  async _invalidateLocalRuntime(
    groupId,
    {
      authoritativeEpoch = null,
      authoritativeCiphersuite = null,
      clearRecoveryClaim = false,
      source = 'messages_v2_runtime_invalidated',
    } = {},
  ) {
    const normalizedGroupId = toStringOrNull(groupId);
    if (!normalizedGroupId) {
      return null;
    }

    await Promise.resolve(dropRuntimeGroupsByGroupId?.({ groupId: normalizedGroupId })).catch(
      () => null,
    );
    this.localRuntimeAssertions.delete(normalizedGroupId);

    const normalizedEpoch = toFiniteEpoch(authoritativeEpoch);
    const normalizedCiphersuite = normalizeSuiteId(authoritativeCiphersuite);
    return this._updateGroup(normalizedGroupId, (previous) => ({
      ...(previous || {}),
      groupId: normalizedGroupId,
      group_id: normalizedGroupId,
      epoch:
        normalizedEpoch ?? toFiniteEpoch(previous?.epoch ?? previous?.current_epoch ?? null) ?? 1,
      current_epoch:
        normalizedEpoch ?? toFiniteEpoch(previous?.epoch ?? previous?.current_epoch ?? null) ?? 1,
      ciphersuite:
        normalizedCiphersuite ??
        normalizeSuiteId(previous?.ciphersuite ?? this.preferredCiphersuite),
      runtimeHandle: null,
      runtime_handle: null,
      runtimeEpoch: null,
      runtime_epoch: null,
      runtimeSessionGeneration: null,
      runtime_session_generation: null,
      runtimeLive: false,
      runtime_live: false,
      ingestRuntimeHandle: null,
      ingest_runtime_handle: null,
      ingestHandshakeServerSeq: null,
      ingest_handshake_server_seq: null,
      runtimeRecoveryClaim: clearRecoveryClaim
        ? null
        : normalizeRecoveryClaim(previous?.runtimeRecoveryClaim ?? null),
      source,
    }));
  }

  async _discardNonAuthoritativeRuntime(groupId, info = null) {
    const normalizedGroupId = toStringOrNull(groupId);
    if (!normalizedGroupId || hasConcreteRuntime(info) || !hasObservedRuntimeState(info)) {
      return info;
    }

    const conflictsWithAuthority = runtimeConflictsWithAuthority({
      runtimeEpoch: info?.runtimeEpoch ?? info?.runtime_epoch ?? null,
      authoritativeEpoch: info?.epoch ?? info?.current_epoch ?? null,
    });
    const sessionGeneration = toFiniteEpoch(
      info?.sessionGeneration ?? info?.session_generation ?? null,
    );
    const runtimeSessionGeneration = toFiniteEpoch(
      info?.runtimeSessionGeneration ?? info?.runtime_session_generation ?? null,
    );
    const generationConflictsWithAuthority = Boolean(
      sessionGeneration != null &&
        runtimeSessionGeneration != null &&
        runtimeSessionGeneration !== sessionGeneration,
    );
    if (!conflictsWithAuthority && !generationConflictsWithAuthority) {
      return info;
    }

    await this._invalidateLocalRuntime(normalizedGroupId, {
      authoritativeEpoch: info?.epoch ?? info?.current_epoch ?? null,
      authoritativeCiphersuite: info?.ciphersuite ?? null,
      source: 'messages_v2_non_authoritative_runtime_discarded',
    });
    return (await this.groupInfo({ groupId: normalizedGroupId }).catch(() => null)) || info;
  }

  _runtimeRecoveryKey({ kind, groupId = null, identity = null, reissue = null } = {}) {
    const normalizedKind = toStringOrNull(kind) || 'runtime';
    const ownsCurrentState = ['join_bundle', 'reissue_welcome', 'authoritative_conflict'].includes(
      normalizedKind,
    );
    const identityId =
      toStringOrNull(identity?.clientId ?? identity?.client_id ?? null) ||
      toStringOrNull(identity?.identityId ?? identity?.identity_id ?? null) ||
      this.identityClientId ||
      'unknown-client';
    return [
      ownsCurrentState ? 'current_state' : normalizedKind,
      toStringOrNull(groupId) || 'unknown-group',
      identityId,
      ownsCurrentState ? 'authority' : reissue === true ? 'reissue' : 'standard',
    ].join(':');
  }

  _withRuntimeRecoverySingleFlight(key, task) {
    const normalizedKey = toStringOrNull(key);
    if (!normalizedKey) {
      return task();
    }
    if (this.runtimeRecoveryPromises.has(normalizedKey)) {
      return this.runtimeRecoveryPromises.get(normalizedKey);
    }
    const promise = Promise.resolve()
      .then(task)
      .finally(() => {
        if (this.runtimeRecoveryPromises.get(normalizedKey) === promise) {
          this.runtimeRecoveryPromises.delete(normalizedKey);
        }
      });
    this.runtimeRecoveryPromises.set(normalizedKey, promise);
    return promise;
  }

  _withRuntimeRestoreSingleFlight(key, task) {
    const normalizedKey = toStringOrNull(key);
    if (!normalizedKey) {
      return task();
    }
    if (this.runtimeRestorePromises.has(normalizedKey)) {
      return this.runtimeRestorePromises.get(normalizedKey);
    }
    const promise = Promise.resolve()
      .then(task)
      .finally(() => {
        if (this.runtimeRestorePromises.get(normalizedKey) === promise) {
          this.runtimeRestorePromises.delete(normalizedKey);
        }
      });
    this.runtimeRestorePromises.set(normalizedKey, promise);
    return promise;
  }

  async _restoreRuntimeGroup({
    groupId,
    conversationId = null,
    identity = null,
    info = null,
  } = {}) {
    const normalizedGroupId = toStringOrNull(groupId);
    if (!normalizedGroupId || hasConcreteRuntime(info) || isMessengerV2ReadOnlyGroup(info)) {
      return info;
    }

    const identityId =
      toStringOrNull(identity?.clientId ?? identity?.client_id ?? null) ||
      toStringOrNull(identity?.identityId ?? identity?.identity_id ?? null) ||
      this.identityClientId ||
      this.identityId ||
      null;
    const restoreKey = [
      normalizedGroupId,
      identityId || 'unknown-client',
      normalizeRuntimeCiphersuite(info?.ciphersuite ?? this.preferredCiphersuite),
    ].join(':');

    return this._withRuntimeRestoreSingleFlight(restoreKey, async () => {
      await this.ensureInitialized();
      const identityRecord =
        (await this._loadIdentityRecord({ refreshFromStorage: true }).catch(() => null)) || null;
      const storageSnapshotB64 = pickBase64(
        identityRecord?.storageSnapshotB64,
        identityRecord?.storage_snapshot_b64,
        // Migration-only fallback for records written before the provider
        // snapshot became identity-scoped and authoritative.
        info?.storageSnapshotB64,
        info?.storage_snapshot_b64,
      );
      const mlsGroupIdB64 = pickBase64(info?.mlsGroupIdB64, info?.mls_group_id_b64);
      const restored = await Promise.resolve(
        restoreRuntimeGroup({
          groupId: normalizedGroupId,
          identityId,
          ciphersuite: info?.ciphersuite ?? this.preferredCiphersuite,
          mlsGroupIdB64,
          mls_group_id_b64: mlsGroupIdB64,
          storageSnapshotB64,
        }),
      ).catch((error) => ({
        restored: false,
        reason: 'restore_failed',
        error: error?.message || String(error || ''),
      }));

      if (!restored?.restored || !toStringOrNull(restored?.groupHandle ?? restored?.group_handle)) {
        await this._updateGroup(normalizedGroupId, (previous) => ({
          ...(info || {}),
          ...(previous || {}),
          groupId: normalizedGroupId,
          group_id: normalizedGroupId,
          conversationId: toStringOrNull(
            conversationId ?? info?.conversationId ?? info?.conversation_id ?? null,
          ),
          conversation_id: toStringOrNull(
            conversationId ?? info?.conversationId ?? info?.conversation_id ?? null,
          ),
          runtimeRestoreAttemptedAt: Date.now(),
          runtime_restore_attempted_at: Date.now(),
          runtimeRestoreReason: toStringOrNull(restored?.reason) || 'not_restored',
          runtime_restore_reason: toStringOrNull(restored?.reason) || 'not_restored',
          runtimeRestoreHadStorage: Boolean(storageSnapshotB64),
          runtime_restore_had_storage: Boolean(storageSnapshotB64),
        }));
        return (await this.groupInfo({ groupId: normalizedGroupId }).catch(() => null)) || info;
      }

      const runtimeHandle = toStringOrNull(restored.groupHandle ?? restored.group_handle);
      const restoredEpoch =
        toFiniteEpoch(restored.epoch ?? restored.runtimeEpoch ?? restored.runtime_epoch ?? null) ??
        toFiniteEpoch(
          info?.runtimeEpoch ?? info?.runtime_epoch ?? info?.epoch ?? info?.current_epoch,
        ) ??
        1;
      const serverEpoch =
        toFiniteEpoch(info?.epoch ?? info?.current_epoch ?? null) ??
        toFiniteEpoch(info?.currentEpoch ?? null) ??
        null;
      const restoredCiphersuite = normalizeSuiteId(
        restored?.ciphersuite ?? info?.ciphersuite ?? this.preferredCiphersuite,
      );
      const serverCiphersuite = normalizeSuiteId(info?.ciphersuite ?? null);
      const restoredRuntimeBehindAuthority = Boolean(
        restoredEpoch != null && serverEpoch != null && restoredEpoch < serverEpoch,
      );
      const restoredRuntimeConflicts =
        restoredRuntimeBehindAuthority ||
        runtimeConflictsWithAuthority({
          runtimeEpoch: restoredEpoch,
          authoritativeEpoch: serverEpoch,
          runtimeCiphersuite: restoredCiphersuite,
          authoritativeCiphersuite: serverCiphersuite,
        });
      if (restoredRuntimeConflicts) {
        await this._invalidateLocalRuntime(normalizedGroupId, {
          authoritativeEpoch: serverEpoch,
          authoritativeCiphersuite: serverCiphersuite,
          clearRecoveryClaim: true,
          source: restoredRuntimeBehindAuthority
            ? 'messages_v2_restored_runtime_requires_current_state'
            : 'messages_v2_restored_runtime_rejected',
        });
        return (await this.groupInfo({ groupId: normalizedGroupId }).catch(() => null)) || info;
      }
      this._rememberLocalRuntimeAssertion({
        groupId: normalizedGroupId,
        runtimeHandle,
        epoch: restoredEpoch,
      });
      const restoredStorageSnapshotB64 = pickBase64(
        restored.storageSnapshotB64,
        restored.storage_snapshot_b64,
        storageSnapshotB64,
      );
      await this._persistProviderSnapshot(restoredStorageSnapshotB64);

      const updated = await this._updateGroup(normalizedGroupId, (previous) => {
        const authoritativeEpoch =
          serverEpoch ??
          toFiniteEpoch(info?.epoch ?? info?.current_epoch ?? null) ??
          toFiniteEpoch(previous?.epoch ?? previous?.current_epoch ?? null) ??
          restoredEpoch;
        return asConcreteRuntimeInfo(
          {
            ...(info || {}),
            ...(previous || {}),
            groupId: normalizedGroupId,
            group_id: normalizedGroupId,
            conversationId: toStringOrNull(
              conversationId ?? info?.conversationId ?? info?.conversation_id ?? null,
            ),
            conversation_id: toStringOrNull(
              conversationId ?? info?.conversationId ?? info?.conversation_id ?? null,
            ),
            ciphersuite: normalizeRuntimeCiphersuite(
              restoredCiphersuite,
              this.preferredCiphersuite,
            ),
            epoch: authoritativeEpoch,
            current_epoch: authoritativeEpoch,
            ratchetTreeB64:
              pickBase64(restored.ratchetTreeB64, restored.ratchet_tree_b64) ||
              previous?.ratchetTreeB64 ||
              previous?.ratchet_tree_b64 ||
              info?.ratchetTreeB64 ||
              info?.ratchet_tree_b64 ||
              null,
            ratchet_tree_b64:
              pickBase64(restored.ratchetTreeB64, restored.ratchet_tree_b64) ||
              previous?.ratchet_tree_b64 ||
              previous?.ratchetTreeB64 ||
              info?.ratchet_tree_b64 ||
              info?.ratchetTreeB64 ||
              null,
            mlsGroupIdB64:
              pickBase64(
                restored.mlsGroupIdB64,
                restored.mls_group_id_b64,
                mlsGroupIdB64,
                previous?.mlsGroupIdB64,
                previous?.mls_group_id_b64,
                info?.mlsGroupIdB64,
                info?.mls_group_id_b64,
              ) || null,
            mls_group_id_b64:
              pickBase64(
                restored.mlsGroupIdB64,
                restored.mls_group_id_b64,
                mlsGroupIdB64,
                previous?.mlsGroupIdB64,
                previous?.mls_group_id_b64,
                info?.mlsGroupIdB64,
                info?.mls_group_id_b64,
              ) || null,
            runtimeRestoredAt: Date.now(),
            runtime_restored_at: Date.now(),
            runtimeRestoreReason: null,
            runtime_restore_reason: null,
            runtimeRestoreHadStorage: false,
            runtime_restore_had_storage: false,
            source: 'messages_v2_runtime_restored',
          },
          {
            runtimeHandle,
            runtimeEpoch: restoredEpoch,
          },
        );
      });

      return (
        (await this.groupInfo({ groupId: normalizedGroupId }).catch(() => null)) || updated || info
      );
    });
  }

  async _withBrowserRuntimeRecoveryLock({ groupId, identity = null, info = null } = {}, task) {
    const normalizedGroupId = toStringOrNull(groupId);
    const clientId =
      toStringOrNull(identity?.clientId ?? identity?.client_id ?? null) ||
      toStringOrNull(identity?.identityId ?? identity?.identity_id ?? null) ||
      this.identityClientId ||
      this.identityId ||
      'unknown-client';
    const lockKey = buildRuntimeRecoveryLockKey({
      userId: this.userId,
      groupId: normalizedGroupId,
      clientId,
    });
    return withItemLock(lockKey, () =>
      this._withRuntimeRecoveryLease({ lockKey, normalizedGroupId, clientId, info }, task),
    );
  }

  async _withKeyPackageRecoveryLock(identity = null, task) {
    const clientId =
      toStringOrNull(identity?.clientId ?? identity?.client_id ?? null) ||
      toStringOrNull(identity?.identityId ?? identity?.identity_id ?? null) ||
      this.identityClientId ||
      this.identityId ||
      'unknown-client';
    return withItemLock(
      buildKeyPackageRecoveryLockKey({
        userId: this.userId,
        clientId,
      }),
      task,
    );
  }

  async _withRuntimeRecoveryLease({ lockKey, normalizedGroupId, clientId, info } = {}, task) {
    const nowMs = Date.now();
    const existing = await Promise.resolve(getItem(lockKey)).catch(() => null);
    const existingOwner = toStringOrNull(existing?.ownerId ?? existing?.owner_id ?? null);
    const existingExpiresAt = Number(existing?.expiresAt ?? existing?.expires_at ?? 0);

    if (existingOwner && existingOwner !== this.instanceId && existingExpiresAt > nowMs) {
      const deadline = nowMs + RUNTIME_RECOVERY_LOCK_WAIT_MS;
      let latest = info;
      while (Date.now() < deadline) {
        await delay(RUNTIME_RECOVERY_LOCK_POLL_MS);
        latest = (await this.groupInfo({ groupId: normalizedGroupId }).catch(() => null)) || latest;
        if (hasConcreteRuntime(latest) || isMessengerV2ReadOnlyGroup(latest)) {
          return latest;
        }
        const refreshed = await Promise.resolve(getItem(lockKey)).catch(() => null);
        const refreshedOwner = toStringOrNull(refreshed?.ownerId ?? refreshed?.owner_id ?? null);
        const refreshedExpiresAt = Number(refreshed?.expiresAt ?? refreshed?.expires_at ?? 0);
        if (
          !refreshedOwner ||
          refreshedOwner === this.instanceId ||
          refreshedExpiresAt <= Date.now()
        ) {
          break;
        }
      }

      // Never steal a live recovery lease just because this caller exhausted
      // its short wait budget. The current owner may still be persisting and
      // acknowledging the Welcome under the longer lease.
      const remainingLock = await Promise.resolve(getItem(lockKey)).catch(() => null);
      const remainingOwner = toStringOrNull(
        remainingLock?.ownerId ?? remainingLock?.owner_id ?? null,
      );
      const remainingExpiresAt = Number(remainingLock?.expiresAt ?? remainingLock?.expires_at ?? 0);
      if (remainingOwner && remainingOwner !== this.instanceId && remainingExpiresAt > Date.now()) {
        return latest;
      }
    }

    const lock = {
      ownerId: this.instanceId,
      owner_id: this.instanceId,
      groupId: normalizedGroupId,
      group_id: normalizedGroupId,
      clientId,
      client_id: clientId,
      expiresAt: Date.now() + RUNTIME_RECOVERY_LOCK_TTL_MS,
      expires_at: Date.now() + RUNTIME_RECOVERY_LOCK_TTL_MS,
    };
    let acquired = false;
    await Promise.resolve(
      updateItem(lockKey, (current) => {
        const currentOwner = toStringOrNull(current?.ownerId ?? current?.owner_id ?? null);
        const currentExpiresAt = Number(current?.expiresAt ?? current?.expires_at ?? 0);
        if (currentOwner && currentOwner !== this.instanceId && currentExpiresAt > Date.now()) {
          return current;
        }
        acquired = true;
        return lock;
      }),
    ).catch(() => null);
    if (!acquired) {
      return (await this.groupInfo({ groupId: normalizedGroupId }).catch(() => null)) || info;
    }

    let released = false;
    let leaseRefresh = Promise.resolve();
    const refreshLease = () => {
      if (released) return;
      leaseRefresh = leaseRefresh
        .catch(() => null)
        .then(() =>
          updateItem(lockKey, (current) => {
            const currentOwner = toStringOrNull(current?.ownerId ?? current?.owner_id ?? null);
            if (currentOwner !== this.instanceId) {
              return current;
            }
            const expiresAt = Date.now() + RUNTIME_RECOVERY_LOCK_TTL_MS;
            return {
              ...(current || {}),
              expiresAt,
              expires_at: expiresAt,
            };
          }),
        )
        .catch(() => null);
    };
    const leaseRefreshTimer = setInterval(refreshLease, RUNTIME_RECOVERY_LOCK_REFRESH_MS);

    try {
      return await task();
    } finally {
      released = true;
      clearInterval(leaseRefreshTimer);
      await leaseRefresh.catch(() => null);
      await Promise.resolve(
        updateItem(lockKey, (current) => {
          const currentOwner = toStringOrNull(current?.ownerId ?? current?.owner_id ?? null);
          return currentOwner === this.instanceId ? null : current;
        }),
      ).catch(() => null);
    }
  }

  async adoptConversationCreateBootstrapAuthority({
    identityId = null,
    groupId = null,
    groupHandle = null,
    conversationId = null,
    ciphersuite = null,
    targetRef = null,
    source = 'messages_v2_create_response',
  } = {}) {
    const normalizedGroupId = toStringOrNull(groupId);
    if (!normalizedGroupId) {
      return null;
    }
    return this._updateGroup(normalizedGroupId, (previous) => ({
      ...(previous || {}),
      groupId: normalizedGroupId,
      group_id: normalizedGroupId,
      groupHandle: toStringOrNull(groupHandle) || normalizedGroupId,
      group_handle: toStringOrNull(groupHandle) || normalizedGroupId,
      conversationId: toStringOrNull(conversationId),
      conversation_id: toStringOrNull(conversationId),
      identityId: toStringOrNull(identityId ?? this.identityId),
      identity_id: toStringOrNull(identityId ?? this.identityId),
      clientId: toStringOrNull(identityId ?? this.identityClientId ?? this.identityId),
      client_id: toStringOrNull(identityId ?? this.identityClientId ?? this.identityId),
      ciphersuite: normalizeSuiteId(
        ciphersuite ?? previous?.ciphersuite ?? this.preferredCiphersuite,
      ),
      welcomeTargetRefLock:
        normalizeHex(
          targetRef ?? previous?.welcomeTargetRefLock ?? previous?.welcome_target_ref_lock,
        ) || null,
      welcome_target_ref_lock:
        normalizeHex(
          targetRef ?? previous?.welcomeTargetRefLock ?? previous?.welcome_target_ref_lock,
        ) || null,
      welcomeTargetIdentityId:
        toStringOrNull(identityId ?? this.identityClientId ?? this.identityId) || null,
      welcome_target_identity_id:
        toStringOrNull(identityId ?? this.identityClientId ?? this.identityId) || null,
      bootstrapAuthority: 'creator',
      bootstrap_authority: 'creator',
      source,
    }));
  }

  async applyServerSnapshot(
    snapshot,
    { conversationId = null, source = 'messages_v2_snapshot' } = {},
  ) {
    if (!snapshot || typeof snapshot !== 'object') {
      return null;
    }
    const normalizedGroupId = toStringOrNull(snapshot.group_id ?? snapshot.groupId ?? null);
    if (!normalizedGroupId) {
      return null;
    }
    const authoritativeEpoch = toFiniteEpoch(
      snapshot.epoch ?? snapshot.current_epoch ?? snapshot.currentEpoch,
    );
    const existingGroups = await this._loadGroups({ refreshFromStorage: true });
    const existingEntry = existingGroups[normalizedGroupId] || null;
    const explicitSessionGeneration = toFiniteEpoch(
      snapshot.sessionGeneration ?? snapshot.session_generation ?? null,
    );
    const existingSessionGeneration =
      toFiniteEpoch(
        existingEntry?.sessionGeneration ?? existingEntry?.session_generation ?? null,
      ) ?? 1;
    const authoritativeSessionGeneration = explicitSessionGeneration ?? existingSessionGeneration;
    const sessionGenerationAdvanced = Boolean(
      existingEntry && authoritativeSessionGeneration > existingSessionGeneration,
    );
    const sessionGenerationRegressed = Boolean(
      existingEntry && authoritativeSessionGeneration < existingSessionGeneration,
    );
    if (sessionGenerationRegressed) {
      return this.groupInfo({ groupId: normalizedGroupId }).catch(() => existingEntry);
    }
    const snapshotGeneratedAt = toStringOrNull(
      snapshot.snapshotGeneratedAt ?? snapshot.snapshot_generated_at ?? null,
    );
    const existingSnapshotGeneratedAt = toStringOrNull(
      existingEntry?.serverSnapshotGeneratedAt ??
        existingEntry?.server_snapshot_generated_at ??
        null,
    );
    const staleSnapshot = Boolean(
      toTimestampMillis(snapshotGeneratedAt) != null &&
        toTimestampMillis(existingSnapshotGeneratedAt) != null &&
        toTimestampMillis(snapshotGeneratedAt) < toTimestampMillis(existingSnapshotGeneratedAt),
    );
    if (staleSnapshot && !sessionGenerationAdvanced) {
      return this.groupInfo({ groupId: normalizedGroupId }).catch(() => existingEntry);
    }
    const observedRuntime = existingEntry
      ? await this._findRuntimeGroup(existingEntry, normalizedGroupId, null).catch(() => null)
      : null;
    const snapshotCurrentDevice =
      snapshot.currentDevice && typeof snapshot.currentDevice === 'object'
        ? snapshot.currentDevice
        : snapshot.current_device && typeof snapshot.current_device === 'object'
          ? snapshot.current_device
          : null;
    const currentDeviceEnrollmentRequired = isMessengerV2CurrentDeviceEnrollmentRequired(
      { currentDevice: snapshotCurrentDevice },
      { clientId: this.identityClientId },
    );
    const observedRuntimeEpoch = toFiniteEpoch(
      observedRuntime?.epoch ?? existingEntry?.runtimeEpoch ?? existingEntry?.runtime_epoch,
    );
    const authoritativeCiphersuite = normalizeSuiteId(snapshot.ciphersuite ?? null);
    const observedRuntimeCiphersuite = normalizeSuiteId(
      observedRuntime?.ciphersuite ?? existingEntry?.ciphersuite ?? null,
    );
    const runtimeConflicts =
      sessionGenerationAdvanced ||
      currentDeviceEnrollmentRequired ||
      runtimeConflictsWithAuthority({
        runtimeEpoch: observedRuntimeEpoch,
        authoritativeEpoch,
        runtimeCiphersuite: observedRuntimeCiphersuite,
        authoritativeCiphersuite,
      });
    if (runtimeConflicts) {
      await this._invalidateLocalRuntime(normalizedGroupId, {
        authoritativeEpoch,
        authoritativeCiphersuite,
        clearRecoveryClaim: true,
        source: 'messages_v2_server_authority_runtime_invalidated',
      });
    }
    const normalizedPendingWelcomes = normalizePendingWelcomes(
      snapshot.pending_welcomes ?? snapshot.pendingWelcomes ?? [],
    );
    const pendingWelcomes = normalizedPendingWelcomes.filter((welcome) => {
      const generation = toFiniteEpoch(
        welcome?.session_generation ?? welcome?.sessionGeneration ?? null,
      );
      return generation == null || generation === authoritativeSessionGeneration;
    });
    const historyKeyB64 = pickBase64(snapshot.history_key_b64, snapshot.historyKeyB64) || null;
    if (historyKeyB64 && conversationId) {
      await Promise.resolve(
        persistHistoryKey({
          conversationId,
          keyB64: historyKeyB64,
        }),
      ).catch(() => null);
    }
    const snapshotHasRecoveryBlockedMarker =
      hasOwn(snapshot, 'recovery_blocked_marker') || hasOwn(snapshot, 'recoveryBlockedMarker');
    const snapshotHasRecoveryBlockedReason =
      hasOwn(snapshot, 'recovery_blocked_reason') || hasOwn(snapshot, 'recoveryBlockedReason');
    const snapshotHasRecoveryBlockedAt =
      hasOwn(snapshot, 'recovery_blocked_at') || hasOwn(snapshot, 'recoveryBlockedAt');
    const snapshotHasReadinessState =
      Boolean(snapshotCurrentDevice && hasOwn(snapshotCurrentDevice, 'state')) ||
      hasOwn(snapshot, 'readiness_state') ||
      hasOwn(snapshot, 'readinessState');
    const snapshotHasReadinessReason =
      Boolean(snapshotCurrentDevice && hasOwn(snapshotCurrentDevice, 'reason')) ||
      hasOwn(snapshot, 'readiness_reason') ||
      hasOwn(snapshot, 'readinessReason');
    const snapshotHasReadyToSend =
      hasOwn(snapshot, 'ready_to_send') || hasOwn(snapshot, 'readyToSend');
    const snapshotHasSendCapable =
      Boolean(
        snapshotCurrentDevice &&
          (hasOwn(snapshotCurrentDevice, 'send_capable') ||
            hasOwn(snapshotCurrentDevice, 'sendCapable')),
      ) ||
      hasOwn(snapshot, 'send_capable') ||
      hasOwn(snapshot, 'sendCapable');
    const snapshotHasReadOnly = hasOwn(snapshot, 'read_only') || hasOwn(snapshot, 'readOnly');
    const nextEntry = await this._updateGroup(normalizedGroupId, (previous) => {
      const sessionPrevious = sessionGenerationAdvanced ? null : previous;
      const welcomeTargetClientId =
        toStringOrNull(
          pendingWelcomes[0]?.to_client_id ??
            pendingWelcomes[0]?.client_id ??
            sessionPrevious?.welcomeTargetIdentityId ??
            sessionPrevious?.welcome_target_identity_id ??
            null,
        ) || null;
      const resolvedClientId = toStringOrNull(
        this.identityClientId ?? welcomeTargetClientId ?? previous?.clientId ?? previous?.client_id,
      );
      const targetedWelcome = pickPendingWelcomeForIdentity(pendingWelcomes, {
        clientId: resolvedClientId,
        identityId: resolvedClientId,
      });
      const welcomeTargetRef =
        normalizeHex(
          targetedWelcome?.used_kp_ref ??
            targetedWelcome?.usedKpRef ??
            snapshot.used_kp_ref ??
            snapshot.usedKpRef ??
            sessionPrevious?.welcomeTargetRefLock ??
            sessionPrevious?.welcome_target_ref_lock ??
            null,
        ) || null;
      const epoch =
        toFiniteEpoch(snapshot.epoch ?? snapshot.current_epoch ?? snapshot.currentEpoch) ??
        toFiniteEpoch(previous?.epoch) ??
        1;
      const previousRecoveryClaim = normalizeRecoveryClaim(
        sessionPrevious?.runtimeRecoveryClaim ?? null,
      );
      const candidateRuntimeEpoch =
        observedRuntimeEpoch ??
        Math.max(
          toFiniteEpoch(sessionPrevious?.runtimeEpoch ?? sessionPrevious?.runtime_epoch) ?? 0,
          toFiniteEpoch(snapshot.runtimeEpoch ?? snapshot.runtime_epoch) ?? 0,
        );
      const runtimeEpoch =
        !runtimeConflicts && candidateRuntimeEpoch > 0 && candidateRuntimeEpoch <= epoch
          ? candidateRuntimeEpoch
          : null;
      const runtimeHandle = runtimeConflicts
        ? null
        : toStringOrNull(
            observedRuntime?.groupHandle ??
              observedRuntime?.group_handle ??
              sessionPrevious?.runtimeHandle ??
              sessionPrevious?.runtime_handle ??
              null,
          ) || null;
      const runtimeSessionGeneration = runtimeHandle ? authoritativeSessionGeneration : null;
      const runtimeLive = Boolean(
        !runtimeConflicts &&
          runtimeHandle &&
          runtimeEpoch != null &&
          (observedRuntime || sessionPrevious?.runtimeLive === true),
      );
      let recoveryBlockedMarker = snapshotHasRecoveryBlockedMarker
        ? toStringOrNull(snapshot.recoveryBlockedMarker ?? snapshot.recovery_blocked_marker ?? null)
        : toStringOrNull(
            sessionPrevious?.recoveryBlockedMarker ??
              sessionPrevious?.recovery_blocked_marker ??
              null,
          );
      let recoveryBlockedReason = snapshotHasRecoveryBlockedReason
        ? toStringOrNull(snapshot.recoveryBlockedReason ?? snapshot.recovery_blocked_reason ?? null)
        : toStringOrNull(
            sessionPrevious?.recoveryBlockedReason ??
              sessionPrevious?.recovery_blocked_reason ??
              null,
          );
      let recoveryBlockedAt = snapshotHasRecoveryBlockedAt
        ? toStringOrNull(snapshot.recoveryBlockedAt ?? snapshot.recovery_blocked_at ?? null)
        : toStringOrNull(
            sessionPrevious?.recoveryBlockedAt ?? sessionPrevious?.recovery_blocked_at ?? null,
          );
      let readinessState = snapshotHasReadinessState
        ? normalizeReadinessState(
            snapshotCurrentDevice?.state ??
              snapshot.readinessState ??
              snapshot.readiness_state ??
              null,
          )
        : normalizeReadinessState(
            sessionPrevious?.readinessState ?? sessionPrevious?.readiness_state ?? null,
          );
      let readinessReason = snapshotHasReadinessReason
        ? toStringOrNull(
            snapshotCurrentDevice?.reason ??
              snapshot.readinessReason ??
              snapshot.readiness_reason ??
              null,
          )
        : snapshotHasReadinessState
          ? null
          : toStringOrNull(
              sessionPrevious?.readinessReason ?? sessionPrevious?.readiness_reason ?? null,
            );
      const snapshotMembershipStatus = toStringOrNull(
        snapshot.currentUserGroupMembershipStatus ??
          snapshot.current_user_group_membership_status ??
          snapshot.membershipStatus ??
          snapshot.membership_status ??
          null,
      );
      const snapshotSuggestsActiveMembership =
        snapshotMembershipStatus === 'active' ||
        snapshot.formerMember === false ||
        snapshot.former_member === false ||
        snapshot.readOnly === false ||
        snapshot.read_only === false;
      const snapshotSuggestsActiveSetup =
        snapshotSuggestsActiveMembership ||
        pendingWelcomes.length > 0 ||
        snapshot.readyToSend === true ||
        snapshot.ready_to_send === true ||
        snapshot.sendCapable === true ||
        snapshot.send_capable === true;
      const recoveryAuthorityChanged = Boolean(
        previousRecoveryClaim &&
          previousRecoveryClaim.clientId &&
          resolvedClientId &&
          previousRecoveryClaim.clientId !== resolvedClientId,
      );
      if (recoveryAuthorityChanged) {
        recoveryBlockedMarker = null;
        recoveryBlockedReason = null;
        recoveryBlockedAt = null;
      }
      if (
        snapshotSuggestsActiveSetup &&
        !snapshotHasReadinessState &&
        readinessState === 'read_only'
      ) {
        readinessState = null;
      }
      if (
        snapshotSuggestsActiveSetup &&
        !snapshotHasReadinessReason &&
        readinessReason === 'former_member'
      ) {
        readinessReason = null;
      }
      const readOnly = snapshotHasReadOnly
        ? Boolean(snapshot.readOnly ?? snapshot.read_only ?? false)
        : snapshotHasReadinessState || snapshotHasReadinessReason
          ? readinessState === 'read_only' || readinessReason === 'former_member'
          : snapshotSuggestsActiveSetup
            ? false
            : isMessengerV2ReadOnlyGroup(sessionPrevious);
      const resolvedReadinessState = readOnly ? 'read_only' : readinessState;
      const resolvedReadinessReason =
        readOnly && !readinessReason ? 'former_member' : readinessReason;
      const readyToSend = snapshotHasReadyToSend
        ? !readOnly && Boolean(snapshot.readyToSend ?? snapshot.ready_to_send ?? false)
        : snapshotHasReadinessState || readOnly
          ? resolvedReadinessState === 'ready'
          : sessionPrevious?.readyToSend === true || sessionPrevious?.ready_to_send === true;
      const sendCapable = snapshotHasSendCapable
        ? !readOnly &&
          Boolean(
            snapshotCurrentDevice?.sendCapable ??
              snapshotCurrentDevice?.send_capable ??
              snapshot.sendCapable ??
              snapshot.send_capable ??
              false,
          )
        : snapshotHasReadinessState || readOnly
          ? resolvedReadinessState === 'ready'
          : sessionPrevious?.sendCapable === true || sessionPrevious?.send_capable === true;
      const resolvedReadyToSend = readOnly ? false : readyToSend;
      const resolvedSendCapable = readOnly ? false : sendCapable;
      const resolvedPendingWelcomes = readOnly ? [] : pendingWelcomes;
      return {
        ...(previous || {}),
        groupId: normalizedGroupId,
        group_id: normalizedGroupId,
        groupHandle:
          toStringOrNull(snapshot.group_handle ?? snapshot.groupHandle ?? null) ||
          toStringOrNull(previous?.groupHandle ?? previous?.group_handle ?? null) ||
          normalizedGroupId,
        group_handle:
          toStringOrNull(snapshot.group_handle ?? snapshot.groupHandle ?? null) ||
          toStringOrNull(previous?.groupHandle ?? previous?.group_handle ?? null) ||
          normalizedGroupId,
        conversationId: toStringOrNull(
          conversationId ??
            snapshot.conversationId ??
            snapshot.conversation_id ??
            previous?.conversationId ??
            previous?.conversation_id,
        ),
        conversation_id: toStringOrNull(
          conversationId ??
            snapshot.conversationId ??
            snapshot.conversation_id ??
            previous?.conversationId ??
            previous?.conversation_id,
        ),
        identityId: toStringOrNull(
          previous?.identityId ?? previous?.identity_id ?? this.identityId,
        ),
        identity_id: toStringOrNull(
          previous?.identityId ?? previous?.identity_id ?? this.identityId,
        ),
        clientId: toStringOrNull(
          this.identityClientId ?? previous?.clientId ?? previous?.client_id,
        ),
        client_id: toStringOrNull(
          this.identityClientId ?? previous?.clientId ?? previous?.client_id,
        ),
        ciphersuite:
          normalizeSuiteId(
            snapshot.ciphersuite ?? previous?.ciphersuite ?? this.preferredCiphersuite,
          ) ?? this.preferredCiphersuite,
        sessionGeneration: authoritativeSessionGeneration,
        session_generation: authoritativeSessionGeneration,
        epoch,
        current_epoch: epoch,
        runtimeEpoch,
        runtime_epoch: runtimeEpoch,
        runtimeSessionGeneration,
        runtime_session_generation: runtimeSessionGeneration,
        runtimeHandle,
        runtime_handle: runtimeHandle,
        runtimeLive,
        runtime_live: runtimeLive,
        pendingWelcomes: resolvedPendingWelcomes,
        pending_welcomes: resolvedPendingWelcomes,
        members: normalizeSnapshotMembers(snapshot).length
          ? normalizeSnapshotMembers(snapshot)
          : normalizeMembers(previous?.members),
        welcomeTargetRefLock: welcomeTargetRef,
        welcome_target_ref_lock: welcomeTargetRef,
        welcomeTargetIdentityId:
          toStringOrNull(
            targetedWelcome?.to_client_id ??
              targetedWelcome?.client_id ??
              sessionPrevious?.welcomeTargetIdentityId ??
              sessionPrevious?.welcome_target_identity_id ??
              resolvedClientId,
          ) || null,
        welcome_target_identity_id:
          toStringOrNull(
            targetedWelcome?.to_client_id ??
              targetedWelcome?.client_id ??
              sessionPrevious?.welcomeTargetIdentityId ??
              sessionPrevious?.welcome_target_identity_id ??
              resolvedClientId,
          ) || null,
        historyKeyB64:
          historyKeyB64 || pickBase64(previous?.historyKeyB64, previous?.history_key_b64) || null,
        history_key_b64:
          historyKeyB64 || pickBase64(previous?.historyKeyB64, previous?.history_key_b64) || null,
        mlsGroupIdB64:
          pickBase64(
            snapshot.mlsGroupIdB64,
            snapshot.mls_group_id_b64,
            previous?.mlsGroupIdB64,
            previous?.mls_group_id_b64,
          ) || null,
        mls_group_id_b64:
          pickBase64(
            snapshot.mlsGroupIdB64,
            snapshot.mls_group_id_b64,
            previous?.mlsGroupIdB64,
            previous?.mls_group_id_b64,
          ) || null,
        recoveryBlockedMarker,
        recovery_blocked_marker: recoveryBlockedMarker,
        recoveryBlockedReason,
        recovery_blocked_reason: recoveryBlockedReason,
        recoveryBlockedAt,
        recovery_blocked_at: recoveryBlockedAt,
        runtimeRecoveryClaim: recoveryAuthorityChanged ? null : previousRecoveryClaim,
        currentDevice: snapshotCurrentDevice,
        current_device: snapshotCurrentDevice,
        serverSnapshotGeneratedAt:
          snapshotGeneratedAt ||
          toStringOrNull(
            previous?.serverSnapshotGeneratedAt ?? previous?.server_snapshot_generated_at ?? null,
          ),
        server_snapshot_generated_at:
          snapshotGeneratedAt ||
          toStringOrNull(
            previous?.serverSnapshotGeneratedAt ?? previous?.server_snapshot_generated_at ?? null,
          ),
        serverReadinessState: resolvedReadinessState,
        server_readiness_state: resolvedReadinessState,
        serverReadinessReason: resolvedReadinessReason,
        server_readiness_reason: resolvedReadinessReason,
        serverSendCapable: resolvedSendCapable,
        server_send_capable: resolvedSendCapable,
        readinessState: resolvedReadinessState,
        readiness_state: resolvedReadinessState,
        readinessReason: resolvedReadinessReason,
        readiness_reason: resolvedReadinessReason,
        readOnly,
        read_only: readOnly,
        readyToSend: resolvedReadyToSend,
        ready_to_send: resolvedReadyToSend,
        sendCapable: resolvedSendCapable,
        send_capable: resolvedSendCapable,
        bootstrapAuthority: toStringOrNull(
          sessionPrevious?.bootstrapAuthority ?? sessionPrevious?.bootstrap_authority ?? null,
        ),
        bootstrap_authority: toStringOrNull(
          sessionPrevious?.bootstrapAuthority ?? sessionPrevious?.bootstrap_authority ?? null,
        ),
        source,
      };
    });
    return nextEntry;
  }

  async _fetchConversationSnapshot({
    conversationId = null,
    clientId = null,
    forceRefresh = false,
  } = {}) {
    const normalizedConversationId = toStringOrNull(conversationId);
    if (!normalizedConversationId || typeof fetchConversationMlsGroup !== 'function') {
      return null;
    }
    const normalizedClientId = toStringOrNull(clientId ?? this.identityClientId ?? null);
    const key = [normalizedConversationId, normalizedClientId || 'default'].join(':');
    const cached = this.conversationSnapshotCache.get(key);
    if (
      forceRefresh !== true &&
      cached &&
      Date.now() - cached.storedAt <= CONVERSATION_SNAPSHOT_CACHE_TTL_MS
    ) {
      return cached.snapshot;
    }
    const existing = this.conversationSnapshotPromises.get(key);
    if (existing) {
      return existing;
    }

    const promise = Promise.resolve(
      fetchConversationMlsGroup({
        conversationId: normalizedConversationId,
        clientId: normalizedClientId,
      }),
    )
      .then((snapshot) => {
        if (snapshot) {
          this.conversationSnapshotCache.set(key, {
            snapshot,
            storedAt: Date.now(),
          });
        }
        return snapshot;
      })
      .catch(() => null)
      .finally(() => {
        if (this.conversationSnapshotPromises.get(key) === promise) {
          this.conversationSnapshotPromises.delete(key);
        }
      });

    this.conversationSnapshotPromises.set(key, promise);
    return promise;
  }

  async ensureGroupForConversation({ conversationId = null, groupId = null } = {}) {
    const normalizedConversationId = toStringOrNull(conversationId);
    let normalizedGroupId = toStringOrNull(groupId);
    let existing = null;
    if (!normalizedGroupId && normalizedConversationId) {
      const groups = await this._loadGroups();
      existing = Object.values(groups).find(
        (entry) =>
          toStringOrNull(entry?.conversationId ?? entry?.conversation_id ?? null) ===
          normalizedConversationId,
      );
      normalizedGroupId = toStringOrNull(existing?.groupId ?? existing?.group_id ?? null);
    }

    const identity = await this.ensureIdentity({});
    if (normalizedConversationId) {
      const snapshot = await this._fetchConversationSnapshot({
        conversationId: normalizedConversationId,
        clientId: identity.clientId,
      });
      if (snapshot) {
        await this.applyServerSnapshot(snapshot, {
          conversationId: normalizedConversationId,
          source: 'messages_v2_ensure_group',
        });
        normalizedGroupId = toStringOrNull(
          snapshot.group_id ?? snapshot.groupId ?? normalizedGroupId,
        );
      }
    }
    return normalizedGroupId ? this.groupInfo({ groupId: normalizedGroupId }) : null;
  }

  async _findRuntimeGroup(entry, explicitGroupId = null, explicitHandle = null) {
    const groups = await Promise.resolve(workerListGroups?.()).catch(() => []);
    const expectedIdentityId = toStringOrNull(
      entry?.identityId ?? entry?.identity_id ?? this.identityId ?? null,
    );
    const candidates = new Set(
      [
        toStringOrNull(explicitGroupId),
        toStringOrNull(entry?.groupId ?? entry?.group_id ?? null),
        toStringOrNull(explicitHandle),
        toStringOrNull(entry?.groupHandle ?? entry?.group_handle ?? null),
        toStringOrNull(entry?.runtimeHandle ?? entry?.runtime_handle ?? null),
      ].filter(Boolean),
    );
    return (
      groups.find((item) => {
        const groupId = toStringOrNull(item?.groupId ?? null);
        const groupHandle = toStringOrNull(item?.groupHandle ?? null);
        const identityId = toStringOrNull(item?.identityId ?? item?.identity_id ?? null);
        if (expectedIdentityId && identityId && identityId !== expectedIdentityId) {
          return false;
        }
        return (groupId && candidates.has(groupId)) || (groupHandle && candidates.has(groupHandle));
      }) || null
    );
  }

  async groupInfo({ groupId = null, groupHandle = null } = {}) {
    const groups = await this._loadGroups({ refreshFromStorage: true });
    const normalizedGroupId = toStringOrNull(groupId);
    const normalizedHandle = toStringOrNull(groupHandle);
    let entry =
      (normalizedGroupId ? groups[normalizedGroupId] : null) ||
      Object.values(groups).find((item) => {
        const itemHandle = toStringOrNull(item?.groupHandle ?? item?.group_handle ?? null);
        return normalizedHandle && itemHandle === normalizedHandle;
      }) ||
      null;
    if (!entry) {
      return null;
    }

    const runtimeGroup = await this._findRuntimeGroup(entry, normalizedGroupId, normalizedHandle);
    const runtimeAssertion = this._resolveLocalRuntimeAssertion({
      groupId:
        normalizedGroupId ?? toStringOrNull(entry?.groupId ?? entry?.group_id ?? null) ?? null,
      groupHandle: normalizedHandle,
    });
    const entryReadinessState = normalizeReadinessState(
      entry?.readinessState ?? entry?.readiness_state ?? null,
    );
    const entryBlocked =
      entryReadinessState === 'blocked' ||
      entry?.recoveryBlocked === true ||
      entry?.recovery_blocked === true ||
      toStringOrNull(entry?.recoveryBlockedMarker ?? entry?.recovery_blocked_marker ?? null) !=
        null;
    const entryReadOnly = isMessengerV2ReadOnlyGroup(entry);
    const currentDeviceEnrollmentRequired = isMessengerV2CurrentDeviceEnrollmentRequired(entry, {
      clientId: this.identityClientId,
    });
    const serverReady =
      !entryReadOnly && !entryBlocked && (entryReadinessState === 'ready' || entry?.ready === true);
    const storedRuntimeHandle =
      toStringOrNull(entry?.runtimeHandle ?? entry?.runtime_handle ?? null) || null;
    const canonicalRuntimeHandle =
      toStringOrNull(
        serverReady && !entryReadOnly
          ? (entry?.groupHandle ?? entry?.group_handle ?? normalizedGroupId)
          : null,
      ) || null;
    const runtimeHandleCandidate =
      toStringOrNull(
        entryReadOnly
          ? null
          : (runtimeGroup?.groupHandle ??
              runtimeGroup?.groupId ??
              runtimeAssertion?.runtimeHandle ??
              canonicalRuntimeHandle ??
              null),
      ) || null;
    const runtimeDebug = runtimeHandleCandidate
      ? await Promise.resolve(
          debugGroupRuntimeState?.({
            groupHandle: runtimeHandleCandidate,
            includeExportKey: false,
            label: HISTORY_DERIVED_KEY_LABEL,
            context:
              toStringOrNull(entry?.conversationId ?? entry?.conversation_id ?? null) ||
              toStringOrNull(entry?.groupId ?? entry?.group_id ?? null),
            length: 1,
          }),
        ).catch(() => null)
      : null;
    const runtimeObserved = Boolean(runtimeGroup) || runtimeDebug?.found === true;
    const runtimeHandle = runtimeObserved ? runtimeHandleCandidate : null;
    const runtimeEpoch = runtimeObserved
      ? (toFiniteEpoch(runtimeDebug?.epoch ?? runtimeGroup?.epoch ?? runtimeAssertion?.epoch) ??
        null)
      : null;
    const epoch = toFiniteEpoch(entry?.epoch ?? entry?.current_epoch) ?? 1;
    const sessionGeneration = toFiniteEpoch(
      entry?.sessionGeneration ?? entry?.session_generation ?? null,
    );
    const runtimeSessionGeneration = toFiniteEpoch(
      entry?.runtimeSessionGeneration ?? entry?.runtime_session_generation ?? null,
    );
    const runtimeGenerationAligned =
      sessionGeneration == null ||
      runtimeSessionGeneration == null ||
      runtimeSessionGeneration === sessionGeneration;
    const canonicalRuntimeEpoch =
      runtimeObserved && runtimeEpoch == null
        ? (toFiniteEpoch(entry?.epoch ?? entry?.current_epoch) ?? null)
        : runtimeEpoch;
    const runtimeLive = Boolean(
      !entryBlocked &&
        !entryReadOnly &&
        !currentDeviceEnrollmentRequired &&
        runtimeObserved &&
        runtimeHandle &&
        canonicalRuntimeEpoch != null &&
        canonicalRuntimeEpoch === epoch &&
        runtimeGenerationAligned,
    );
    const ready = Boolean(
      !entryBlocked &&
        !entryReadOnly &&
        !currentDeviceEnrollmentRequired &&
        runtimeLive &&
        runtimeHandle &&
        canonicalRuntimeEpoch != null &&
        canonicalRuntimeEpoch === epoch &&
        runtimeGenerationAligned,
    );
    const readinessState = entryBlocked
      ? 'blocked'
      : entryReadOnly
        ? 'read_only'
        : ready
          ? 'ready'
          : entryReadinessState === 'pending_setup'
            ? 'pending_setup'
            : 'pending';
    const readinessReason = ready
      ? null
      : toStringOrNull(entry?.readinessReason ?? entry?.readiness_reason ?? null) ||
        (entryBlocked
          ? toStringOrNull(
              entry?.recoveryBlockedReason ?? entry?.recovery_blocked_reason ?? null,
            ) || 'recovery_blocked'
          : entryReadOnly
            ? 'former_member'
            : 'runtime_missing');
    const sendCapable = ready;
    const normalizedPendingWelcomes = entryReadOnly
      ? []
      : normalizePendingWelcomes(entry?.pendingWelcomes ?? entry?.pending_welcomes ?? []);
    const runtimeRecoveryClaim = ready
      ? null
      : normalizeRecoveryClaim(entry?.runtimeRecoveryClaim ?? null);

    const normalized = {
      ...entry,
      groupId: toStringOrNull(entry?.groupId ?? entry?.group_id ?? normalizedGroupId),
      group_id: toStringOrNull(entry?.groupId ?? entry?.group_id ?? normalizedGroupId),
      groupHandle:
        toStringOrNull(entry?.groupHandle ?? entry?.group_handle ?? runtimeHandle) ||
        toStringOrNull(entry?.groupId ?? entry?.group_id ?? normalizedGroupId),
      group_handle:
        toStringOrNull(entry?.groupHandle ?? entry?.group_handle ?? runtimeHandle) ||
        toStringOrNull(entry?.groupId ?? entry?.group_id ?? normalizedGroupId),
      epoch,
      current_epoch: epoch,
      runtimeHandle,
      runtime_handle: runtimeHandle,
      lastKnownRuntimeHandle: runtimeHandle || storedRuntimeHandle || null,
      last_known_runtime_handle: runtimeHandle || storedRuntimeHandle || null,
      runtimeEpoch: canonicalRuntimeEpoch,
      runtime_epoch: canonicalRuntimeEpoch,
      runtimeSessionGeneration:
        runtimeHandle && runtimeGenerationAligned
          ? (runtimeSessionGeneration ?? sessionGeneration)
          : null,
      runtime_session_generation:
        runtimeHandle && runtimeGenerationAligned
          ? (runtimeSessionGeneration ?? sessionGeneration)
          : null,
      runtimeLive,
      runtime_live: runtimeLive,
      pendingWelcomes: normalizedPendingWelcomes,
      pending_welcomes: normalizedPendingWelcomes,
      members: normalizeMembers(entry?.members),
      ready,
      readOnly: entryReadOnly,
      read_only: entryReadOnly,
      readyToSend: sendCapable,
      ready_to_send: sendCapable,
      sendCapable,
      send_capable: sendCapable,
      readinessState,
      readiness_state: readinessState,
      readinessReason,
      readiness_reason: readinessReason,
      runtimeState: entryBlocked
        ? 'blocked'
        : entryReadOnly
          ? 'read_only'
          : ready
            ? runtimeLive
              ? 'ready'
              : 'runtime_missing'
            : 'welcome_required',
      needsRecovery: entryReadOnly ? false : entryBlocked || !ready,
      recoveryReason: readinessReason,
      clientId: toStringOrNull(entry?.clientId ?? entry?.client_id ?? this.identityClientId),
      client_id: toStringOrNull(entry?.clientId ?? entry?.client_id ?? this.identityClientId),
      identityClientId: toStringOrNull(
        entry?.clientId ?? entry?.client_id ?? this.identityClientId,
      ),
      identity_client_id: toStringOrNull(
        entry?.clientId ?? entry?.client_id ?? this.identityClientId,
      ),
      recoveryBlocked:
        toStringOrNull(entry?.recoveryBlockedMarker ?? entry?.recovery_blocked_marker ?? null) !=
        null,
      recovery_blocked:
        toStringOrNull(entry?.recoveryBlockedMarker ?? entry?.recovery_blocked_marker ?? null) !=
        null,
      recoveryBlockedReason:
        toStringOrNull(entry?.recoveryBlockedReason ?? entry?.recovery_blocked_reason ?? null) ||
        null,
      recovery_blocked_reason:
        toStringOrNull(entry?.recoveryBlockedReason ?? entry?.recovery_blocked_reason ?? null) ||
        null,
      recoveryBlockedAt: toStringOrNull(
        entry?.recoveryBlockedAt ?? entry?.recovery_blocked_at ?? null,
      ),
      recovery_blocked_at: toStringOrNull(
        entry?.recoveryBlockedAt ?? entry?.recovery_blocked_at ?? null,
      ),
      runtimeRecoveryClaim,
      historyKeyB64: pickBase64(entry?.historyKeyB64, entry?.history_key_b64),
      history_key_b64: pickBase64(entry?.historyKeyB64, entry?.history_key_b64),
    };

    return normalized;
  }

  async _joinPendingWelcome({ groupId, identity, pendingWelcome }) {
    const welcomeId = toStringOrNull(pendingWelcome?.welcome_id ?? null);
    const joinResult = await joinGroup({
      identityId: identity.identityId,
      groupId,
      welcomeB64: pendingWelcome.welcome_b64,
      ratchetTreeB64: pendingWelcome.ratchet_tree_b64 ?? null,
      allowIdentityRestore: true,
      usedKeyPackageRef:
        toStringOrNull(
          pendingWelcome.used_kp_ref ?? pendingWelcome.usedKeyPackageRef ?? null,
        )?.toLowerCase() || null,
    });
    const runtimeHandle = toStringOrNull(
      joinResult?.groupHandle ?? joinResult?.group_handle ?? null,
    );
    const storageSnapshotB64 = pickBase64(
      joinResult?.storageSnapshotB64,
      joinResult?.storage_snapshot_b64,
    );
    if (!runtimeHandle || !storageSnapshotB64) {
      throw new Error('MLS Welcome joined without a durable runtime snapshot');
    }
    await this._persistProviderSnapshot(storageSnapshotB64);
    this._rememberLocalRuntimeAssertion({
      groupId,
      runtimeHandle,
      epoch: toFiniteEpoch(joinResult?.epoch ?? pendingWelcome?.epoch ?? null),
    });

    await this._updateGroup(groupId, (previous) => {
      const currentPending = normalizePendingWelcomes(
        previous?.pendingWelcomes ?? previous?.pending_welcomes ?? [],
      );
      const nextEpoch =
        Math.max(
          toFiniteEpoch(previous?.epoch ?? previous?.current_epoch) ?? 0,
          toFiniteEpoch(joinResult?.epoch ?? pendingWelcome?.epoch) ?? 0,
        ) || null;
      return {
        ...(previous || {}),
        groupId,
        group_id: groupId,
        runtimeHandle,
        runtime_handle: runtimeHandle,
        runtimeEpoch: toFiniteEpoch(joinResult?.epoch ?? null) ?? nextEpoch,
        runtime_epoch: toFiniteEpoch(joinResult?.epoch ?? null) ?? nextEpoch,
        runtimeSessionGeneration: toFiniteEpoch(
          previous?.sessionGeneration ?? previous?.session_generation ?? null,
        ),
        runtime_session_generation: toFiniteEpoch(
          previous?.sessionGeneration ?? previous?.session_generation ?? null,
        ),
        runtimeLive: true,
        runtime_live: true,
        epoch: nextEpoch,
        current_epoch: nextEpoch,
        ciphersuite: normalizeSuiteId(
          joinResult?.ciphersuite ?? previous?.ciphersuite ?? this.preferredCiphersuite,
        ),
        mlsGroupIdB64:
          pickBase64(
            joinResult?.mlsGroupIdB64,
            joinResult?.mls_group_id_b64,
            previous?.mlsGroupIdB64,
            previous?.mls_group_id_b64,
          ) || null,
        mls_group_id_b64:
          pickBase64(
            joinResult?.mlsGroupIdB64,
            joinResult?.mls_group_id_b64,
            previous?.mlsGroupIdB64,
            previous?.mls_group_id_b64,
          ) || null,
        pendingWelcomes: currentPending,
        pending_welcomes: currentPending,
        members: Array.from(new Set([...normalizeMembers(previous?.members), identity.clientId])),
        runtimeRecoveryClaim: normalizeRecoveryClaim(previous?.runtimeRecoveryClaim ?? null)
          ? {
              ...normalizeRecoveryClaim(previous.runtimeRecoveryClaim),
              phase: 'awaiting_ack',
              reason: null,
              updatedAt: new Date().toISOString(),
            }
          : null,
      };
    });

    const persistedGroups = await this._loadGroups({ refreshFromStorage: true });
    const persistedGroup = persistedGroups[toStringOrNull(groupId)];
    if (
      toStringOrNull(persistedGroup?.runtimeHandle ?? persistedGroup?.runtime_handle) !==
        runtimeHandle ||
      pickBase64(
        this.identityRecord?.storageSnapshotB64,
        this.identityRecord?.storage_snapshot_b64,
      ) !== storageSnapshotB64
    ) {
      throw new Error('MLS runtime snapshot was not persisted before Welcome acknowledgement');
    }

    if (welcomeId) {
      await this._ackPendingWelcomes({
        groupId,
        identity,
        pendingWelcomes: [pendingWelcome],
      });
    }

    await this._updateGroup(groupId, (previous) => {
      const nextPending = normalizePendingWelcomes(
        previous?.pendingWelcomes ?? previous?.pending_welcomes ?? [],
      ).filter(
        (entry) => toStringOrNull(entry.welcome_id ?? entry.welcomeId ?? null) !== welcomeId,
      );
      return {
        ...(previous || {}),
        pendingWelcomes: nextPending,
        pending_welcomes: nextPending,
        runtimeRecoveryClaim: null,
      };
    });

    // A Welcome consumes a one-time package. Refill outside this group's
    // recovery lock so another direct/group conversation can recover without
    // waiting for the next focus event or periodic inventory check.
    void this._replenishConsumedKeyPackage({ identity }).catch((error) => {
      if (typeof console?.warn === 'function') {
        console.warn('[MLS V2] key-package replenishment after Welcome failed', error);
      }
    });

    return joinResult;
  }

  async _restoreSharedRuntimeBeforeRecovery({
    groupId,
    conversationId = null,
    identity,
    info = null,
  } = {}) {
    const latest = (await this.groupInfo({ groupId }).catch(() => null)) || info;
    if (hasConcreteRuntime(latest) || isMessengerV2ReadOnlyGroup(latest)) {
      return latest;
    }

    return (
      (await this._restoreRuntimeGroup({
        groupId,
        conversationId,
        identity,
        info: latest,
      })) || latest
    );
  }

  _resolveTargetedRecoveryWelcome({ info = null, identity = null, keyPackage = null } = {}) {
    const pendingWelcomes = pickPendingWelcomesForIdentity(
      normalizePendingWelcomes(info?.pendingWelcomes ?? info?.pending_welcomes ?? []),
      identity,
    );
    const targetRef = normalizeHex(
      keyPackage?.keyPackageRef ?? keyPackage?.key_package_ref ?? null,
    );
    const exactPendingWelcome = targetRef
      ? pendingWelcomes.find((entry) => resolvePendingWelcomeTargetRef(entry) === targetRef) || null
      : null;

    // The server may replay the already-active Welcome when another tab races
    // with a newly generated package. Try that sole durable target first; if
    // its private package is unavailable the caller explicitly replaces it.
    return exactPendingWelcome || (pendingWelcomes.length === 1 ? pendingWelcomes[0] : null);
  }

  async _hasLocalPrivateKeyForWelcome({ identity, keyPackage, pendingWelcome } = {}) {
    const targetRef = resolvePendingWelcomeTargetRef(pendingWelcome);
    if (!targetRef) {
      return true;
    }

    const currentRef = normalizeHex(
      keyPackage?.keyPackageRef ?? keyPackage?.key_package_ref ?? null,
    );
    if (currentRef === targetRef) {
      return true;
    }

    const identityId = toStringOrNull(
      identity?.identityId ?? identity?.identity_id ?? this.identityId ?? null,
    );
    if (!identityId) {
      return false;
    }

    const identityState = await Promise.resolve(
      debugIdentityState?.({ identityId, includeKeyPackage: false }),
    ).catch(() => null);
    const retainedRefs = Array.isArray(identityState?.retainedKeyPackageRefs)
      ? identityState.retainedKeyPackageRefs
      : Array.isArray(identityState?.retained_key_package_refs)
        ? identityState.retained_key_package_refs
        : [];

    return retainedRefs.some((entry) => normalizeHex(entry) === targetRef);
  }

  async _completeTargetedRecoveryWelcome({
    groupId,
    identity,
    keyPackage,
    info = null,
    pendingWelcome: explicitPendingWelcome = null,
  } = {}) {
    let latest = (await this.groupInfo({ groupId }).catch(() => null)) || info;
    if (hasConcreteRuntime(latest)) {
      return { info: latest, joined: false, pendingWelcome: null };
    }

    const pendingWelcome =
      explicitPendingWelcome ||
      this._resolveTargetedRecoveryWelcome({
        info: latest,
        identity,
        keyPackage,
      });
    if (!pendingWelcome) {
      return { info: latest, joined: false, pendingWelcome: null };
    }

    if (hasObservedRuntimeState(latest)) {
      latest =
        (await this._invalidateLocalRuntime(groupId, {
          authoritativeEpoch: latest?.epoch ?? latest?.current_epoch ?? pendingWelcome?.epoch,
          authoritativeCiphersuite: latest?.ciphersuite ?? null,
          source: 'messages_v2_targeted_welcome_rejoin',
        })) || latest;
    }

    await this._joinPendingWelcome({
      groupId,
      identity,
      pendingWelcome,
    });
    return {
      info: (await this.groupInfo({ groupId }).catch(() => null)) || latest,
      joined: true,
      pendingWelcome,
    };
  }

  async _replenishConsumedKeyPackage({ identity = null } = {}) {
    if (this.keyPackageReplenishPromise) {
      return this.keyPackageReplenishPromise;
    }

    const resolvedIdentity = identity || (await this.ensureIdentity({}));
    const identityId = toStringOrNull(
      resolvedIdentity?.identityId ?? resolvedIdentity?.identity_id ?? this.identityId,
    );
    if (!identityId) {
      return null;
    }

    this.keyPackageReplenishPromise = Promise.resolve()
      .then(() =>
        this._withKeyPackageRecoveryLock(resolvedIdentity, async () => {
          const disposition = await this.getBootstrapPublishDisposition({
            identityId: resolvedIdentity?.identityId ?? resolvedIdentity?.identity_id ?? identityId,
            clientId:
              resolvedIdentity?.clientId ??
              resolvedIdentity?.client_id ??
              resolvedIdentity?.identityId ??
              identityId,
            ciphersuite: resolvedIdentity?.ciphersuite ?? this.preferredCiphersuite,
          });
          return this._ensureKeyPackageInventory({
            identity: resolvedIdentity,
            disposition,
            forcePublish: disposition?.requiresFreshPackage === true,
            fillTarget: true,
          });
        }),
      )
      .finally(() => {
        this.keyPackageReplenishPromise = null;
      });

    return this.keyPackageReplenishPromise;
  }

  async replenishKeyPackageInventory({ identity = null } = {}) {
    return this._replenishConsumedKeyPackage({ identity });
  }

  async _ackPendingWelcomes({ groupId, identity, pendingWelcomes = [] } = {}) {
    const normalizedGroupId = toStringOrNull(groupId);
    const welcomeIds = Array.from(
      new Set(
        (Array.isArray(pendingWelcomes) ? pendingWelcomes : [])
          .map((entry) =>
            toStringOrNull(entry?.welcome_id ?? entry?.welcomeId ?? entry?.id ?? null),
          )
          .filter(Boolean),
      ),
    );
    if (!normalizedGroupId || !welcomeIds.length) {
      return null;
    }

    const ackResult = await acknowledgeMlsWelcomes({
      groupId: normalizedGroupId,
      welcomeIds,
      clientId: toStringOrNull(identity?.clientId ?? identity?.identityId ?? null),
    });
    const groupState = ackResult?.group_state ?? ackResult?.groupState ?? null;
    if (groupState) {
      await this.applyServerSnapshot(groupState, {
        conversationId:
          toStringOrNull(groupState?.conversation_id ?? groupState?.conversationId ?? null) || null,
        source: 'messages_v2_welcome_ack',
      });
    }
    return ackResult;
  }

  async _materializeCreatorRuntime({ groupId, conversationId = null } = {}) {
    const normalizedGroupId = toStringOrNull(groupId);
    if (!normalizedGroupId) {
      return null;
    }

    const existing = await this.groupInfo({ groupId: normalizedGroupId });
    if (!shouldMaterializeCreatorRuntime(existing)) {
      return existing;
    }
    if (existing?.ready) {
      return existing;
    }

    const identity = await this.ensureIdentity({});
    const resolvedEpoch = toFiniteEpoch(existing?.epoch ?? existing?.current_epoch) ?? 1;

    const created = await createGroup({
      identityId: identity.identityId,
      groupId: normalizedGroupId,
      ciphersuite: normalizeSuiteId(existing?.ciphersuite ?? this.preferredCiphersuite),
    });
    await this._persistProviderSnapshot(
      pickBase64(created?.storageSnapshotB64, created?.storage_snapshot_b64),
    );
    this._rememberLocalRuntimeAssertion({
      groupId: normalizedGroupId,
      runtimeHandle: toStringOrNull(created?.groupHandle ?? created?.group_handle ?? null),
      epoch: toFiniteEpoch(created?.epoch ?? resolvedEpoch),
    });

    const updated = await this._updateGroup(normalizedGroupId, (previous) => {
      const nextRuntimeHandle =
        toStringOrNull(created?.groupHandle ?? created?.group_handle ?? null) ||
        toStringOrNull(previous?.runtimeHandle ?? previous?.runtime_handle ?? null) ||
        normalizedGroupId;
      const nextRuntimeEpoch =
        toFiniteEpoch(created?.epoch ?? null) ??
        toFiniteEpoch(previous?.runtimeEpoch ?? previous?.runtime_epoch) ??
        resolvedEpoch;
      const nextEpoch = Math.max(resolvedEpoch, nextRuntimeEpoch ?? 0) || resolvedEpoch;

      return {
        ...(previous || {}),
        groupId: normalizedGroupId,
        group_id: normalizedGroupId,
        conversationId:
          toStringOrNull(conversationId ?? previous?.conversationId ?? previous?.conversation_id) ||
          null,
        conversation_id:
          toStringOrNull(conversationId ?? previous?.conversationId ?? previous?.conversation_id) ||
          null,
        runtimeHandle: nextRuntimeHandle,
        runtime_handle: nextRuntimeHandle,
        runtimeEpoch: nextRuntimeEpoch,
        runtime_epoch: nextRuntimeEpoch,
        runtimeSessionGeneration: toFiniteEpoch(
          previous?.sessionGeneration ?? previous?.session_generation ?? null,
        ),
        runtime_session_generation: toFiniteEpoch(
          previous?.sessionGeneration ?? previous?.session_generation ?? null,
        ),
        runtimeLive: true,
        runtime_live: true,
        epoch: nextEpoch,
        current_epoch: nextEpoch,
        mlsGroupIdB64:
          pickBase64(
            created?.mlsGroupIdB64,
            created?.mls_group_id_b64,
            previous?.mlsGroupIdB64,
            previous?.mls_group_id_b64,
          ) || null,
        mls_group_id_b64:
          pickBase64(
            created?.mlsGroupIdB64,
            created?.mls_group_id_b64,
            previous?.mlsGroupIdB64,
            previous?.mls_group_id_b64,
          ) || null,
        members: Array.from(
          new Set(
            [
              ...normalizeMembers(previous?.members),
              toStringOrNull(identity.clientId ?? identity.identityId),
            ].filter(Boolean),
          ),
        ),
        bootstrapAuthority: 'creator',
        bootstrap_authority: 'creator',
        source: 'messages_v2_creator_runtime_materialized',
      };
    });

    const authoritative = asConcreteRuntimeInfo(updated, {
      runtimeHandle:
        toStringOrNull(created?.groupHandle ?? created?.group_handle ?? null) ||
        toStringOrNull(updated?.runtimeHandle ?? updated?.runtime_handle ?? null),
      runtimeEpoch: toFiniteEpoch(
        created?.epoch ?? updated?.runtimeEpoch ?? updated?.runtime_epoch,
      ),
    });
    const observed = await this.groupInfo({
      groupId: normalizedGroupId,
      groupHandle:
        toStringOrNull(created?.groupHandle ?? created?.group_handle ?? null) ||
        toStringOrNull(updated?.runtimeHandle ?? updated?.runtime_handle ?? null) ||
        toStringOrNull(updated?.groupHandle ?? updated?.group_handle ?? null),
    }).catch(() => null);
    return preferAuthoritativeRuntimeInfo(authoritative, observed) || authoritative;
  }

  async _requestJoinBundle({
    groupId,
    conversationId = null,
    identity,
    info = null,
    reissue = false,
    allowFreshKeyPackageRetry = true,
    allowRuntimeRestore = true,
    singleFlight = true,
    deviceLock = true,
    browserLock = true,
  } = {}) {
    const normalizedGroupId = toStringOrNull(groupId);
    if (!normalizedGroupId) {
      return info;
    }
    if (isMessengerV2ReadOnlyGroup(info)) {
      return info;
    }
    const existingRecoveryClaim = normalizeRecoveryClaim(info?.runtimeRecoveryClaim ?? null);
    const existingRecoveryBlocked =
      info?.recoveryBlocked === true ||
      info?.recovery_blocked === true ||
      existingRecoveryClaim?.phase === 'blocked';
    if (existingRecoveryBlocked) {
      return info;
    }

    if (singleFlight) {
      const recoveryKey = this._runtimeRecoveryKey({
        kind: 'join_bundle',
        groupId: normalizedGroupId,
        conversationId,
        identity,
        info,
        reissue,
      });
      return this._withRuntimeRecoverySingleFlight(recoveryKey, () =>
        this._requestJoinBundle({
          groupId: normalizedGroupId,
          conversationId,
          identity,
          info,
          reissue,
          allowFreshKeyPackageRetry,
          allowRuntimeRestore,
          singleFlight: false,
          deviceLock,
          browserLock,
        }),
      );
    }

    if (deviceLock) {
      return this._withKeyPackageRecoveryLock(identity, () =>
        this._requestJoinBundle({
          groupId: normalizedGroupId,
          conversationId,
          identity,
          info,
          reissue,
          allowFreshKeyPackageRetry,
          allowRuntimeRestore,
          singleFlight: false,
          deviceLock: false,
          browserLock,
        }),
      );
    }

    if (browserLock) {
      return this._withBrowserRuntimeRecoveryLock(
        {
          groupId: normalizedGroupId,
          identity,
          info,
        },
        () =>
          this._requestJoinBundle({
            groupId: normalizedGroupId,
            conversationId,
            identity,
            info,
            reissue,
            allowFreshKeyPackageRetry,
            allowRuntimeRestore,
            singleFlight: false,
            deviceLock: false,
            browserLock: false,
          }),
      );
    }

    const resolvedIdentity =
      identity ||
      (await this.ensureIdentity({
        ciphersuite: info?.ciphersuite ?? null,
      }));
    const restoredBeforeJoin =
      allowRuntimeRestore === false
        ? (await this.groupInfo({ groupId: normalizedGroupId }).catch(() => null)) || info
        : await this._restoreSharedRuntimeBeforeRecovery({
            groupId: normalizedGroupId,
            conversationId,
            identity: resolvedIdentity,
            info,
          });
    if (hasConcreteRuntime(restoredBeforeJoin) || isMessengerV2ReadOnlyGroup(restoredBeforeJoin)) {
      return restoredBeforeJoin;
    }
    const recoveryInfo = restoredBeforeJoin || info;

    const requestBundleWithKeyPackage = (keyPackage) =>
      getJoinBundle({
        groupId: normalizedGroupId,
        identityId: keyPackage.clientId ?? keyPackage.identityId,
        keyPackageB64: keyPackage.keyPackageB64,
        reissue,
      });

    let currentKeyPackage = await this._ensureJoinKeyPackage({
      identity: resolvedIdentity,
      ciphersuite: recoveryInfo?.ciphersuite ?? null,
    });
    if (!currentKeyPackage?.identityId || !currentKeyPackage?.keyPackageB64) {
      return recoveryInfo;
    }

    const recoveryClaim = this._buildRuntimeRecoveryClaim({
      groupId: normalizedGroupId,
      conversationId,
      identity: resolvedIdentity,
      keyPackage: currentKeyPackage,
      info: recoveryInfo,
    });
    const recoveryOwnership = await this._claimRuntimeRecovery({
      claim: recoveryClaim,
      operation: 'join_bundle',
      info: recoveryInfo,
    });
    if (!recoveryOwnership.claimed) {
      return (
        (await this.groupInfo({ groupId: normalizedGroupId }).catch(() => null)) || recoveryInfo
      );
    }

    const recoveryMarker = [
      normalizedGroupId,
      toStringOrNull(conversationId),
      toStringOrNull(currentKeyPackage.clientId ?? currentKeyPackage.identityId),
      toStringOrNull(currentKeyPackage.keyPackageRef ?? null) || 'no-kp-ref',
      toFiniteEpoch(recoveryInfo?.epoch ?? recoveryInfo?.current_epoch ?? null) ?? 'no-epoch',
    ].join(':');

    let bundle;
    try {
      bundle = await requestBundleWithKeyPackage(currentKeyPackage);
    } catch (error) {
      let recoveredWithFreshKeyPackage = false;
      if (allowFreshKeyPackageRetry && shouldRecoverFreshKeyPackage(error)) {
        currentKeyPackage = await this._refreshJoinKeyPackage({
          identity: resolvedIdentity,
          ciphersuite: recoveryInfo?.ciphersuite ?? null,
        });
        try {
          bundle = await requestBundleWithKeyPackage(currentKeyPackage);
          recoveredWithFreshKeyPackage = true;
        } catch (retryError) {
          if (shouldRecoverFreshKeyPackage(retryError)) {
            await this._clearRuntimeRecoveryBlocked(normalizedGroupId);
            await this._clearRuntimeRecoveryClaim(normalizedGroupId);
            void this._replenishConsumedKeyPackage({ identity: resolvedIdentity }).catch(
              () => null,
            );
            return (
              (await this.groupInfo({ groupId: normalizedGroupId }).catch(() => null)) ||
              recoveryInfo
            );
          }
          if (shouldBlockMessengerV2JoinBundleRecovery(retryError)) {
            await this._markRuntimeRecoveryBlocked(
              normalizedGroupId,
              recoveryMarker,
              extractMessengerV2ApiErrorCode(retryError) || 'welcome_unavailable',
            );
            return (
              (await this.groupInfo({ groupId: normalizedGroupId }).catch(() => null)) ||
              recoveryInfo
            );
          }
          throw retryError;
        }
      }

      if (!recoveredWithFreshKeyPackage) {
        if (shouldRecoverFreshKeyPackage(error)) {
          await this._clearRuntimeRecoveryBlocked(normalizedGroupId);
          await this._clearRuntimeRecoveryClaim(normalizedGroupId);
          void this._replenishConsumedKeyPackage({ identity: resolvedIdentity }).catch(() => null);
          return (
            (await this.groupInfo({ groupId: normalizedGroupId }).catch(() => null)) || recoveryInfo
          );
        }

        if (shouldBlockMessengerV2JoinBundleRecovery(error)) {
          await this._markRuntimeRecoveryBlocked(
            normalizedGroupId,
            recoveryMarker,
            extractMessengerV2ApiErrorCode(error) || 'welcome_unavailable',
          );
          return (
            (await this.groupInfo({ groupId: normalizedGroupId }).catch(() => null)) || recoveryInfo
          );
        }

        throw error;
      }
    }

    await this._clearRuntimeRecoveryBlocked(normalizedGroupId);

    if (bundle?.welcome_b64) {
      await this.applyServerSnapshot(
        {
          snapshot_generated_at:
            bundle?.snapshot_generated_at ?? bundle?.snapshotGeneratedAt ?? null,
          group_id: normalizedGroupId,
          conversation_id: conversationId,
          epoch: toFiniteEpoch(bundle?.epoch ?? recoveryInfo?.epoch ?? null),
          current_epoch: toFiniteEpoch(bundle?.epoch ?? recoveryInfo?.epoch ?? null),
          ciphersuite: normalizeSuiteId(
            bundle?.ciphersuite ?? recoveryInfo?.ciphersuite ?? this.preferredCiphersuite,
          ),
          history_key_b64: pickBase64(bundle?.history_key_b64, bundle?.historyKeyB64),
          ratchet_tree_b64: pickBase64(bundle?.ratchet_tree_b64, bundle?.ratchetTreeB64),
          pending_welcomes: [
            {
              welcome_id: toStringOrNull(bundle?.welcome_id ?? bundle?.welcomeId ?? null),
              group_id: normalizedGroupId,
              epoch: toFiniteEpoch(bundle?.epoch ?? recoveryInfo?.epoch ?? null),
              welcome_b64: bundle.welcome_b64,
              ratchet_tree_b64: pickBase64(bundle?.ratchet_tree_b64, bundle?.ratchetTreeB64),
              to_client_id: currentKeyPackage.clientId ?? currentKeyPackage.identityId,
              used_kp_ref: bundle?.used_kp_ref ?? currentKeyPackage.keyPackageRef ?? null,
            },
          ],
        },
        {
          conversationId,
          source: 'messages_v2_join_bundle',
        },
      );
    }

    let completedWelcome = await this._completeTargetedRecoveryWelcome({
      groupId: normalizedGroupId,
      identity: resolvedIdentity,
      keyPackage: currentKeyPackage,
      info: recoveryInfo,
    });

    if (conversationId && !completedWelcome.joined) {
      const snapshot = await this._fetchConversationSnapshot({
        conversationId,
        clientId: currentKeyPackage.clientId ?? currentKeyPackage.identityId,
      });
      if (snapshot) {
        await this.applyServerSnapshot(snapshot, {
          conversationId,
          source: 'messages_v2_join_bundle_snapshot',
        });
      }
      completedWelcome = await this._completeTargetedRecoveryWelcome({
        groupId: normalizedGroupId,
        identity: resolvedIdentity,
        keyPackage: currentKeyPackage,
        info: completedWelcome.info,
      });
    }

    if (!completedWelcome.joined) {
      await this._settleRuntimeRecoveryClaim(
        normalizedGroupId,
        bundle?.welcome_b64 ? 'awaiting_runtime' : 'awaiting_snapshot',
        bundle?.welcome_b64 ? null : 'welcome_not_returned',
      );
    }

    return (
      (await this.groupInfo({ groupId: normalizedGroupId }).catch(() => null)) ||
      completedWelcome.info ||
      recoveryInfo
    );
  }

  async _requestRuntimeRecoveryWelcome({
    groupId,
    conversationId = null,
    identity,
    info = null,
    failedWelcomeId = null,
    allowRuntimeRestore = true,
    singleFlight = true,
    deviceLock = true,
    browserLock = true,
  } = {}) {
    const normalizedGroupId = toStringOrNull(groupId);
    if (!normalizedGroupId) {
      return info;
    }
    if (isMessengerV2ReadOnlyGroup(info)) {
      return info;
    }
    const existingRecoveryClaim = normalizeRecoveryClaim(info?.runtimeRecoveryClaim ?? null);
    const existingRecoveryBlocked =
      info?.recoveryBlocked === true ||
      info?.recovery_blocked === true ||
      existingRecoveryClaim?.phase === 'blocked';
    if (existingRecoveryBlocked) {
      return info;
    }

    if (singleFlight) {
      const recoveryKey = this._runtimeRecoveryKey({
        kind: 'reissue_welcome',
        groupId: normalizedGroupId,
        conversationId,
        identity,
        info,
      });
      return this._withRuntimeRecoverySingleFlight(recoveryKey, () =>
        this._requestRuntimeRecoveryWelcome({
          groupId: normalizedGroupId,
          conversationId,
          identity,
          info,
          failedWelcomeId,
          allowRuntimeRestore,
          singleFlight: false,
          deviceLock,
          browserLock,
        }),
      );
    }

    if (deviceLock) {
      return this._withKeyPackageRecoveryLock(identity, () =>
        this._requestRuntimeRecoveryWelcome({
          groupId: normalizedGroupId,
          conversationId,
          identity,
          info,
          failedWelcomeId,
          allowRuntimeRestore,
          singleFlight: false,
          deviceLock: false,
          browserLock,
        }),
      );
    }

    if (browserLock) {
      return this._withBrowserRuntimeRecoveryLock(
        {
          groupId: normalizedGroupId,
          identity,
          info,
        },
        () =>
          this._requestRuntimeRecoveryWelcome({
            groupId: normalizedGroupId,
            conversationId,
            identity,
            info,
            failedWelcomeId,
            allowRuntimeRestore,
            singleFlight: false,
            deviceLock: false,
            browserLock: false,
          }),
      );
    }

    const resolvedIdentity =
      identity ||
      (await this.ensureIdentity({
        ciphersuite: info?.ciphersuite ?? null,
      }));
    const restoredBeforeRecovery =
      allowRuntimeRestore === false
        ? (await this.groupInfo({ groupId: normalizedGroupId }).catch(() => null)) || info
        : await this._restoreSharedRuntimeBeforeRecovery({
            groupId: normalizedGroupId,
            conversationId,
            identity: resolvedIdentity,
            info,
          });
    if (
      hasConcreteRuntime(restoredBeforeRecovery) ||
      isMessengerV2ReadOnlyGroup(restoredBeforeRecovery)
    ) {
      return restoredBeforeRecovery;
    }
    let recoveryInfo = restoredBeforeRecovery || info;
    if (conversationId) {
      const authoritativeSnapshot = await this._fetchConversationSnapshot({
        conversationId,
        clientId:
          resolvedIdentity?.clientId ??
          resolvedIdentity?.client_id ??
          resolvedIdentity?.identityId ??
          null,
        forceRefresh: true,
      });
      if (authoritativeSnapshot) {
        await this.applyServerSnapshot(authoritativeSnapshot, {
          conversationId,
          source: 'messages_v2_runtime_recovery_authority',
        });
        recoveryInfo =
          (await this.groupInfo({ groupId: normalizedGroupId }).catch(() => null)) || recoveryInfo;
      }
    }

    const claimKeyPackage = await this._resolveCurrentKeyPackage({
      identity: resolvedIdentity,
      ciphersuite: recoveryInfo?.ciphersuite ?? null,
    });
    const activePendingWelcome = this._resolveTargetedRecoveryWelcome({
      info: recoveryInfo,
      identity: resolvedIdentity,
      keyPackage: claimKeyPackage,
    });
    const activePendingWelcomeId = toStringOrNull(
      activePendingWelcome?.welcome_id ??
        activePendingWelcome?.welcomeId ??
        activePendingWelcome?.id ??
        null,
    );

    const recoveryClaim = this._buildRuntimeRecoveryClaim({
      groupId: normalizedGroupId,
      conversationId,
      identity: resolvedIdentity,
      keyPackage: claimKeyPackage,
      info: recoveryInfo,
    });
    let replacementWelcomeId = toStringOrNull(failedWelcomeId);
    const recoveryOwnership = await this._claimRuntimeRecovery({
      claim: recoveryClaim,
      operation: replacementWelcomeId
        ? `replace_failed_welcome:${replacementWelcomeId}`
        : activePendingWelcomeId
          ? `join_active_welcome:${activePendingWelcomeId}`
          : 'reissue_welcome',
      info: recoveryInfo,
    });
    if (!recoveryOwnership.claimed) {
      return (
        (await this.groupInfo({ groupId: normalizedGroupId }).catch(() => null)) || recoveryInfo
      );
    }

    if (activePendingWelcome && !replacementWelcomeId) {
      const hasLocalPrivateKey = await this._hasLocalPrivateKeyForWelcome({
        identity: resolvedIdentity,
        keyPackage: claimKeyPackage,
        pendingWelcome: activePendingWelcome,
      });

      if (hasLocalPrivateKey) {
        try {
          const completedActiveWelcome = await this._completeTargetedRecoveryWelcome({
            groupId: normalizedGroupId,
            identity: resolvedIdentity,
            keyPackage: claimKeyPackage,
            info: recoveryInfo,
            pendingWelcome: activePendingWelcome,
          });
          const completedInfo =
            (await this.groupInfo({ groupId: normalizedGroupId }).catch(() => null)) ||
            completedActiveWelcome.info ||
            recoveryInfo;
          if (completedActiveWelcome.joined || hasConcreteRuntime(completedInfo)) {
            await this._clearRuntimeRecoveryBlocked(normalizedGroupId);
            return completedInfo;
          }
        } catch {
          // The exact durable Welcome remains the replacement authority below.
          // Capturing its id before invalidating runtime state prevents a failed
          // join from losing the only safe server-side replacement target.
        }
      }

      replacementWelcomeId = activePendingWelcomeId;
      if (!replacementWelcomeId) {
        await this._markRuntimeRecoveryBlocked(
          normalizedGroupId,
          recoveryClaim.key,
          'pending_welcome_id_missing',
        );
        return (
          (await this.groupInfo({ groupId: normalizedGroupId }).catch(() => null)) || recoveryInfo
        );
      }

      const replacementOwnership = await this._claimRuntimeRecovery({
        claim: recoveryClaim,
        operation: `replace_failed_welcome:${replacementWelcomeId}`,
        info: recoveryInfo,
      });
      const replacementAlreadyOwned = Boolean(
        replacementOwnership.claim?.key === recoveryClaim.key &&
          replacementOwnership.claim?.attemptedOperations?.includes(
            `replace_failed_welcome:${replacementWelcomeId}`,
          ),
      );
      if (!replacementOwnership.claimed && !replacementAlreadyOwned) {
        return (
          (await this.groupInfo({ groupId: normalizedGroupId }).catch(() => null)) || recoveryInfo
        );
      }
    }

    // A recovery owner rotates once. Other tabs/processes observe the persisted
    // claim and return before consuming or publishing another one-time package.
    // Existing Welcomes are attempted with their original retained private key
    // before this point; rotation is reserved for an exact replacement request.
    const currentKeyPackage = await this._refreshJoinKeyPackage({
      identity: resolvedIdentity,
      ciphersuite: recoveryInfo?.ciphersuite ?? null,
    });
    if (!currentKeyPackage?.identityId || !currentKeyPackage?.keyPackageB64) {
      await this._settleRuntimeRecoveryClaim(
        normalizedGroupId,
        'awaiting_snapshot',
        'key_package_unavailable',
      );
      return (
        (await this.groupInfo({ groupId: normalizedGroupId }).catch(() => null)) || recoveryInfo
      );
    }

    const recoveryMarker = [
      normalizedGroupId,
      toStringOrNull(conversationId),
      toStringOrNull(currentKeyPackage.clientId ?? currentKeyPackage.identityId),
      toFiniteEpoch(recoveryInfo?.epoch ?? recoveryInfo?.current_epoch ?? null) ?? 'no-epoch',
      entryContainsIdentityMember(recoveryInfo, resolvedIdentity) ? 'member' : 'nonmember',
    ].join(':');
    const applyRecoveryPayload = async (
      payload = null,
      keyPackage = currentKeyPackage,
      source = 'messages_v2_runtime_recovery_welcome',
    ) => {
      if (!payload || typeof payload !== 'object') {
        return null;
      }
      if (payload.recovery_blocked === true || payload.recoveryBlocked === true) {
        await this._markRuntimeRecoveryBlocked(
          normalizedGroupId,
          toStringOrNull(payload.recovery_blocked_marker ?? payload.recoveryBlockedMarker) ||
            recoveryMarker,
          toStringOrNull(payload.recovery_blocked_reason ?? payload.recoveryBlockedReason) ||
            'reissue_welcome_unavailable',
        );
        return this.groupInfo({ groupId: normalizedGroupId }).catch(() => null);
      }

      const groupState = payload.group_state ?? payload.groupState ?? null;
      if (groupState && typeof groupState === 'object') {
        await this.applyServerSnapshot(groupState, {
          conversationId:
            toStringOrNull(groupState?.conversation_id ?? groupState?.conversationId ?? null) ||
            conversationId,
          source,
        });
      }

      const pendingWelcomes = normalizeRecoveryPayloadWelcomes({
        payload,
        groupId: normalizedGroupId,
        fallbackEpoch: recoveryInfo?.epoch ?? recoveryInfo?.current_epoch ?? null,
        keyPackage,
      });
      if (!pendingWelcomes.length) {
        return this.groupInfo({ groupId: normalizedGroupId }).catch(() => null);
      }

      await this.applyServerSnapshot(
        {
          snapshot_generated_at:
            payload.snapshot_generated_at ?? payload.snapshotGeneratedAt ?? null,
          group_id: normalizedGroupId,
          conversation_id: conversationId,
          epoch: toFiniteEpoch(
            payload.epoch ?? recoveryInfo?.epoch ?? recoveryInfo?.current_epoch ?? null,
          ),
          current_epoch: toFiniteEpoch(
            payload.epoch ?? recoveryInfo?.epoch ?? recoveryInfo?.current_epoch ?? null,
          ),
          ciphersuite: normalizeSuiteId(
            payload.ciphersuite ?? recoveryInfo?.ciphersuite ?? this.preferredCiphersuite,
          ),
          history_key_b64: pickBase64(
            payload.history_key_b64,
            payload.historyKeyB64,
            recoveryInfo?.historyKeyB64,
            recoveryInfo?.history_key_b64,
          ),
          ratchet_tree_b64: pickBase64(
            payload.ratchet_tree_b64,
            payload.ratchetTreeB64,
            recoveryInfo?.ratchetTreeB64,
            recoveryInfo?.ratchet_tree_b64,
          ),
          pending_welcomes: pendingWelcomes,
        },
        {
          conversationId,
          source,
        },
      );
      return this.groupInfo({ groupId: normalizedGroupId }).catch(() => null);
    };

    let appliedRecovery = null;
    let recoveryDeliveredPendingWelcome = false;
    let deliveredPendingWelcome = null;

    try {
      const reissueResult = await Promise.resolve(
        reissueMlsWelcomes({
          groupId: normalizedGroupId,
          identityId: currentKeyPackage.clientId ?? currentKeyPackage.identityId,
          keyPackageB64: currentKeyPackage.keyPackageB64,
          ...(replacementWelcomeId ? { replaceWelcomeId: replacementWelcomeId } : {}),
        }),
      );
      const deliveredWelcomes = normalizeRecoveryPayloadWelcomes({
        payload: reissueResult,
        groupId: normalizedGroupId,
        fallbackEpoch: recoveryInfo?.epoch ?? recoveryInfo?.current_epoch ?? null,
        keyPackage: currentKeyPackage,
      });
      deliveredPendingWelcome = this._resolveTargetedRecoveryWelcome({
        info: {
          pendingWelcomes: deliveredWelcomes,
        },
        identity: resolvedIdentity,
        keyPackage: currentKeyPackage,
      });
      if (replacementWelcomeId) {
        const reportedAction = toStringOrNull(
          reissueResult?.recovery_action ?? reissueResult?.recoveryAction ?? null,
        );
        const reportedReplacedWelcomeId = toStringOrNull(
          reissueResult?.replaced_welcome_id ?? reissueResult?.replacedWelcomeId ?? null,
        );
        const deliveredWelcomeId = toStringOrNull(
          deliveredPendingWelcome?.welcome_id ??
            deliveredPendingWelcome?.welcomeId ??
            deliveredPendingWelcome?.id ??
            null,
        );
        const deliveredKeyPackageRef = normalizeHex(
          deliveredPendingWelcome?.used_kp_ref ?? deliveredPendingWelcome?.usedKpRef ?? null,
        );
        const expectedKeyPackageRef = normalizeHex(currentKeyPackage.keyPackageRef ?? null);
        const replacementContractValid = Boolean(
          deliveredWelcomeId &&
            deliveredWelcomeId !== replacementWelcomeId &&
            deliveredKeyPackageRef &&
            expectedKeyPackageRef &&
            deliveredKeyPackageRef === expectedKeyPackageRef &&
            (!reportedAction || reportedAction === 'welcome_replaced') &&
            (!reportedReplacedWelcomeId || reportedReplacedWelcomeId === replacementWelcomeId),
        );
        if (!replacementContractValid) {
          const error = new Error('MLS Welcome replacement contract mismatch');
          error.code = 'MLS_WELCOME_REPLACEMENT_CONTRACT_MISMATCH';
          throw error;
        }
      }
      appliedRecovery = await applyRecoveryPayload(reissueResult, currentKeyPackage);
      if (appliedRecovery?.recoveryBlocked === true || appliedRecovery?.recovery_blocked === true) {
        return appliedRecovery;
      }
      recoveryDeliveredPendingWelcome =
        Boolean(deliveredPendingWelcome) ||
        hasPendingWelcomesForIdentity(appliedRecovery, resolvedIdentity);
      deliveredPendingWelcome =
        deliveredPendingWelcome ||
        this._resolveTargetedRecoveryWelcome({
          info: appliedRecovery,
          identity: resolvedIdentity,
          keyPackage: currentKeyPackage,
        });
    } catch (error) {
      if (shouldRecoverFreshKeyPackage(error)) {
        await this._clearRuntimeRecoveryBlocked(normalizedGroupId);
        await this._clearRuntimeRecoveryClaim(normalizedGroupId);
        void this._replenishConsumedKeyPackage({ identity: resolvedIdentity }).catch(() => null);
        return (
          (await this.groupInfo({ groupId: normalizedGroupId }).catch(() => null)) || recoveryInfo
        );
      }
      if (Number(error?.status) === 422 || shouldBlockRuntimeRecoveryWelcome(error)) {
        await this._markRuntimeRecoveryBlocked(
          normalizedGroupId,
          recoveryMarker,
          'reissue_welcome_unavailable',
        );
        return (
          (await this.groupInfo({ groupId: normalizedGroupId }).catch(() => null)) || recoveryInfo
        );
      }
      throw error;
    }

    let completedWelcome;
    try {
      completedWelcome = await this._completeTargetedRecoveryWelcome({
        groupId: normalizedGroupId,
        identity: resolvedIdentity,
        keyPackage: currentKeyPackage,
        info: appliedRecovery || recoveryInfo,
        pendingWelcome: deliveredPendingWelcome,
      });

      if (conversationId && !recoveryDeliveredPendingWelcome && !completedWelcome.joined) {
        const snapshot = await this._fetchConversationSnapshot({
          conversationId,
          clientId: currentKeyPackage.clientId ?? currentKeyPackage.identityId,
          forceRefresh: true,
        });
        if (snapshot) {
          await this.applyServerSnapshot(snapshot, {
            conversationId,
            source: 'messages_v2_runtime_recovery_welcome',
          });
        }
        const refreshedInfo =
          (await this.groupInfo({ groupId: normalizedGroupId }).catch(() => null)) ||
          completedWelcome.info;
        deliveredPendingWelcome = this._resolveTargetedRecoveryWelcome({
          info: refreshedInfo,
          identity: resolvedIdentity,
          keyPackage: currentKeyPackage,
        });
        completedWelcome = await this._completeTargetedRecoveryWelcome({
          groupId: normalizedGroupId,
          identity: resolvedIdentity,
          keyPackage: currentKeyPackage,
          info: refreshedInfo,
          pendingWelcome: deliveredPendingWelcome,
        });
      }
    } catch (error) {
      const failedInfo =
        (await this.groupInfo({ groupId: normalizedGroupId }).catch(() => null)) ||
        appliedRecovery ||
        recoveryInfo;
      const failedTarget =
        deliveredPendingWelcome ||
        this._resolveTargetedRecoveryWelcome({
          info: appliedRecovery || failedInfo,
          identity: resolvedIdentity,
          keyPackage: currentKeyPackage,
        });
      const failedTargetId = toStringOrNull(
        failedTarget?.welcome_id ?? failedTarget?.welcomeId ?? failedTarget?.id ?? null,
      );
      if (!replacementWelcomeId && failedTargetId) {
        return this._requestRuntimeRecoveryWelcome({
          groupId: normalizedGroupId,
          conversationId,
          identity: resolvedIdentity,
          info: failedInfo,
          failedWelcomeId: failedTargetId,
          allowRuntimeRestore: false,
          singleFlight: false,
          deviceLock: false,
          browserLock: false,
        });
      }
      if (replacementWelcomeId && failedTargetId) {
        const failedReplacementClaim = this._buildRuntimeRecoveryClaim({
          groupId: normalizedGroupId,
          conversationId,
          identity: resolvedIdentity,
          keyPackage: currentKeyPackage,
          info: failedInfo,
        });
        await this._claimRuntimeRecovery({
          claim: failedReplacementClaim,
          operation: `replace_failed_welcome:${failedTargetId}`,
          info: failedInfo,
        });
      }
      await this._markRuntimeRecoveryBlocked(
        normalizedGroupId,
        recoveryMarker,
        replacementWelcomeId ? 'replacement_welcome_join_failed' : 'welcome_join_failed',
      );
      throw error;
    }

    const finalInfo =
      (await this.groupInfo({ groupId: normalizedGroupId }).catch(() => null)) ||
      completedWelcome.info ||
      recoveryInfo;
    if (completedWelcome.joined || hasConcreteRuntime(finalInfo)) {
      await this._clearRuntimeRecoveryBlocked(normalizedGroupId);
    } else {
      await this._settleRuntimeRecoveryClaim(
        normalizedGroupId,
        recoveryDeliveredPendingWelcome ? 'awaiting_runtime' : 'awaiting_snapshot',
        recoveryDeliveredPendingWelcome ? null : 'welcome_not_returned',
      );
    }

    return finalInfo;
  }

  async _convergeLocalRuntime({
    groupId,
    conversationId = null,
    identity,
    info = null,
    allowRecovery = true,
    allowPendingWelcomeJoin = allowRecovery !== false,
    allowRuntimeRestore = true,
    recoveryLockHeld = false,
    keyPackageLockHeld = false,
  } = {}) {
    const normalizedGroupId = toStringOrNull(groupId);
    if (!normalizedGroupId) {
      return info;
    }

    let latest = info;
    let lastError = null;
    if (isMessengerV2ReadOnlyGroup(latest)) {
      return latest;
    }

    latest = (await this._discardNonAuthoritativeRuntime(normalizedGroupId, latest)) || latest;

    if (
      shouldMaterializeCreatorRuntime(latest) &&
      !hasConcreteRuntime(latest) &&
      !hasObservedRuntimeState(latest)
    ) {
      try {
        latest =
          (await this._materializeCreatorRuntime({
            groupId: normalizedGroupId,
            conversationId,
          })) || latest;
      } catch (error) {
        lastError = error;
      }
    }

    if (
      allowRuntimeRestore !== false &&
      !hasConcreteRuntime(latest) &&
      !hasObservedRuntimeState(latest) &&
      !shouldMaterializeCreatorRuntime(latest)
    ) {
      try {
        latest =
          (await this._restoreRuntimeGroup({
            groupId: normalizedGroupId,
            conversationId,
            identity,
            info: latest,
          })) || latest;
      } catch (error) {
        lastError = lastError || error;
      }
    }

    latest = (await this._discardNonAuthoritativeRuntime(normalizedGroupId, latest)) || latest;

    let activePendingWelcomes = pickPendingWelcomesForIdentity(
      normalizePendingWelcomes(latest?.pendingWelcomes ?? latest?.pending_welcomes ?? []),
      identity,
    );
    let currentIdentityMember = entryContainsIdentityMember(latest, identity);
    if (
      allowRecovery !== false &&
      !shouldMaterializeCreatorRuntime(latest) &&
      !hasConcreteRuntime(latest) &&
      !hasObservedRuntimeState(latest)
    ) {
      try {
        const currentKeyPackage = currentIdentityMember
          ? null
          : await this._resolveCurrentKeyPackage({
              identity,
              ciphersuite: latest?.ciphersuite ?? null,
            });
        const needsJoinBundle =
          !currentIdentityMember &&
          (!activePendingWelcomes.length ||
            (activePendingWelcomes.length > 1 &&
              !hasMatchingPendingWelcomeTarget(
                activePendingWelcomes,
                currentKeyPackage?.keyPackageRef ?? null,
              )));

        if (currentIdentityMember && !activePendingWelcomes.length) {
          latest =
            (await this._requestRuntimeRecoveryWelcome({
              groupId: normalizedGroupId,
              conversationId,
              identity,
              info: latest,
              allowRuntimeRestore,
              singleFlight: recoveryLockHeld !== true,
              deviceLock: keyPackageLockHeld !== true,
              browserLock: recoveryLockHeld !== true,
            })) || latest;

          activePendingWelcomes = pickPendingWelcomesForIdentity(
            normalizePendingWelcomes(latest?.pendingWelcomes ?? latest?.pending_welcomes ?? []),
            identity,
          );
          currentIdentityMember = entryContainsIdentityMember(latest, identity);
        } else if (needsJoinBundle) {
          latest =
            (await this._requestJoinBundle({
              groupId: normalizedGroupId,
              conversationId,
              identity,
              info: latest,
              reissue: activePendingWelcomes.length > 0,
              allowRuntimeRestore,
              singleFlight: recoveryLockHeld !== true,
              deviceLock: keyPackageLockHeld !== true,
              browserLock: recoveryLockHeld !== true,
            })) || latest;
          activePendingWelcomes = pickPendingWelcomesForIdentity(
            normalizePendingWelcomes(latest?.pendingWelcomes ?? latest?.pending_welcomes ?? []),
            identity,
          );
        }
      } catch (error) {
        lastError = lastError || error;
      }
    }

    if (
      allowPendingWelcomeJoin !== false &&
      !hasConcreteRuntime(latest) &&
      !hasObservedRuntimeState(latest) &&
      activePendingWelcomes.length
    ) {
      try {
        await this._joinPendingWelcome({
          groupId: normalizedGroupId,
          identity,
          pendingWelcome: activePendingWelcomes[0],
        });
        latest = (await this.groupInfo({ groupId: normalizedGroupId }).catch(() => null)) || latest;
      } catch (error) {
        lastError = lastError || error;
        if (allowRecovery !== false) {
          try {
            latest = entryContainsIdentityMember(latest, identity)
              ? (await this._requestRuntimeRecoveryWelcome({
                  groupId: normalizedGroupId,
                  conversationId,
                  identity,
                  info: latest,
                  allowRuntimeRestore,
                  failedWelcomeId: toStringOrNull(
                    activePendingWelcomes[0]?.welcome_id ??
                      activePendingWelcomes[0]?.welcomeId ??
                      activePendingWelcomes[0]?.id ??
                      null,
                  ),
                  singleFlight: recoveryLockHeld !== true,
                  deviceLock: keyPackageLockHeld !== true,
                  browserLock: recoveryLockHeld !== true,
                })) || latest
              : (await this._requestJoinBundle({
                  groupId: normalizedGroupId,
                  conversationId,
                  identity,
                  info: latest,
                  reissue: true,
                  allowRuntimeRestore,
                  singleFlight: recoveryLockHeld !== true,
                  deviceLock: keyPackageLockHeld !== true,
                  browserLock: recoveryLockHeld !== true,
                })) || latest;
            activePendingWelcomes = pickPendingWelcomesForIdentity(
              normalizePendingWelcomes(latest?.pendingWelcomes ?? latest?.pending_welcomes ?? []),
              identity,
            );
            if (!hasConcreteRuntime(latest) && activePendingWelcomes.length) {
              await this._joinPendingWelcome({
                groupId: normalizedGroupId,
                identity,
                pendingWelcome: activePendingWelcomes[0],
              });
              latest =
                (await this.groupInfo({ groupId: normalizedGroupId }).catch(() => null)) || latest;
              lastError = null;
            }
          } catch (retryError) {
            lastError = lastError || retryError;
          }
        }
      }
    }

    activePendingWelcomes = pickPendingWelcomesForIdentity(
      normalizePendingWelcomes(latest?.pendingWelcomes ?? latest?.pending_welcomes ?? []),
      identity,
    );

    if (
      allowPendingWelcomeJoin !== false &&
      hasConcreteRuntime(latest) &&
      activePendingWelcomes.length
    ) {
      await this._ackPendingWelcomes({
        groupId: normalizedGroupId,
        identity,
        pendingWelcomes: activePendingWelcomes,
      });
      latest = preferAuthoritativeRuntimeInfo(
        latest,
        await this.groupInfo({ groupId: normalizedGroupId }).catch(() => null),
      );
    }

    if (!hasConcreteRuntime(latest) && lastError) {
      throw lastError;
    }

    return latest;
  }

  async _replayMissingHandshakeFrames({
    groupId,
    conversationId = null,
    identity,
    info = null,
    allowRecovery = true,
  } = {}) {
    const normalizedGroupId = toStringOrNull(groupId);
    const normalizedConversationId = toStringOrNull(conversationId);
    const clientId = toStringOrNull(
      identity?.clientId ??
        identity?.client_id ??
        identity?.identityId ??
        identity?.identity_id ??
        this.identityClientId ??
        null,
    );
    if (!normalizedGroupId || !clientId) {
      return info;
    }

    let latest = info;
    let pageCount = 0;
    while (pageCount < MAX_HANDSHAKE_REPLAY_PAGES) {
      const runtimeEpoch = toFiniteEpoch(latest?.runtimeEpoch ?? latest?.runtime_epoch ?? null);
      const authoritativeEpoch = toFiniteEpoch(latest?.epoch ?? latest?.current_epoch ?? null);
      if (
        runtimeEpoch == null ||
        runtimeEpoch < 1 ||
        authoritativeEpoch == null ||
        runtimeEpoch >= authoritativeEpoch
      ) {
        return latest;
      }

      const response = await fetchMlsMessages({
        groupId: normalizedGroupId,
        clientId,
        handshakeAfterEpoch: runtimeEpoch,
      });
      const recoveryDirective = response?.handshake_recovery ?? response?.handshakeRecovery ?? null;
      if (recoveryDirective?.required === true) {
        if (allowRecovery === false) {
          return latest;
        }
        return this.recoverAuthoritativeRuntime({
          groupId: normalizedGroupId,
          conversationId: normalizedConversationId,
          authoritativeEpoch:
            recoveryDirective?.authoritative_epoch ??
            recoveryDirective?.authoritativeEpoch ??
            authoritativeEpoch,
          authoritativeCiphersuite: latest?.ciphersuite ?? null,
          reason: toStringOrNull(recoveryDirective?.reason) || 'handshake_replay_recovery_required',
        });
      }

      const records = Array.isArray(response?.items) ? response.items : [];
      if (!records.length) {
        // The authority snapshot can briefly arrive before its commit is visible
        // in the pending feed. Keep the observed runtime and let the next
        // convergence retry instead of discarding valid local MLS state.
        return latest;
      }

      const replayed = await this.ingestServerMessages({
        groupId: normalizedGroupId,
        conversationId: normalizedConversationId,
        records,
        skipRuntimeConvergence: true,
      });
      latest =
        preferAuthoritativeRuntimeInfo(
          replayed || latest,
          await this.groupInfo({ groupId: normalizedGroupId }).catch(() => null),
        ) ||
        replayed ||
        latest;
      const nextRuntimeEpoch = toFiniteEpoch(latest?.runtimeEpoch ?? latest?.runtime_epoch ?? null);
      if (nextRuntimeEpoch == null || nextRuntimeEpoch <= runtimeEpoch) {
        if (allowRecovery === false) {
          return latest;
        }
        return this.recoverAuthoritativeRuntime({
          groupId: normalizedGroupId,
          conversationId: normalizedConversationId,
          authoritativeEpoch,
          authoritativeCiphersuite: latest?.ciphersuite ?? null,
          reason: 'handshake_replay_no_progress',
        });
      }
      pageCount += 1;
    }

    if (allowRecovery === false) {
      return latest;
    }
    return this.recoverAuthoritativeRuntime({
      groupId: normalizedGroupId,
      conversationId: normalizedConversationId,
      authoritativeEpoch: latest?.epoch ?? latest?.current_epoch ?? null,
      authoritativeCiphersuite: latest?.ciphersuite ?? null,
      reason: 'handshake_replay_page_limit',
    });
  }

  async recoverAuthoritativeRuntime({
    groupId,
    conversationId = null,
    authoritativeEpoch = null,
    authoritativeCiphersuite = null,
    reason = 'mls_authoritative_state_conflict',
    forceLocalRebuild = false,
  } = {}) {
    const normalizedGroupId = toStringOrNull(groupId);
    const normalizedConversationId = toStringOrNull(conversationId);
    if (!normalizedGroupId) {
      return null;
    }

    const identity = await this.ensureIdentity({
      ciphersuite: authoritativeCiphersuite ?? null,
    });
    const recoveryKey = this._runtimeRecoveryKey({
      kind: 'authoritative_conflict',
      groupId: normalizedGroupId,
      identity,
    });

    return this._withRuntimeRecoverySingleFlight(recoveryKey, () =>
      this._withKeyPackageRecoveryLock(identity, () =>
        this._withBrowserRuntimeRecoveryLock(
          {
            groupId: normalizedGroupId,
            identity,
            info: null,
          },
          async () => {
            const current = await this.groupInfo({ groupId: normalizedGroupId }).catch(() => null);
            const currentRuntimeEpoch = toFiniteEpoch(
              current?.runtimeEpoch ?? current?.runtime_epoch ?? null,
            );
            const currentAuthoritativeEpoch = toFiniteEpoch(
              current?.epoch ?? current?.current_epoch ?? null,
            );
            const normalizedAuthoritativeEpoch = toFiniteEpoch(authoritativeEpoch);
            if (
              forceLocalRebuild !== true &&
              hasConcreteRuntime(current) &&
              currentRuntimeEpoch != null &&
              currentAuthoritativeEpoch != null &&
              currentRuntimeEpoch === currentAuthoritativeEpoch &&
              (normalizedAuthoritativeEpoch == null ||
                currentAuthoritativeEpoch === normalizedAuthoritativeEpoch)
            ) {
              return current;
            }

            let latest = await this._invalidateLocalRuntime(normalizedGroupId, {
              authoritativeEpoch,
              authoritativeCiphersuite,
              clearRecoveryClaim: true,
              source: `messages_v2_${toStringOrNull(reason) || 'authoritative_state_conflict'}`,
            });
            await this._clearRuntimeRecoveryBlocked(normalizedGroupId);
            latest =
              (await this.groupInfo({ groupId: normalizedGroupId }).catch(() => null)) || latest;
            if (isMessengerV2ReadOnlyGroup(latest)) {
              return latest;
            }

            return this._convergeLocalRuntime({
              groupId: normalizedGroupId,
              conversationId: normalizedConversationId,
              identity,
              info: latest,
              allowRecovery: true,
              allowPendingWelcomeJoin: true,
              allowRuntimeRestore: false,
              // Web Locks are not re-entrant. This authoritative recovery owns
              // both the device KeyPackage lock and group recovery lock.
              recoveryLockHeld: true,
              keyPackageLockHeld: true,
            });
          },
        ),
      ),
    );
  }

  async _convergeGroupStateInternal({
    groupId = null,
    conversationId = null,
    preferServerSnapshot = false,
    allowRecovery = true,
    allowPendingWelcomeJoin = allowRecovery !== false,
  } = {}) {
    const normalizedConversationId = toStringOrNull(conversationId);
    let normalizedGroupId = toStringOrNull(groupId);
    let info = null;

    if (normalizedGroupId) {
      info = await this.groupInfo({ groupId: normalizedGroupId });
    }

    if (!info && normalizedConversationId) {
      const groups = await this._loadGroups();
      const existing = Object.values(groups).find(
        (entry) =>
          toStringOrNull(entry?.conversationId ?? entry?.conversation_id ?? null) ===
          normalizedConversationId,
      );
      normalizedGroupId =
        normalizedGroupId || toStringOrNull(existing?.groupId ?? existing?.group_id ?? null);
      if (normalizedGroupId) {
        info = await this.groupInfo({ groupId: normalizedGroupId });
      }
    }

    if (
      normalizedGroupId &&
      info &&
      (info?.recoveryBlocked === true || info?.recovery_blocked === true) &&
      (isRetryableKeyPackageRecoveryState(info) ||
        isMessengerV2CurrentDeviceEnrollmentRequired(info, {
          clientId: this.identityClientId,
        }))
    ) {
      // Older clients persisted a terminal block when a one-time package was
      // temporarily exhausted. Package inventory is a retryable precondition,
      // so migrate that local state and let the authoritative snapshot drive a
      // new bounded recovery attempt.
      await this._clearRuntimeRecoveryBlocked(normalizedGroupId);
      await this._clearRuntimeRecoveryClaim(normalizedGroupId);
      info = await this.groupInfo({ groupId: normalizedGroupId });
    }

    const infoReadinessState = normalizeReadinessState(
      info?.readinessState ?? info?.readiness_state ?? null,
    );
    const infoBlocked =
      info?.recoveryBlocked === true ||
      info?.recovery_blocked === true ||
      infoReadinessState === 'blocked';
    const shouldRefreshServerSnapshot =
      normalizedConversationId &&
      (preferServerSnapshot === true ||
        !info ||
        (allowRecovery !== false &&
          info &&
          !infoBlocked &&
          !isMessengerV2ReadOnlyGroup(info) &&
          !shouldMaterializeCreatorRuntime(info) &&
          !hasConcreteRuntime(info)));

    if (shouldRefreshServerSnapshot) {
      const ensured = await this.ensureGroupForConversation({
        conversationId: normalizedConversationId,
        groupId: normalizedGroupId,
      });
      normalizedGroupId = toStringOrNull(
        normalizedGroupId ?? ensured?.groupId ?? ensured?.group_id ?? null,
      );
      info =
        ensured ||
        (normalizedGroupId ? await this.groupInfo({ groupId: normalizedGroupId }) : null);
    }

    if (!normalizedGroupId) {
      return null;
    }
    if (isMessengerV2ReadOnlyGroup(info)) {
      return info;
    }
    const identity = await this.ensureIdentity({});
    const readinessState = normalizeReadinessState(
      info?.readinessState ?? info?.readiness_state ?? null,
    );
    if (
      info &&
      (info?.recoveryBlocked === true ||
        info?.recovery_blocked === true ||
        readinessState === 'blocked')
    ) {
      return info;
    }

    const observedRuntimeEpoch = toFiniteEpoch(info?.runtimeEpoch ?? info?.runtime_epoch ?? null);
    const authoritativeEpoch = toFiniteEpoch(info?.epoch ?? info?.current_epoch ?? null);
    if (
      hasObservedRuntimeState(info) &&
      observedRuntimeEpoch != null &&
      authoritativeEpoch != null &&
      observedRuntimeEpoch < authoritativeEpoch
    ) {
      info =
        (await this._replayMissingHandshakeFrames({
          groupId: normalizedGroupId,
          conversationId: normalizedConversationId,
          identity,
          info,
          allowRecovery,
        })) || info;
    }
    if (hasConcreteRuntime(info) && !hasPendingWelcomesForIdentity(info, identity)) {
      return info;
    }

    info = await this._convergeLocalRuntime({
      groupId: normalizedGroupId,
      conversationId: normalizedConversationId,
      identity,
      info,
      allowRecovery,
      allowPendingWelcomeJoin,
    });

    return (
      preferAuthoritativeRuntimeInfo(
        info,
        await this.groupInfo({ groupId: normalizedGroupId }).catch(() => null),
      ) || info
    );
  }

  async convergeGroupState({
    groupId = null,
    conversationId = null,
    preferServerSnapshot = false,
    allowRecovery = true,
    allowPendingWelcomeJoin = allowRecovery !== false,
  } = {}) {
    const normalizedGroupId = toStringOrNull(groupId);
    const normalizedConversationId = toStringOrNull(conversationId);
    const baseConvergeKey =
      normalizedGroupId || `conversation:${normalizedConversationId || 'unknown'}`;
    const recoveryScope =
      allowRecovery === false
        ? allowPendingWelcomeJoin === false
          ? 'restore-only'
          : 'pending-welcome'
        : 'recovery';
    const snapshotScope = preferServerSnapshot === true ? 'server-snapshot' : 'local-first';
    const convergeKey = `${baseConvergeKey}:${recoveryScope}:${snapshotScope}`;
    if (this.groupConvergePromises.has(convergeKey)) {
      return this.groupConvergePromises.get(convergeKey);
    }

    const authorityKey = baseConvergeKey;
    const previousTail = this.groupConvergeTails.get(authorityKey) || Promise.resolve();
    const promise = previousTail
      .catch(() => null)
      .then(() =>
        this._convergeGroupStateInternal({
          groupId: normalizedGroupId,
          conversationId: normalizedConversationId,
          preferServerSnapshot,
          allowRecovery,
          allowPendingWelcomeJoin,
        }),
      )
      .finally(() => {
        if (this.groupConvergePromises.get(convergeKey) === promise) {
          this.groupConvergePromises.delete(convergeKey);
        }
        if (this.groupConvergeTails.get(authorityKey) === promise) {
          this.groupConvergeTails.delete(authorityKey);
        }
      });

    this.groupConvergePromises.set(convergeKey, promise);
    this.groupConvergeTails.set(authorityKey, promise);
    return promise;
  }

  async waitForGroupReady({
    groupId = null,
    groupHandle = null,
    conversationId = null,
    timeoutMs = 5000,
    allowRecovery = false,
    allowPendingWelcomeJoin = true,
    maxRecoveryAttempts = allowRecovery === false ? 0 : 3,
  } = {}) {
    const normalizedGroupId = toStringOrNull(groupId);
    const normalizedGroupHandle = toStringOrNull(groupHandle);
    const normalizedConversationId = toStringOrNull(conversationId);
    const waitKey =
      normalizedGroupId ||
      normalizedGroupHandle ||
      (normalizedConversationId ? `conversation:${normalizedConversationId}` : null);
    if (!waitKey) {
      return null;
    }

    const inFlight = this.groupReadyWaitPromises.get(waitKey);
    if (inFlight) {
      return inFlight;
    }

    const promise = (async () => {
      const deadline = Date.now() + Math.max(0, Number(timeoutMs) || 0);
      let latest =
        (await this.groupInfo({
          groupId: normalizedGroupId,
          groupHandle: normalizedGroupHandle,
        }).catch(() => null)) || null;
      if (isMessengerV2ReadOnlyGroup(latest)) {
        return latest;
      }
      let recoveryAttempts = 0;

      while (Date.now() <= deadline) {
        const latestReadinessState = normalizeReadinessState(
          latest?.readinessState ?? latest?.readiness_state ?? null,
        );
        if (
          latest?.recoveryBlocked === true ||
          latest?.recovery_blocked === true ||
          latestReadinessState === 'blocked' ||
          latestReadinessState === 'read_only' ||
          isMessengerV2ReadOnlyGroup(latest)
        ) {
          return latest;
        }

        if (isSettledRecoveryClaim(latest?.runtimeRecoveryClaim ?? null)) {
          return latest;
        }

        if (hasMessengerV2ConcreteRuntime(latest)) {
          return latest;
        }

        if (typeof this.convergeGroupState === 'function') {
          const canAttemptRecovery =
            allowRecovery !== false &&
            recoveryAttempts < Math.max(0, Number(maxRecoveryAttempts) || 0);
          if (canAttemptRecovery) {
            recoveryAttempts += 1;
          }
          const converged =
            (await Promise.resolve(
              this.convergeGroupState({
                groupId: normalizedGroupId ?? latest?.groupId ?? latest?.group_id ?? null,
                conversationId:
                  normalizedConversationId ??
                  latest?.conversationId ??
                  latest?.conversation_id ??
                  null,
                allowRecovery: canAttemptRecovery,
                allowPendingWelcomeJoin,
              }),
            ).catch(() => null)) || null;
          if (converged) {
            latest = converged;
          }
        }

        if (hasMessengerV2ConcreteRuntime(latest)) {
          return latest;
        }

        latest =
          (await this.groupInfo({
            groupId: normalizedGroupId ?? latest?.groupId ?? latest?.group_id ?? null,
            groupHandle:
              normalizedGroupHandle ?? latest?.groupHandle ?? latest?.group_handle ?? null,
          }).catch(() => null)) || latest;

        if (hasMessengerV2ConcreteRuntime(latest)) {
          return latest;
        }

        if (Date.now() >= deadline) {
          break;
        }

        await delay(250);
      }

      return latest;
    })().finally(() => {
      if (this.groupReadyWaitPromises.get(waitKey) === promise) {
        this.groupReadyWaitPromises.delete(waitKey);
      }
    });

    this.groupReadyWaitPromises.set(waitKey, promise);
    return promise;
  }

  async ingestServerMessages({
    groupId = null,
    conversationId = null,
    records = [],
    skipRuntimeConvergence = false,
  } = {}) {
    const normalizedGroupId = toStringOrNull(groupId);
    if (!normalizedGroupId || !Array.isArray(records) || records.length === 0) {
      return null;
    }

    let orderedHandshakeRecords = records
      .filter((record) => isHandshakeServerRecord(record))
      .map((record) => ({
        record,
        serverSeq: toFiniteServerSeq(record?.server_seq ?? record?.serverSeq ?? null) ?? 0,
        epoch: toFiniteEpoch(record?.epoch ?? record?.current_epoch ?? null),
      }))
      .sort((left, right) => {
        if (left.epoch != null && right.epoch != null && left.epoch !== right.epoch) {
          return left.epoch - right.epoch;
        }
        return left.serverSeq - right.serverSeq;
      });

    if (!orderedHandshakeRecords.length) {
      return this.groupInfo({ groupId: normalizedGroupId }).catch(() => null);
    }

    const resolveInfo = async () =>
      (await Promise.resolve(this.groupInfo({ groupId: normalizedGroupId })).catch(() => null)) ||
      null;

    let info = await resolveInfo();
    if (!hasConcreteRuntime(info) && skipRuntimeConvergence !== true) {
      info =
        (await Promise.resolve(
          this.convergeGroupState({
            groupId: normalizedGroupId,
            conversationId,
            allowRecovery: false,
          }),
        ).catch(() => null)) || info;
      info = preferAuthoritativeRuntimeInfo(info, await resolveInfo()) || info;
    }
    const activeSessionGeneration = toFiniteEpoch(
      info?.sessionGeneration ?? info?.session_generation ?? null,
    );
    orderedHandshakeRecords = orderedHandshakeRecords.filter(({ record }) => {
      const generation = toFiniteEpoch(
        record?.session_generation ?? record?.sessionGeneration ?? null,
      );
      return (
        activeSessionGeneration == null ||
        generation == null ||
        generation === activeSessionGeneration
      );
    });
    if (!orderedHandshakeRecords.length) {
      return info;
    }

    let runtimeHandle =
      toStringOrNull(info?.runtimeHandle ?? info?.runtime_handle ?? info?.groupHandle ?? null) ||
      null;
    const storedIngestHandle =
      toStringOrNull(info?.ingestRuntimeHandle ?? info?.ingest_runtime_handle ?? null) || null;
    let lastIngestedServerSeq =
      toFiniteServerSeq(
        info?.ingestHandshakeServerSeq ?? info?.ingest_handshake_server_seq ?? null,
      ) ?? 0;
    const authoritativeEpoch = toFiniteEpoch(info?.epoch ?? info?.current_epoch ?? null);
    const runtimeEpoch = toFiniteEpoch(info?.runtimeEpoch ?? info?.runtime_epoch ?? null);
    let replayRuntimeEpoch = runtimeEpoch;
    const recoverHandshakeRuntime = (reason, latestInfo = info) =>
      this.recoverAuthoritativeRuntime({
        groupId: normalizedGroupId,
        conversationId,
        authoritativeEpoch:
          toFiniteEpoch(latestInfo?.epoch ?? latestInfo?.current_epoch ?? null) ??
          authoritativeEpoch,
        authoritativeCiphersuite: latestInfo?.ciphersuite ?? info?.ciphersuite ?? null,
        reason,
      });

    if (runtimeHandle && storedIngestHandle && storedIngestHandle !== runtimeHandle) {
      lastIngestedServerSeq = 0;
    }
    if (
      runtimeHandle &&
      runtimeEpoch != null &&
      authoritativeEpoch != null &&
      runtimeEpoch < authoritativeEpoch
    ) {
      lastIngestedServerSeq = 0;
    }

    for (const { record, serverSeq } of orderedHandshakeRecords) {
      const incomingEpoch = toFiniteEpoch(record?.epoch ?? record?.current_epoch ?? null);
      if (
        isCommitServerRecord(record) &&
        replayRuntimeEpoch != null &&
        incomingEpoch != null &&
        incomingEpoch > replayRuntimeEpoch + 1
      ) {
        return recoverHandshakeRuntime('handshake_history_gap');
      }

      const ciphertext = pickBase64(
        record?.ciphertext_b64,
        record?.ciphertextB64,
        record?.envelope_b64,
        record?.envelopeB64,
      );
      if (!ciphertext) {
        continue;
      }
      const advancesRuntime =
        incomingEpoch != null && replayRuntimeEpoch != null && incomingEpoch > replayRuntimeEpoch;
      if (serverSeq > 0 && lastIngestedServerSeq >= serverSeq && !advancesRuntime) {
        continue;
      }

      try {
        await this.decrypt({
          groupId: normalizedGroupId,
          conversationId,
          ciphertext,
          epochHint: record?.epoch ?? record?.current_epoch ?? null,
          strict: true,
          skipRuntimeConvergence: true,
        });
        info = preferAuthoritativeRuntimeInfo(info, await resolveInfo()) || info;
      } catch (error) {
        const latestInfo = preferAuthoritativeRuntimeInfo(info, await resolveInfo()) || info;
        const localEpoch =
          toFiniteEpoch(latestInfo?.runtimeEpoch ?? latestInfo?.runtime_epoch ?? null) ?? null;
        const failedIncomingEpoch = incomingEpoch ?? null;

        if (
          !isRuntimeNotReadyError(error) &&
          isBenignReplayDecryptError({
            error,
            incomingEpoch: failedIncomingEpoch,
            localEpoch,
          })
        ) {
          info = latestInfo;
        } else {
          return recoverHandshakeRuntime('handshake_replay_apply_failed', latestInfo);
        }
      }

      info = preferAuthoritativeRuntimeInfo(info, await resolveInfo()) || info;
      const observedRuntimeEpoch =
        toFiniteEpoch(info?.runtimeEpoch ?? info?.runtime_epoch ?? null) ?? null;
      if (
        isCommitServerRecord(record) &&
        incomingEpoch != null &&
        (observedRuntimeEpoch == null || observedRuntimeEpoch < incomingEpoch)
      ) {
        return recoverHandshakeRuntime('handshake_replay_apply_failed', info);
      }
      if (observedRuntimeEpoch != null) {
        replayRuntimeEpoch = observedRuntimeEpoch;
      }

      runtimeHandle =
        toStringOrNull(info?.runtimeHandle ?? info?.runtime_handle ?? info?.groupHandle ?? null) ||
        runtimeHandle ||
        null;
      lastIngestedServerSeq =
        serverSeq > 0 ? Math.max(lastIngestedServerSeq, serverSeq) : lastIngestedServerSeq;
      const ingestServerSeq = lastIngestedServerSeq || null;
      const ingestRuntimeHandle = runtimeHandle;

      await this._updateGroup(normalizedGroupId, (previous) => ({
        ...(previous || {}),
        ingestHandshakeServerSeq: ingestServerSeq,
        ingest_handshake_server_seq: ingestServerSeq,
        ingestRuntimeHandle,
        ingest_runtime_handle: ingestRuntimeHandle,
      }));
    }

    return preferAuthoritativeRuntimeInfo(info, await resolveInfo()) || info;
  }

  async ingestPendingEntries({ groupId = null, conversationId = null, items = [] } = {}) {
    const normalizedGroupId = toStringOrNull(groupId);
    if (!normalizedGroupId || !Array.isArray(items) || items.length === 0) {
      return this.groupInfo({ groupId: normalizedGroupId }).catch(() => null);
    }

    const currentInfo = await this.groupInfo({ groupId: normalizedGroupId }).catch(() => null);
    const currentSessionGeneration =
      toFiniteEpoch(currentInfo?.sessionGeneration ?? currentInfo?.session_generation ?? null) ?? 1;
    const normalizedPendingWelcomes = items
      .filter((item) => {
        if (!item || typeof item !== 'object') return false;
        const entryType = toStringOrNull(item?.type)?.toLowerCase() || null;
        return entryType === 'welcome' || Boolean(pickBase64(item?.welcome_b64, item?.welcomeB64));
      })
      .map((item) => ({
        welcome_id: toStringOrNull(item?.welcome_id ?? item?.welcomeId ?? item?.id ?? null),
        group_id:
          toStringOrNull(item?.group_id ?? item?.groupId ?? normalizedGroupId) || normalizedGroupId,
        epoch: toFiniteEpoch(item?.epoch ?? item?.current_epoch ?? null),
        session_generation: toFiniteEpoch(
          item?.session_generation ?? item?.sessionGeneration ?? null,
        ),
        welcome_b64: pickBase64(item?.welcome_b64, item?.welcomeB64),
        ratchet_tree_b64: pickBase64(item?.ratchet_tree_b64, item?.ratchetTreeB64),
        used_kp_ref: normalizeHex(item?.used_kp_ref ?? item?.usedKpRef ?? null),
        to_user_id: toStringOrNull(item?.to_user_id ?? item?.toUserId ?? null),
        to_client_id: toStringOrNull(item?.to_client_id ?? item?.toClientId ?? null),
      }))
      .filter((item) => item.welcome_b64);
    const newestIncomingGeneration = normalizedPendingWelcomes.reduce((latest, welcome) => {
      const generation = toFiniteEpoch(welcome?.session_generation);
      return generation == null ? latest : Math.max(latest, generation);
    }, currentSessionGeneration);
    const pendingWelcomes = normalizedPendingWelcomes.filter((welcome) => {
      const generation = toFiniteEpoch(welcome?.session_generation);
      return generation == null || generation === newestIncomingGeneration;
    });

    if (pendingWelcomes.length) {
      const highestWelcomeEpoch = pendingWelcomes.reduce((highest, welcome) => {
        const epoch = toFiniteEpoch(welcome?.epoch);
        if (epoch == null) return highest;
        return Math.max(highest, epoch);
      }, 0);

      await this.applyServerSnapshot(
        {
          group_id: normalizedGroupId,
          conversation_id: toStringOrNull(conversationId),
          epoch: highestWelcomeEpoch || null,
          current_epoch: highestWelcomeEpoch || null,
          session_generation: newestIncomingGeneration,
          pending_welcomes: pendingWelcomes,
        },
        {
          conversationId,
          source: 'messages_v2_pending_feed',
        },
      );

      // The pending feed is delivery-only and intentionally does not carry
      // authoritative membership. Do not let its short-lived snapshot cache
      // hide the full conversation snapshot needed to choose exact member
      // Welcome replacement instead of the non-member join-bundle path.
      const normalizedConversationId = toStringOrNull(conversationId);
      if (normalizedConversationId) {
        const identity = await this.ensureIdentity({});
        const authoritativeSnapshot = await this._fetchConversationSnapshot({
          conversationId: normalizedConversationId,
          clientId: identity.clientId,
          forceRefresh: true,
        });
        if (authoritativeSnapshot) {
          await this.applyServerSnapshot(authoritativeSnapshot, {
            conversationId: normalizedConversationId,
            source: 'messages_v2_pending_feed_authority',
          });
        }
      }

      await this.convergeGroupState({
        groupId: normalizedGroupId,
        conversationId,
        preferServerSnapshot: Boolean(toStringOrNull(conversationId)),
        allowRecovery: true,
        allowPendingWelcomeJoin: true,
      });
    }

    const messageRecords = items.filter((item) => {
      const entryType = toStringOrNull(item?.type)?.toLowerCase() || null;
      return entryType !== 'welcome';
    });

    if (!messageRecords.length) {
      return this.groupInfo({ groupId: normalizedGroupId }).catch(() => null);
    }

    return this.ingestServerMessages({
      groupId: normalizedGroupId,
      conversationId,
      records: messageRecords,
    });
  }

  async exportHistoryKey({
    conversationId = null,
    groupId = null,
    groupHandle = null,
    epoch = null,
    label = HISTORY_DERIVED_KEY_LABEL,
    length = HISTORY_DERIVED_KEY_LENGTH,
  } = {}) {
    const info =
      (await Promise.resolve(
        this.groupInfo({
          groupId,
          groupHandle,
        }),
      ).catch(() => null)) || null;
    const stableHistoryKeyB64 = pickBase64(info?.historyKeyB64, info?.history_key_b64);
    if (stableHistoryKeyB64) {
      return stableHistoryKeyB64;
    }
    const runtimeHandle = toStringOrNull(info?.runtimeHandle ?? info?.groupHandle ?? groupHandle);
    if (!runtimeHandle) {
      return null;
    }
    const resolvedEpoch = toFiniteEpoch(epoch ?? info?.epoch ?? null);
    if (resolvedEpoch == null) {
      return null;
    }
    const exported = await Promise.resolve(
      exportGroupKey?.({
        groupHandle: runtimeHandle,
        label,
        context: toStringOrNull(conversationId) || toStringOrNull(groupId),
        length,
      }),
    ).catch(() => null);
    return pickBase64(exported?.key, exported?.keyB64);
  }

  async decrypt({
    groupId = null,
    groupHandle = null,
    conversationId = null,
    ciphertext,
    epochHint = null,
    strict = false,
    skipRuntimeConvergence = false,
  } = {}) {
    const normalizedCiphertext = pickBase64(ciphertext);
    if (!normalizedCiphertext) {
      if (strict) {
        const error = new Error('MLS ciphertext is required');
        error.code = 'MLS_CIPHERTEXT_REQUIRED';
        throw error;
      }
      return null;
    }

    const resolveInfo = async (candidateGroupId = groupId, candidateHandle = groupHandle) =>
      (await Promise.resolve(
        this.groupInfo({
          groupId: candidateGroupId,
          groupHandle: candidateHandle,
        }),
      ).catch(() => null)) || null;

    let info = await resolveInfo();
    const normalizedGroupId = toStringOrNull(groupId ?? info?.groupId ?? info?.group_id ?? null);
    const normalizedConversationId =
      toStringOrNull(conversationId ?? info?.conversationId ?? info?.conversation_id ?? null) ||
      null;

    const convergeRuntime = async (reason) => {
      if (!normalizedGroupId) {
        return info;
      }
      const converged =
        (await Promise.resolve(
          this.convergeGroupState({
            groupId: normalizedGroupId,
            conversationId: normalizedConversationId,
            reason,
            allowRecovery: false,
          }),
        ).catch(() => null)) || null;
      const observed = await resolveInfo(normalizedGroupId, groupHandle);
      return preferAuthoritativeRuntimeInfo(converged, observed) || converged || observed || info;
    };

    if (!hasConcreteRuntime(info) && normalizedGroupId && skipRuntimeConvergence !== true) {
      info = await convergeRuntime('messages_v2_timeline_decrypt_preflight');
    }

    let runtimeHandle =
      toStringOrNull(info?.runtimeHandle ?? info?.runtime_handle ?? groupHandle) || null;
    if (!runtimeHandle) {
      if (strict) {
        const error = new Error('MLS runtime handle is unavailable');
        error.code = 'MLS_RUNTIME_UNAVAILABLE';
        throw error;
      }
      return null;
    }

    let result = null;
    try {
      result = await decryptMessage({
        groupHandle: runtimeHandle,
        ciphertext: normalizedCiphertext,
        epochHint,
      });
    } catch (error) {
      const message = String(error?.message || '').toLowerCase();
      if (
        message.includes('unknown group') &&
        normalizedGroupId &&
        skipRuntimeConvergence !== true
      ) {
        await this._invalidateLocalRuntime(normalizedGroupId, {
          authoritativeEpoch: info?.epoch ?? info?.current_epoch ?? null,
          authoritativeCiphersuite: info?.ciphersuite ?? null,
          source: 'messages_v2_unknown_runtime_group_invalidated',
        });
        info = await convergeRuntime('messages_v2_timeline_decrypt_runtime_repair');
        runtimeHandle =
          toStringOrNull(info?.runtimeHandle ?? info?.runtime_handle ?? groupHandle) || null;
        if (!runtimeHandle) {
          if (strict) {
            throw error;
          }
          return null;
        }
        try {
          result = await decryptMessage({
            groupHandle: runtimeHandle,
            ciphertext: normalizedCiphertext,
            epochHint,
          });
        } catch (retryError) {
          if (strict) {
            throw retryError;
          }
          return null;
        }
      } else {
        if (strict) {
          throw error;
        }
        return null;
      }
    }

    if (!result || typeof result !== 'object') {
      if (strict) {
        const error = new Error('MLS decrypt returned no runtime result');
        error.code = 'MLS_DECRYPT_RESULT_INVALID';
        throw error;
      }
      return null;
    }

    const text =
      toStringOrNull(result?.text ?? null) || decodeUtf8Base64(result?.plaintext ?? null) || null;
    const runtimeEpoch = toFiniteEpoch(result?.epoch ?? info?.epoch ?? info?.current_epoch ?? null);

    if (normalizedGroupId && runtimeHandle && runtimeEpoch != null) {
      this._rememberLocalRuntimeAssertion({
        groupId: normalizedGroupId,
        runtimeHandle,
        epoch: runtimeEpoch,
      });
      const storageSnapshotB64 = pickBase64(
        result?.storageSnapshotB64,
        result?.storage_snapshot_b64,
      );
      await this._persistProviderSnapshot(storageSnapshotB64);
      const mlsGroupIdB64 = pickBase64(result?.mlsGroupIdB64, result?.mls_group_id_b64);
      await this._updateGroup(normalizedGroupId, (previous) => ({
        ...(previous || {}),
        runtimeHandle,
        runtime_handle: runtimeHandle,
        runtimeEpoch,
        runtime_epoch: runtimeEpoch,
        runtimeLive: true,
        runtime_live: true,
        epoch: Math.max(
          toFiniteEpoch(previous?.epoch ?? previous?.current_epoch ?? null) ?? 0,
          runtimeEpoch,
        ),
        current_epoch: Math.max(
          toFiniteEpoch(previous?.epoch ?? previous?.current_epoch ?? null) ?? 0,
          runtimeEpoch,
        ),
        mlsGroupIdB64:
          mlsGroupIdB64 || pickBase64(previous?.mlsGroupIdB64, previous?.mls_group_id_b64) || null,
        mls_group_id_b64:
          mlsGroupIdB64 || pickBase64(previous?.mlsGroupIdB64, previous?.mls_group_id_b64) || null,
      }));
    }

    if (!text) {
      return null;
    }

    return {
      text,
      plaintext: pickBase64(result?.plaintext),
      epoch: runtimeEpoch,
      runtimeHandle,
      groupId: normalizedGroupId,
    };
  }

  async encrypt({ groupId = null, groupHandle = null, identityId = null, plaintext } = {}) {
    const normalizedGroupId = toStringOrNull(groupId);
    const normalizedGroupHandle = toStringOrNull(groupHandle);
    let info =
      (await Promise.resolve(
        this.groupInfo({
          groupId: normalizedGroupId,
          groupHandle: normalizedGroupHandle,
        }),
      ).catch(() => null)) || null;
    if (
      info &&
      !hasMessengerV2ConcreteRuntime(info) &&
      typeof this.convergeGroupState === 'function'
    ) {
      const converged =
        (await Promise.resolve(
          this.convergeGroupState({
            groupId: normalizedGroupId ?? info?.groupId ?? null,
            conversationId: info?.conversationId ?? info?.conversation_id ?? null,
          }),
        ).catch(() => null)) || null;
      if (converged) {
        info = converged;
      } else if (normalizedGroupId) {
        info =
          (await Promise.resolve(
            this.groupInfo({
              groupId: normalizedGroupId,
            }),
          ).catch(() => null)) || info;
      }
    }

    if (
      info &&
      !hasMessengerV2ConcreteRuntime(info) &&
      typeof this.waitForGroupReady === 'function'
    ) {
      const waited =
        (await Promise.resolve(
          this.waitForGroupReady({
            groupId: normalizedGroupId ?? info?.groupId ?? info?.group_id ?? null,
            groupHandle:
              normalizedGroupHandle ??
              info?.groupHandle ??
              info?.group_handle ??
              info?.runtimeHandle ??
              info?.runtime_handle ??
              null,
            conversationId: info?.conversationId ?? info?.conversation_id ?? null,
            timeoutMs: 5000,
          }),
        ).catch(() => null)) || null;
      if (waited) {
        info = waited;
      }
    }

    const runtimeHandle = hasMessengerV2ConcreteRuntime(info)
      ? toStringOrNull(info?.runtimeHandle ?? info?.runtime_handle ?? info?.groupHandle ?? null)
      : null;
    const identity = await this.ensureIdentity({});
    if (!runtimeHandle) {
      throw new Error('V2 runtime handle unavailable for encrypt');
    }
    const encrypted = await encryptMessage({
      groupHandle: runtimeHandle,
      identityId: toStringOrNull(identityId ?? identity.identityId),
      plaintext,
    });
    const runtimeEpoch = toFiniteEpoch(encrypted?.epoch ?? info?.epoch ?? info?.current_epoch);
    const storageSnapshotB64 = pickBase64(
      encrypted?.storageSnapshotB64,
      encrypted?.storage_snapshot_b64,
    );
    await this._persistProviderSnapshot(storageSnapshotB64);
    const mlsGroupIdB64 = pickBase64(encrypted?.mlsGroupIdB64, encrypted?.mls_group_id_b64);
    if (normalizedGroupId && runtimeEpoch != null) {
      this._rememberLocalRuntimeAssertion({
        groupId: normalizedGroupId,
        runtimeHandle,
        epoch: runtimeEpoch,
      });
      await this._updateGroup(normalizedGroupId, (previous) => ({
        ...(previous || {}),
        runtimeHandle,
        runtime_handle: runtimeHandle,
        runtimeEpoch,
        runtime_epoch: runtimeEpoch,
        runtimeLive: true,
        runtime_live: true,
        epoch: Math.max(
          toFiniteEpoch(previous?.epoch ?? previous?.current_epoch ?? null) ?? 0,
          runtimeEpoch,
        ),
        current_epoch: Math.max(
          toFiniteEpoch(previous?.epoch ?? previous?.current_epoch ?? null) ?? 0,
          runtimeEpoch,
        ),
        mlsGroupIdB64:
          mlsGroupIdB64 || pickBase64(previous?.mlsGroupIdB64, previous?.mls_group_id_b64) || null,
        mls_group_id_b64:
          mlsGroupIdB64 || pickBase64(previous?.mlsGroupIdB64, previous?.mls_group_id_b64) || null,
      }));
    }
    return encrypted;
  }

  async rememberSuccessfulSendRuntime({
    groupId = null,
    groupHandle = null,
    epoch = null,
    ciphersuite = null,
  } = {}) {
    const normalizedGroupId = toStringOrNull(groupId);
    if (!normalizedGroupId) {
      return null;
    }
    const runtimeEpoch = toFiniteEpoch(epoch);
    if (runtimeEpoch == null) {
      return this.groupInfo({ groupId: normalizedGroupId, groupHandle }).catch(() => null);
    }

    const latest =
      (await this.groupInfo({
        groupId: normalizedGroupId,
        groupHandle,
      }).catch(() => null)) || null;
    const runtimeHandle =
      toStringOrNull(
        latest?.runtimeHandle ??
          latest?.runtime_handle ??
          latest?.groupHandle ??
          latest?.group_handle ??
          groupHandle,
      ) || null;
    if (!runtimeHandle) {
      return latest;
    }

    this._rememberLocalRuntimeAssertion({
      groupId: normalizedGroupId,
      runtimeHandle,
      epoch: runtimeEpoch,
    });

    return this._updateGroup(normalizedGroupId, (previous) => {
      const nextEpoch = Math.max(
        toFiniteEpoch(previous?.epoch ?? previous?.current_epoch ?? null) ?? 0,
        runtimeEpoch,
      );
      const nextRuntimeEpoch = Math.max(
        toFiniteEpoch(previous?.runtimeEpoch ?? previous?.runtime_epoch ?? null) ?? 0,
        runtimeEpoch,
      );
      return {
        ...(previous || {}),
        groupId: normalizedGroupId,
        group_id: normalizedGroupId,
        runtimeHandle,
        runtime_handle: runtimeHandle,
        runtimeEpoch: nextRuntimeEpoch,
        runtime_epoch: nextRuntimeEpoch,
        runtimeSessionGeneration: toFiniteEpoch(
          previous?.sessionGeneration ?? previous?.session_generation ?? null,
        ),
        runtime_session_generation: toFiniteEpoch(
          previous?.sessionGeneration ?? previous?.session_generation ?? null,
        ),
        runtimeLive: true,
        runtime_live: true,
        epoch: nextEpoch || runtimeEpoch,
        current_epoch: nextEpoch || runtimeEpoch,
        ciphersuite:
          normalizeSuiteId(ciphersuite ?? previous?.ciphersuite ?? this.preferredCiphersuite) ??
          this.preferredCiphersuite,
      };
    });
  }

  async sendApplicationMessage({
    groupId,
    senderClientId,
    envelopeB64,
    epoch,
    ciphersuite,
    contentType,
    clientMsgId,
    historyCiphertextB64,
    historyIvB64,
    historyAlg,
    notificationCiphertextB64,
    notificationIvB64,
    notificationAlg,
  } = {}) {
    return sendMlsMessage({
      groupId,
      senderClientId,
      identityId: senderClientId,
      envelopeB64,
      epoch,
      ciphersuite,
      contentType,
      clientMsgId,
      historyCiphertextB64,
      historyIvB64,
      historyAlg,
      notificationCiphertextB64,
      notificationIvB64,
      notificationAlg,
    });
  }
}

export const getSessionManagerV2 = (options = {}) => {
  const normalizedUserId = toStringOrNull(options.userId) || 'anonymous';
  const suite = normalizeSuiteId(options.preferredCiphersuite) ?? DEFAULT_MLS_CIPHERSUITE;
  const cacheKey = [
    normalizedUserId,
    suite,
    options.wantPQ !== false ? 'pq' : 'classic',
    options.serverManaged !== false ? 'server' : 'local',
  ].join(':');
  const existing = managerCache.get(cacheKey);
  if (existing && existing.disposed !== true) {
    if (options.identityName) {
      existing.identityName = toStringOrNull(options.identityName) || existing.identityName;
    }
    return existing;
  }
  const manager = new SessionManagerV2({
    ...options,
    userId: normalizedUserId,
    preferredCiphersuite: suite,
  });
  managerCache.set(cacheKey, manager);
  return manager;
};

export const clearSessionManagerV2Cache = () => {
  managerCache.forEach((manager) => {
    try {
      manager?.dispose?.();
    } catch {}
  });
  managerCache.clear();
  try {
    shutdownMlsWorker?.();
  } catch {}
  try {
    if (typeof window !== 'undefined' && window.sessionManagerMLSV2) {
      window.sessionManagerMLSV2 = null;
    }
  } catch {}
};

export default getSessionManagerV2;
